import {t, useLanguage, interfaceLocale} from './i18n';
import {getLanguage} from './i18n';
import {localInstant,businessZone,partsAt,wallDate} from './utils/businessTime';
import {getMarket,formatMarketMoney} from './utils/market';
import "./BookingMobilePolish.css";
import PaymentHoldNotice from "./components/booking/PaymentHoldNotice";
import "./components/events/eventBookingJourney.css";
// LOOP FROM PRICE V1 — booking treats starting prices as estimates, not final totals.
import React, { useEffect, useMemo, useState, useRef } from "react";
import {resolveEventBookingDraft} from './utils/eventBookingDraft';
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db as defaultDb, firebaseConfigRuntime } from "./firebase";
import fsClient from "./data/firestoreClient";
import { isIosCapacitor, httpRequestJson } from "./utils/nativeHttp";
import { callCallableNativeSafe } from "./utils/callableNativeSafe";
import { bookingPaymentCallable } from "./utils/bookingPaymentCallable";
import { getManualAuth, isManualAuthLikelyValid } from "./utils/manualAuth";
import { Clock } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { PhoneInput } from "./components/PhoneKit";
import PublicBookingWompiInlineCard from "./components/booking/PublicBookingWompiInlineCard";
import { normalizePhoneToE164, parsePhoneValue } from "./utils/phoneUtils";
import {
  PUBLIC_BOOKING_PAYMENT_FLOW_VERSION,
  buildRequiredPaymentSessionRequest,
  getPublicBookingPaymentPreview,
  isServerRequiredPaymentBooking,
  publicBookingPaymentUnsupportedMessage,
  requiredPaymentButtonLabel,
} from "./utils/publicBookingPaymentClient";




function normalizeWorkerTypeForBooking(emp) {
  const raw = String(emp?.workerType || emp?.type || emp?.staffType || "").trim().toLowerCase();
  if (["operational", "operativo", "operations", "staff", "support"].includes(raw)) return "operational";
  if (emp?.isOperationalWorker === true || emp?.operationalOnly === true) return "operational";
  return "service";
}

function isPublicServiceWorker(emp) {
  if (!emp) return false;
  if (emp.disabled === true || emp.active === false || emp.visible === false || emp.hidden === true || emp.deleted === true || emp.archived === true) return false;
  return normalizeWorkerTypeForBooking(emp) === "service";
}

function isServiceVisibleForBooking(service) {
  if (!service) return false;
  if (service.disabled === true) return false;
  if (service.active === false) return false;
  if (service.visible === false) return false;
  if (service.isActive === false) return false;
  if (service.hidden === true) return false;
  if (service.deleted === true) return false;
  if (service.archived === true) return false;
  return true;
}

function getBookingServiceOptionId(service) {
  if (!service) return "";
  return String(service.id || service.serviceId || service.slug || service.code || service.__serviceTitle || service.name || service.title || "").trim();
}

function bookingMoneyNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeBookingServicePriceType(service) {
  const raw = String(service?.priceType || service?.pricingType || "").toLowerCase().trim();
  if (["quote", "quotation", "cotizacion", "cotización"].includes(raw)) return "quote";
  if (["from", "starting", "desde"].includes(raw)) return "from";
  if (
    !raw &&
    service?.fromPrice !== null &&
    service?.fromPrice !== undefined &&
    service?.fromPrice !== ""
  ) return "from";
  return "fixed";
}

function bookingServicePriceIsEstimate(service) {
  return normalizeBookingServicePriceType(service) === "from";
}

function formatBookingServicePrice(service, business = service || {}) {
  const priceType = normalizeBookingServicePriceType(service);
  if (priceType === "quote") {
    return String(service?.priceLabel || service?.publicPriceLabel || "Solicitar cotización");
  }

  const raw = priceType === "from"
    ? (service?.price ?? service?.fromPrice ?? service?.startingPrice)
    : (service?.price ?? service?.basePrice ?? service?.amount ?? service?.durationPrice);

  if (raw === null || raw === undefined || raw === "") return null;
  const amount = bookingMoneyNumber(raw, NaN);
  if (!Number.isFinite(amount)) return null;
  const formatted = formatBookingMoney(amount,business);
  return priceType === "from" ? `Desde ${formatted}` : formatted;
}

function bookingNeedsCustomerLocation(businessDoc) {
  const mode = String(businessDoc?.serviceMode || businessDoc?.mode || "").toLowerCase();
  return mode === "delivery" || mode === "hybrid";
}

function getCoverageAreasForBooking(businessDoc) {
  const advanced = Array.isArray(businessDoc?.serviceAreasAdvanced)
    ? businessDoc.serviceAreasAdvanced
    : Array.isArray(businessDoc?.deliveryAreas)
      ? businessDoc.deliveryAreas
      : [];

  if (advanced.length) return advanced;

  const legacy = Array.isArray(businessDoc?.serviceAreas)
    ? businessDoc.serviceAreas
    : String(businessDoc?.coverageArea || businessDoc?.serviceArea || "").split(",");

  return legacy
    .map((label) => ({ label: String(label || "").trim(), level: "zone", extraFee: 0 }))
    .filter((x) => x.label);
}

function bookingLocationLevel(loc) {
  if (!loc) return "";
  if (loc.zone) return "zone";
  if (loc.corregimiento) return "corregimiento";
  if (loc.district) return "district";
  if (loc.province) return "province";
  return "";
}

function bookingCoverageScore(area) {
  const level = area?.level || bookingLocationLevel(area);
  if (level === "zone" || area?.zone) return 4;
  if (level === "corregimiento" || area?.corregimiento) return 3;
  if (level === "district" || area?.district) return 2;
  if (level === "province" || area?.province) return 1;
  return 0;
}

function bookingAreaMatchesLocation(area, loc) {
  if (!area || !loc) return false;
  const level = area.level || bookingLocationLevel(area);

  if (level === "province") {
    return !!area.province && area.province === loc.province;
  }

  if (level === "district") {
    return area.province === loc.province && area.district === loc.district;
  }

  if (level === "corregimiento") {
    return area.province === loc.province &&
      area.district === loc.district &&
      area.corregimiento === loc.corregimiento;
  }

  if (level === "zone") {
    if (area.zone) {
      return area.province === loc.province &&
        area.district === loc.district &&
        area.corregimiento === loc.corregimiento &&
        area.zone === loc.zone;
    }

    return String(area.label || "").trim().toLowerCase() === String(loc.label || "").trim().toLowerCase();
  }

  return false;
}

function findMatchingCoverageAreaForBooking(businessDoc, loc) {
  if (!loc) return null;
  const areas = getCoverageAreasForBooking(businessDoc);
  const matches = areas.filter((area) => bookingAreaMatchesLocation(area, loc));
  return matches.sort((a, b) => bookingCoverageScore(b) - bookingCoverageScore(a))[0] || null;
}

function formatBookingMoney(value, business = {}) {
  const market=getMarket(business?.countryCode);
  return formatMarketMoney(Number(value) || 0,business?.currency || market.currency,business?.locale || market.locale);
}


function normalizeCoverageOptionForBooking(area, idx = 0) {
  if (!area) return null;
  if (typeof area === "string") {
    const label = String(area || "").trim();
    if (!label) return null;
    return { id: `legacy-${idx}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`, label, level: "zone", extraFee: 0 };
  }

  const level = area.level || bookingLocationLevel(area) || "zone";
  const label = area.label || [
    area.zone,
    area.corregimiento,
    area.district,
    area.province,
  ].filter(Boolean).join(", ");

  if (!label) return null;

  return {
    ...area,
    id: area.id || `${level}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${idx}`,
    level,
    label,
    extraFee: bookingMoneyNumber(area.extraFee ?? area.fee ?? area.deliveryFee ?? 0, 0),
  };
}

function getCoverageOptionsForBooking(businessDoc) {
  return getCoverageAreasForBooking(businessDoc)
    .map(normalizeCoverageOptionForBooking)
    .filter(Boolean);
}

function getBusinessServiceModeForBooking(businessDoc) {
  return String(businessDoc?.serviceMode || businessDoc?.mode || "onsite").toLowerCase();
}

function getInitialVisitModeForBusiness(businessDoc) {
  const mode = getBusinessServiceModeForBooking(businessDoc);
  if (mode === "delivery") return "delivery";
  return "onsite";
}

function isDeliveryVisitMode(mode) {
  return String(mode || "").toLowerCase() === "delivery";
}

function isVisitModeAvailableForBusiness(businessDoc, mode) {
  const businessMode = getBusinessServiceModeForBooking(businessDoc);
  const nextMode = String(mode || "").toLowerCase();
  if (businessMode === "hybrid") return nextMode === "onsite" || nextMode === "delivery";
  if (businessMode === "delivery") return nextMode === "delivery";
  return nextMode === "onsite";
}



function bookingCoverageLevelLabel(area) {
  const level = String(area?.level || bookingLocationLevel(area) || "zone").toLowerCase();
  if (level === "province") return "Provincia";
  if (level === "district") return "Distrito";
  if (level === "corregimiento") return "Corregimiento";
  return "Zona";
}

function bookingAreaSearchText(area) {
  return [
    area?.label,
    area?.zone,
    area?.corregimiento,
    area?.district,
    area?.province,
    bookingCoverageLevelLabel(area),
  ].filter(Boolean).join(" ").toLowerCase();
}


function getBookingBusinessVerticalSafe(businessDoc, selectedService) {
  const raw = [
    businessDoc?.type,
    businessDoc?.category,
    businessDoc?.businessType,
    businessDoc?.vertical,
    businessDoc?.industry,
    businessDoc?.serviceCategory,
    selectedService?.category,
    selectedService?.type,
    selectedService?.serviceType,
    selectedService?.name,
  ].filter(Boolean).join(" ").toLowerCase();

  if (/event|evento|fiesta|party|animaci|show|personaje|mascot|cumple/.test(raw)) return "events";
  if (/beauty|belleza|salon|salón|spa|nail|uña|hair|cabello|barber|barbería|ceja|lash|pestaña|makeup|maquillaje/.test(raw)) return "beauty";
  if (/home|domicilio|delivery|mantenimiento|limpieza|repair|servicio a domicilio|car|auto|detailing/.test(raw)) return "home";
  if (/fitness|gym|trainer|entrenador|yoga|pilates/.test(raw)) return "fitness";
  return "general";
}

function getBaseBookingPrice(selectedService, isEventsBusiness, eventSummary) {
  if (isEventsBusiness && eventSummary) return bookingMoneyNumber(eventSummary.totalPrice, 0);
  if (normalizeBookingServicePriceType(selectedService) === "quote") return 0;
  return bookingMoneyNumber(
    selectedService?.price ??
    selectedService?.fromPrice ??
    selectedService?.startingPrice ??
    selectedService?.basePrice ??
    selectedService?.amount ??
    selectedService?.durationPrice,
    0
  );
}


function getBookingApprovalModeForBusiness(businessDoc,service={}) {
  return require('./utils/eventApprovalV1').eventApprovalMode(businessDoc || {},service);
}

function getBookingApprovalFields(approvalMode) {
  const mode = approvalMode === "auto" ? "auto" : "manual";
  const autoConfirmed = mode === "auto";

  return {
    status: autoConfirmed ? "confirmed" : "pending",
    confirmationStatus: autoConfirmed ? "confirmed" : "pending",
    approvalModeAtCreation: mode,
    requiresApproval: !autoConfirmed,
    confirmedAutomatically: autoConfirmed,
    pendingReason: autoConfirmed ? null : "business_requires_approval",
  };
}

function normalizeBookingCallableCode(err) {
  const raw = String(
    err?.code ||
    err?.details?.code ||
    err?.customData?.code ||
    ""
  ).trim();
  return raw.replace(/^functions\//, "").toLowerCase();
}

function getBookingErrorRawMessage(err) {
  try {
    if (!err) return "";
    return String(err.message || err.details?.message || err.details || err || "").trim();
  } catch (_) {
    return "";
  }
}

function loopBookingRequestStableValue(value) {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.map(loopBookingRequestStableValue);
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((out, key) => {
        const next = loopBookingRequestStableValue(value[key]);
        if (next !== undefined) out[key] = next;
        return out;
      }, {});
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value;
  return String(value || "").trim();
}

function buildLoopBookingAttemptFingerprint(parts = {}) {
  return JSON.stringify(loopBookingRequestStableValue(parts));
}

function buildLoopBookingClientRequestId(parts = {}) {
  const clean = (value, fallback = "x") => {
    const out = String(value || fallback)
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 70);
    return out || fallback;
  };

  const randomPart = loopBookingSafeRandomId("req")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .slice(0, 150);

  return [
    "public",
    clean(parts.uid || parts.userId, "guest"),
    clean(parts.businessId, "business"),
    randomPart,
  ].join("__").slice(0, 420);
}

function getFriendlyCreateBookingError(err) {
  const code = normalizeBookingCallableCode(err);
  const raw = getBookingErrorRawMessage(err);
  const lower = `${code} ${raw}`.toLowerCase();

  if (lower.includes("already-exists") || lower.includes("franja ya fue reservada") || lower.includes("capacidad")) {
    return "Esa hora ya no está disponible. Elige otra hora para reservar.";
  }

  if (lower.includes("unauthenticated") || lower.includes("debes iniciar sesión")) {
    return "Tu sesión expiró. Inicia sesión nuevamente para completar la reserva.";
  }

  if (lower.includes("createbooking-function-timeout") || lower.includes("deadline-exceeded") || lower.includes("timeout")) {
    return "La reserva está tardando más de lo normal. Revisa Mis Reservas en unos segundos antes de intentar otra vez.";
  }

  if (code === "failed-precondition") {
    return "No pudimos completar la reserva con la información actual. Revisa los datos e inténtalo otra vez.";
  }

  if (code === "invalid-argument") {
    return "Revisa la información de la reserva e inténtalo nuevamente.";
  }

  if (code === "permission-denied") {
    return "No pudimos confirmar la reserva en este momento. Intenta nuevamente en unos segundos.";
  }

  if (code === "not-found") {
    return "No encontramos el negocio, servicio o trabajador seleccionado. Vuelve atrás e inténtalo nuevamente.";
  }

  return "No pudimos crear la reserva. Intenta nuevamente en unos segundos.";
}

function isLocalhostBookingRuntime() {
  try {
    if (typeof window === "undefined") return false;

    const hostname = String(window.location?.hostname || "")
      .trim()
      .toLowerCase();

    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname === "[::1]"
    );
  } catch (_) {
    return false;
  }
}

function getFriendlyCreateGuestBookingError(err) {
  const code = normalizeBookingCallableCode(err);
  const raw = getBookingErrorRawMessage(err);

  let detailsText = "";
  try {
    detailsText = JSON.stringify(
      err?.details ||
      err?.customData ||
      {},
    );
  } catch (_) {}

  const lower =
    `${code} ${raw} ${detailsText}`.toLowerCase();

  if (
    lower.includes("guest-booking-disabled") ||
    lower.includes("todavía no están habilitadas")
  ) {
    return "Las reservas sin cuenta todavía no están habilitadas para este negocio.";
  }

  const appCheckIssue =
    code === "unauthenticated" ||
    lower.includes("unauthenticated") ||
    lower.includes("app check") ||
    lower.includes("app-check") ||
    lower.includes("appcheck") ||
    lower.includes("validar esta aplicación") ||
    lower.includes("debug-token-not-registered") ||
    lower.includes("guest-app-check-rejected") ||
    lower.includes("provider-not-ready") ||
    lower.includes("provider-disabled") ||
    lower.includes("app-check-token");

  if (appCheckIssue) {
    if (isLocalhostBookingRuntime()) {
      return "App Check de localhost todavía no está autorizado. Abre la consola, registra el AppCheck debug token en Firebase y recarga la página.";
    }

    return "No pudimos validar esta aplicación. Cierra y vuelve a abrir Loop antes de intentarlo otra vez.";
  }

  if (
    lower.includes("authenticated-caller-must-use-createbooking")
  ) {
    return "Detectamos una sesión iniciada. Cierra sesión o utiliza el flujo normal de reservas.";
  }

  if (
    lower.includes("whatsapp-consent") ||
    lower.includes("autorizar")
  ) {
    return "Debes autorizar las confirmaciones por WhatsApp para reservar sin cuenta.";
  }

  if (
    lower.includes("already-exists") ||
    lower.includes("franja") ||
    lower.includes("capacidad")
  ) {
    return "Esa hora ya no está disponible. Elige otra hora para reservar.";
  }

  if (
    lower.includes("price-changed") ||
    lower.includes("el precio cambió")
  ) {
    return "El precio cambió. Revisa el total actualizado antes de confirmar.";
  }

  if (
    lower.includes("timeout") ||
    lower.includes("deadline-exceeded")
  ) {
    return "La reserva está tardando más de lo normal. No la repitas todavía; revisa tu WhatsApp antes de intentarlo otra vez.";
  }

  if (code === "invalid-argument") {
    return "Revisa los datos de la reserva invitada e inténtalo nuevamente.";
  }

  if (code === "failed-precondition") {
    return "No pudimos completar la reserva invitada con la información actual. Revisa los datos e inténtalo otra vez.";
  }

  return "No pudimos crear la reserva sin cuenta. Intenta nuevamente en unos segundos.";
}

function buildCreateBookingErrorDiagnostic(err, context = {}) {
  const code = normalizeBookingCallableCode(err);
  const rawMessage = getBookingErrorRawMessage(err);
  let details = null;
  try {
    if (err?.details && typeof err.details === "object") details = JSON.parse(JSON.stringify(err.details));
  } catch (_) {}

  let device = {};
  try {
    device = {
      href: window.location.href,
      userAgent: navigator.userAgent || "",
      platform: navigator.platform || "",
      viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
      isIosCapacitor: isIosCapacitor(),
    };
  } catch (_) {}

  return {
    where: "handleConfirmBooking",
    code: code || null,
    message: rawMessage || null,
    details,
    ...context,
    device,
    at: new Date().toISOString(),
  };
}


const LOOP_CREATE_BOOKING_FUNCTION_REGION =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION) ||
  "us-central1";

function getLoopCreateBookingCallableUrl() {
  const projectId =
    firebaseConfigRuntime?.projectId ||
    (typeof window !== "undefined" && window.__FIREBASE_CONFIG__?.projectId) ||
    "web-app-d9799";

  return `https://${LOOP_CREATE_BOOKING_FUNCTION_REGION}-${projectId}.cloudfunctions.net/createBooking`;
}

function normalizeCallableHttpErrorStatus(value) {
  const raw = String(value || "internal").trim();
  return raw
    .replace(/^functions\//i, "")
    .replace(/_/g, "-")
    .toLowerCase();
}

function makeCallableHttpError(errorPayload, fallbackMessage = "createBooking callable error") {
  const payload = errorPayload && typeof errorPayload === "object" ? errorPayload : {};
  const err = new Error(String(payload.message || fallbackMessage));
  err.code = normalizeCallableHttpErrorStatus(payload.status || payload.code || "internal");
  err.details = payload.details || null;
  err.customData = {
    source: "ios-native-http-callable",
    raw: payload,
  };
  return err;
}

async function createBookingSecureIosNativeHttp(payload) {
  const manualAuth = getManualAuth();
  const token = manualAuth && isManualAuthLikelyValid(manualAuth) ? String(manualAuth.idToken || "") : "";

  if (!token) {
    const err = new Error("Debes iniciar sesión para reservar");
    err.code = "unauthenticated";
    err.customData = {
      source: "ios-native-http-callable",
      reason: "missing-valid-manual-auth-token",
      hasManualAuth: Boolean(manualAuth),
      manualAuthUid: manualAuth?.uid || null,
      manualAuthEmail: manualAuth?.email || null,
    };
    throw err;
  }

  const url = getLoopCreateBookingCallableUrl();
  const startedAt = Date.now();

  try {
    if (typeof window !== "undefined") {
      window.__LOOP_CREATE_BOOKING_IOS_NATIVEHTTP__ = {
        status: "starting",
        url,
        businessId: payload?.businessId || null,
        serviceId: payload?.serviceId || null,
        employeeId: payload?.employeeId || null,
        clientRequestId: payload?.clientRequestId || payload?.clientRequestKey || null,
        manualAuthUid: manualAuth?.uid || null,
        manualAuthEmail: manualAuth?.email || null,
        startedAt: new Date(startedAt).toISOString(),
      };
    }
  } catch (_) {}

  try {
    const response = await httpRequestJson({
      url,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: {
        data: payload,
      },
    });

    if (response && response.error) {
      throw makeCallableHttpError(response.error);
    }

    const result =
      response && Object.prototype.hasOwnProperty.call(response, "result")
        ? response.result
        : response;

    try {
      if (typeof window !== "undefined") {
        window.__LOOP_CREATE_BOOKING_IOS_NATIVEHTTP__ = {
          ...(window.__LOOP_CREATE_BOOKING_IOS_NATIVEHTTP__ || {}),
          status: "ok",
          elapsedMs: Date.now() - startedAt,
          resultBookingId: result?.bookingId || result?.booking?.id || null,
          finishedAt: new Date().toISOString(),
        };
      }
    } catch (_) {}

    return result && result.data ? result.data : result;
  } catch (error) {
    try {
      if (typeof window !== "undefined") {
        window.__LOOP_CREATE_BOOKING_IOS_NATIVEHTTP__ = {
          ...(window.__LOOP_CREATE_BOOKING_IOS_NATIVEHTTP__ || {}),
          status: "error",
          elapsedMs: Date.now() - startedAt,
          errorCode: error?.code || null,
          errorMessage: error?.message || String(error),
          errorDetails: error?.details || error?.customData || null,
          finishedAt: new Date().toISOString(),
        };
      }
    } catch (_) {}
    throw error;
  }
}

async function createBookingSecure(bookingData) {
  const payload = {
    refundPolicyAcceptance: bookingData.refundPolicyAcceptance || null,
    businessId: bookingData.businessId,
    businessName: bookingData.businessName || null,
    clientRequestId: bookingData.clientRequestId || bookingData.clientRequestKey || null,
    clientRequestKey: bookingData.clientRequestKey || bookingData.clientRequestId || null,
    date: bookingData.date,
    time: bookingData.time,
    serviceId: bookingData.serviceId || null,
    serviceName: bookingData.serviceName || null,
    duration: bookingData.duration || null,
    customerName: bookingData.customerName || null,
    customerEmail: bookingData.customerEmail || null,
    customerLanguage: getLanguage(),
    customerPhone: bookingData.customerPhone || bookingData.clientPhone || bookingData.phone || null,
    customerUserId: bookingData.customerUserId || bookingData.userId || null,
    customerUserUid: bookingData.customerUserUid || bookingData.userUid || bookingData.userId || null,
    linkedUserId: bookingData.linkedUserId || bookingData.userId || null,
    linkedUserUid: bookingData.linkedUserUid || bookingData.userUid || bookingData.userId || null,
    employeeId: bookingData.employeeId || null,
    employeeName: bookingData.employeeName || null,
    requestedWorkerMode: bookingData.requestedWorkerMode || (bookingData.employeeId ? "specific" : "any"),
    eventOptions: bookingData.eventOptions || null,
    eventDetails: bookingData.eventDetails || null,
    totalPrice: bookingData.totalPrice ?? null,
    price: bookingData.price ?? bookingData.totalPrice ?? null,
    basePrice: bookingData.basePrice ?? null,
    servicePrice: bookingData.servicePrice ?? null,
    priceType: bookingData.priceType || bookingData.pricingType || "fixed",
    pricingType: bookingData.pricingType || bookingData.priceType || "fixed",
    priceIsEstimate: bookingData.priceIsEstimate === true,
    finalPricePending: bookingData.finalPricePending === true,
    priceDisplayLabel: bookingData.priceDisplayLabel || null,
    locationPrice: bookingData.locationPrice ?? bookingData.deliveryFee ?? 0,
    priceBreakdown: bookingData.priceBreakdown || null,
    visitMode: bookingData.visitMode || (bookingData.customerLocation ? "delivery" : "onsite"),
    visitTypeLocation: bookingData.visitTypeLocation || bookingData.visitMode || (bookingData.customerLocation ? "delivery" : "onsite"),
    customerLocation: bookingData.customerLocation || null,
    serviceAreaMatched: bookingData.serviceAreaMatched || null,
    customerLocationLabel: bookingData.customerLocationLabel || bookingData.customerLocation?.label || null,
    serviceAreaLabel: bookingData.serviceAreaLabel || bookingData.serviceAreaMatched?.label || bookingData.customerLocation?.label || null,
    customerExactAddress: bookingData.customerExactAddress || bookingData.customerAddressDetails || null,
    customerAddressDetails: bookingData.customerAddressDetails || bookingData.customerExactAddress || null,
    deliveryFee: bookingData.deliveryFee ?? 0,
    locationFee: bookingData.locationFee ?? bookingData.deliveryFee ?? 0,
    deliverySummaryLabel: bookingData.deliverySummaryLabel || [bookingData.customerLocation?.label, bookingData.deliveryFee > 0 ? `+$${bookingData.deliveryFee}` : ""].filter(Boolean).join(" · ") || null,
    visitType: bookingData.visitType || null,
    visitTypeLabel: bookingData.visitTypeLabel || null,
    visitHours: bookingData.visitHours || null,
    selectedCharacterIds: bookingData.selectedCharacterIds || null,
    selectedCharacters: bookingData.selectedCharacters || null,
    durationPrice: bookingData.durationPrice || null,
    charactersPrice: bookingData.charactersPrice || null,
    allowConcurrentSameService: bookingData.allowConcurrentSameService || null,
    status: bookingData.status || "pending",
    confirmationStatus: bookingData.confirmationStatus || bookingData.status || "pending",
    approvalModeAtCreation: bookingData.approvalModeAtCreation || "manual",
    requiresApproval: bookingData.requiresApproval !== false,
    confirmedAutomatically: bookingData.confirmedAutomatically === true,
    pendingReason: bookingData.pendingReason || null,

    // Loop analytics source classification.
    bookingSource: bookingData.bookingSource || "client_online",
    createdFrom: bookingData.createdFrom || "booking_interface",
    createdByRole: bookingData.createdByRole || "client",
    channel: bookingData.channel || "public_booking_flow",
    origin: bookingData.origin || "business_page_booking",
    onlineBooking: bookingData.onlineBooking !== false,
    isOnlineBooking: bookingData.isOnlineBooking !== false,
    requestedByClient: bookingData.requestedByClient !== false,
    createdByClient: bookingData.createdByClient !== false,
    manual: false,
    isManual: false,

    // LOOP F2A — payment scaffold. Real Wompi widget/signature comes in F2B.
    paymentProvider: "wompi",
    paymentProviderMode: "mock",
    onlinePaymentEnabled: true,
    paymentRequired: false,
    paymentMode: "optional",
    paymentStatus: "pending",

    // LOOP D2.5 — Public booking membership application metadata.
    // The server re-validates these fields before saving them, so the client cannot force benefits.
    membershipApplied: bookingData.membershipApplied === true || bookingData.membershipBenefitApplied === true,
    membershipBenefitApplied: bookingData.membershipApplied === true || bookingData.membershipBenefitApplied === true,
    customerMembershipId: bookingData.customerMembershipId || bookingData.membershipId || null,
    membershipId: bookingData.membershipId || bookingData.customerMembershipId || null,
    membershipPlanId: bookingData.membershipPlanId || bookingData.planId || null,
    membershipPlanName: bookingData.membershipPlanName || null,
    membershipBenefitType: bookingData.membershipBenefitType || null,
    membershipCreditsUsed: bookingData.membershipCreditsUsed || null,
    membershipDiscountPercent: bookingData.membershipDiscountPercent || null,
    membershipDiscountAmount: bookingData.membershipDiscountAmount || null,
    membershipBenefitLabel: bookingData.membershipBenefitLabel || null,
    membershipSource: bookingData.membershipSource || null,
  };

  if (isIosCapacitor()) {
    return createBookingSecureIosNativeHttp(payload);
  }

  const functions = getFunctions();
  const createBooking = httpsCallable(functions, "createBooking");
  const result = await createBooking(payload);
  return result && result.data ? result.data : result;
}

const LOOP_GUEST_BOOKING_SESSION_KEY =
  "loop_guest_booking_session_id_r33_2";

function getOrCreateGuestBookingSessionId() {
  try {
    if (typeof window === "undefined") {
      return loopBookingSafeRandomId("guest-session");
    }

    const storage = window.sessionStorage;
    let value = String(
      storage?.getItem(LOOP_GUEST_BOOKING_SESSION_KEY) || "",
    ).trim();

    if (value.length < 16) {
      value = loopBookingSafeRandomId("guest-session");
      storage?.setItem(LOOP_GUEST_BOOKING_SESSION_KEY, value);
    }

    return value;
  } catch (_) {
    return loopBookingSafeRandomId("guest-session");
  }
}

async function createGuestBookingSecure(bookingData) {
  const payload = {
    businessId: bookingData.businessId,
    clientRequestId:
      bookingData.clientRequestId ||
      bookingData.clientRequestKey ||
      null,
    clientRequestKey:
      bookingData.clientRequestKey ||
      bookingData.clientRequestId ||
      null,
    guestSessionId:
      bookingData.guestSessionId ||
      getOrCreateGuestBookingSessionId(),
    date: bookingData.date,
    time: bookingData.time,
    serviceId: bookingData.serviceId || null,
    employeeId: bookingData.employeeId || null,
    requestedWorkerMode:
      bookingData.requestedWorkerMode ||
      (bookingData.employeeId ? "specific" : "any"),
    customerName: bookingData.customerName || null,
    customerEmail: bookingData.customerEmail || null,
    customerLanguage: getLanguage(),
    customerPhone:
      bookingData.customerPhone ||
      bookingData.clientPhone ||
      bookingData.phone ||
      null,
    whatsappConsent:
      bookingData.whatsappConsent === true,
    notes: bookingData.notes || null,
    totalPrice: bookingData.totalPrice ?? null,
    priceType: bookingData.priceType || bookingData.pricingType || "fixed",
    pricingType: bookingData.pricingType || bookingData.priceType || "fixed",
    priceIsEstimate: bookingData.priceIsEstimate === true,
    finalPricePending: bookingData.finalPricePending === true,
    priceDisplayLabel: bookingData.priceDisplayLabel || null,
    visitMode: bookingData.visitMode || null,
    visitTypeLocation:
      bookingData.visitTypeLocation ||
      bookingData.visitMode ||
      null,
    customerLocation:
      bookingData.customerLocation || null,
    customerExactAddress:
      bookingData.customerExactAddress || null,
    customerAddressDetails:
      bookingData.customerAddressDetails ||
      bookingData.customerExactAddress ||
      null,
    eventOptions: bookingData.eventOptions || null,
    eventDetails: bookingData.eventDetails || null,
    bookingRequestClientVersion:
      bookingData.bookingRequestClientVersion ||
      "r33_2_guest_ui",
  };

  const result = await callCallableNativeSafe({
    functionName: "createGuestBooking",
    data: payload,
    timeoutMs: 45000,
    requireAuth: false,
    requireAppCheck: true,
  });

  return result && result.data ? result.data : result;
}


const LOOP_BOOKING_ANALYTICS_VISITOR_KEY = "loop_analytics_visitor_id_v1";
const LOOP_BOOKING_ANALYTICS_SESSION_KEY = "loop_analytics_session_id_v1";

function loopBookingSafeRandomId(prefix = "loop") {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return `${prefix}_${crypto.randomUUID()}`;
  } catch (_) {}
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getLoopBookingAnalyticsStorageId(key, prefix, storageType = "local") {
  try {
    if (typeof window === "undefined") return loopBookingSafeRandomId(prefix);
    const store = storageType === "session" ? window.sessionStorage : window.localStorage;
    if (!store) return loopBookingSafeRandomId(prefix);
    let value = store.getItem(key);
    if (!value) {
      value = loopBookingSafeRandomId(prefix);
      store.setItem(key, value);
    }
    return value;
  } catch (_) {
    return loopBookingSafeRandomId(prefix);
  }
}

function getLoopBookingAnalyticsSource() {
  try {
    if (typeof window === "undefined") return "Directo / desconocido";
    const params = new URLSearchParams(window.location.search || "");
    const explicit = params.get("utm_source") || params.get("source") || params.get("ref") || "";
    if (explicit) return explicit;

    const ref = String(document.referrer || "").trim();
    if (!ref) return "Directo / desconocido";
    try {
      const host = new URL(ref).hostname.replace(/^www\./, "");
      return host || ref;
    } catch (_) {
      return ref.slice(0, 120);
    }
  } catch (_) {
    return "Directo / desconocido";
  }
}

function getLoopBookingAnalyticsUtm() {
  try {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search || "");
    return {
      utmSource: params.get("utm_source") || params.get("source") || null,
      utmMedium: params.get("utm_medium") || null,
      utmCampaign: params.get("utm_campaign") || null,
      utmContent: params.get("utm_content") || null,
      utmTerm: params.get("utm_term") || null,
    };
  } catch (_) {
    return {};
  }
}

function getLoopBookingAnalyticsDevice() {
  try {
    if (typeof window === "undefined") return "unknown";
    const width = window.innerWidth || 0;
    if (width <= 640) return "mobile";
    if (width <= 1024) return "tablet";
    return "desktop";
  } catch (_) {
    return "unknown";
  }
}

async function trackBookingFunnelEventSafe({ db, businessId, type, payload = {} }) {
  try {
    if (!db || !businessId || !type) return;

    const visitorId = getLoopBookingAnalyticsStorageId(LOOP_BOOKING_ANALYTICS_VISITOR_KEY, "visitor", "local");
    const sessionId = getLoopBookingAnalyticsStorageId(LOOP_BOOKING_ANALYTICS_SESSION_KEY, "session", "session");
    const url = typeof window !== "undefined" ? window.location.href : "";
    const path = typeof window !== "undefined" ? `${window.location.pathname || ""}${window.location.search || ""}` : "";
    const nowIso = new Date().toISOString();

    const eventPayload = {
      type,
      eventType: type,
      name: type,
      action: type,
      businessId,
      visitorId,
      sessionId,
      source: getLoopBookingAnalyticsSource(),
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      url,
      path,
      device: getLoopBookingAnalyticsDevice(),
      ...getLoopBookingAnalyticsUtm(),
      ...payload,
      createdAt: nowIso,
      occurredAt: nowIso,
    };

    if (isIosCapacitor()) {
      await fsClient.addDocToCollectionPath(`businesses/${businessId}/analyticsEvents`, eventPayload, { db });
      await fsClient.addDocToCollectionPath("platformEvents", { ...eventPayload, platformEventMirror: true }, { db });
      return;
    }

    await addDoc(collection(db, `businesses/${businessId}/analyticsEvents`), eventPayload);
    await addDoc(collection(db, "platformEvents"), { ...eventPayload, platformEventMirror: true });
  } catch (err) {
    try { console.warn("[BookingInterface] analytics funnel event skipped", err); } catch (_) {}
  }
}

async function trackBookingCompletedEventSafe({ db, businessId, payload = {} }) {
  try {
    if (!db || !businessId) return;

    const type = "booking_completed";
    const visitorId = getLoopBookingAnalyticsStorageId(LOOP_BOOKING_ANALYTICS_VISITOR_KEY, "visitor", "local");
    const sessionId = getLoopBookingAnalyticsStorageId(LOOP_BOOKING_ANALYTICS_SESSION_KEY, "session", "session");
    const url = typeof window !== "undefined" ? window.location.href : "";
    const path = typeof window !== "undefined" ? `${window.location.pathname || ""}${window.location.search || ""}` : "";
    const nowIso = new Date().toISOString();

    const eventPayload = {
      type,
      eventType: type,
      name: type,
      action: type,
      businessId,
      visitorId,
      sessionId,
      source: getLoopBookingAnalyticsSource(),
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      url,
      path,
      device: getLoopBookingAnalyticsDevice(),
      ...getLoopBookingAnalyticsUtm(),
      ...payload,
      createdAt: nowIso,
      occurredAt: nowIso,
    };

    if (isIosCapacitor()) {
      await fsClient.addDocToCollectionPath(`businesses/${businessId}/analyticsEvents`, eventPayload, { db });
      await fsClient.addDocToCollectionPath("platformEvents", { ...eventPayload, platformEventMirror: true }, { db });
      return;
    }

    await addDoc(collection(db, `businesses/${businessId}/analyticsEvents`), eventPayload);
    await addDoc(collection(db, "platformEvents"), { ...eventPayload, platformEventMirror: true });
  } catch (err) {
    // Analytics should never break the booking flow.
    try { console.warn("[BookingInterface] booking_completed analytics skipped", err); } catch (_) {}
  }
}


const bookingAirbnbStyles = `
  .booking-summary-location-legacy{display:none!important;}
  /* =========================================================
     PHASE 2.1 PRO VISIT MODE + COVERAGE ZONE UX
     ========================================================= */
  .booking-pro-layout {
    grid-template-columns: minmax(0, 1fr) !important;
  }

  .booking-desktop-confirm {
    position: static !important;
    top: auto !important;
    z-index: 1 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .booking-visit-mode-card {
    border: 1px solid #e9eef4;
    border-radius: 26px;
    background:
      radial-gradient(circle at top left, rgba(15,118,110,.08), transparent 36%),
      #ffffff;
    box-shadow: 0 16px 40px rgba(15,23,42,.06);
    padding: 22px;
  }

  .booking-visit-mode-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 16px;
  }

  .booking-visit-mode-option {
    border: 1.5px solid #e2e8f0;
    background: #ffffff;
    border-radius: 22px;
    padding: 16px;
    min-height: 96px;
    text-align: left;
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    cursor: pointer;
    transition: transform .16s ease, border-color .16s ease, box-shadow .16s ease, background .16s ease;
  }

  .booking-visit-mode-option:hover {
    transform: translateY(-1px);
    border-color: rgba(15,118,110,.35);
    box-shadow: 0 14px 30px rgba(15,23,42,.08);
  }

  .booking-visit-mode-option.is-active {
    border-color: #0f766e;
    background: linear-gradient(135deg, #f0fdfa, #ffffff);
    box-shadow: 0 0 0 4px rgba(15,118,110,.10), 0 14px 32px rgba(15,118,110,.10);
  }

  .booking-visit-mode-icon {
    width: 42px;
    height: 42px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    background: #f0fdfa;
    color: #0f766e;
    font-size: 20px;
  }

  .booking-visit-mode-option.is-active .booking-visit-mode-icon {
    background: #0f766e;
    color: #fff;
  }

  .booking-visit-mode-copy strong {
    display: block;
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
    line-height: 1.1;
  }

  .booking-visit-mode-copy span {
    display: block;
    color: #64748b;
    font-size: 12px;
    line-height: 1.35;
    font-weight: 750;
    margin-top: 5px;
  }

  .booking-location-pro-card {
    border: 1px solid #e9eef4;
    border-radius: 26px;
    background: #fff;
    box-shadow: 0 16px 40px rgba(15,23,42,.06);
    padding: 22px;
  }

  .booking-zone-search {
    margin-top: 16px;
    width: 100%;
    height: 48px;
    border: 1.5px solid #e2e8f0;
    border-radius: 17px;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 800;
    color: #0f172a;
    outline: none;
    box-sizing: border-box;
    background: #fff;
  }

  .booking-zone-search:focus {
    border-color: rgba(15,118,110,.38);
    box-shadow: 0 0 0 4px rgba(15,118,110,.10);
  }

  .booking-zone-grid {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
  }

  .booking-zone-option {
    border: 1.5px solid #e2e8f0;
    background: #fff;
    border-radius: 20px;
    padding: 14px;
    text-align: left;
    cursor: pointer;
    display: grid;
    gap: 9px;
    min-height: 116px;
    transition: transform .16s ease, border-color .16s ease, box-shadow .16s ease, background .16s ease;
  }

  .booking-zone-option:hover {
    transform: translateY(-1px);
    border-color: rgba(15,118,110,.34);
    box-shadow: 0 12px 26px rgba(15,23,42,.07);
  }

  .booking-zone-option.is-active {
    border-color: #0f766e;
    background: linear-gradient(135deg, #f0fdfa 0%, #ffffff 100%);
    box-shadow: 0 0 0 4px rgba(15,118,110,.10);
  }

  .booking-zone-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .booking-zone-title {
    color: #0f172a;
    font-size: 14px;
    line-height: 1.25;
    font-weight: 950;
  }

  .booking-zone-badge {
    border-radius: 999px;
    background: #eef2f7;
    color: #64748b;
    padding: 5px 8px;
    font-size: 10px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .04em;
    white-space: nowrap;
  }

  .booking-zone-option.is-active .booking-zone-badge {
    background: #ccfbf1;
    color: #0f766e;
  }

  .booking-zone-fee {
    color: #0f766e;
    font-size: 12px;
    font-weight: 950;
  }

  .booking-zone-note {
    color: #64748b;
    font-size: 12px;
    line-height: 1.35;
    font-weight: 750;
  }

  .booking-address-box {
    margin-top: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    background: #f8fafc;
    padding: 14px;
  }

  .booking-address-box label {
    display: block;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
    margin-bottom: 8px;
  }

  .booking-address-box textarea {
    width: 100%;
    min-height: 92px;
    resize: vertical;
    border: 1.5px solid #e2e8f0;
    border-radius: 16px;
    background: #fff;
    color: #0f172a;
    padding: 12px;
    box-sizing: border-box;
    font-size: 14px;
    line-height: 1.45;
    font-weight: 750;
    outline: none;
  }

  .booking-address-box textarea:focus {
    border-color: rgba(15,118,110,.38);
    box-shadow: 0 0 0 4px rgba(15,118,110,.10);
  }

  .booking-address-helper {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    color: #64748b;
    font-size: 11px;
    line-height: 1.35;
    font-weight: 750;
    margin-top: 7px;
  }

  .booking-final-summary-card {
    margin-top: 0 !important;
  }

  .booking-summary-price-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    color: #334155;
    font-size: 13px;
    font-weight: 800;
    padding: 6px 0;
  }

  .booking-summary-price-row.total {
    border-top: 1px solid #e2e8f0;
    margin-top: 6px;
    padding-top: 12px;
    color: #0f172a;
    font-size: 16px;
    font-weight: 950;
  }

  @media (max-width: 768px) {
    .booking-visit-mode-grid,
    .booking-zone-grid {
      grid-template-columns: 1fr;
    }

    .booking-visit-mode-card,
    .booking-location-pro-card {
      border-radius: 24px;
      padding: 18px;
    }

    .booking-sticky-confirm {
      display: none !important;
    }

    .booking-pro-root {
      padding-bottom: calc(40px + env(safe-area-inset-bottom, 0px)) !important;
    }
  }

  .booking-pro-layout {
    align-items: start !important;
  }

  .booking-pro-left {
    min-width: 0 !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .booking-desktop-confirm {
    position: sticky !important;
    top: 16px !important;
    z-index: 3 !important;
    align-self: start !important;
  }

  .worker-desktop-scroller {
    max-width: 100% !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    padding-right: 12px !important;
  }

  .booking-summary-location {
    border: 1px solid #e2e8f0;
    background: #ffffff;
    border-radius: 18px;
    padding: 12px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    margin-top: 10px;
  }

  .booking-summary-location.is-ok {
    border-color: #99f6e4;
    background: #f0fdfa;
  }

  .booking-summary-location.is-error {
    border-color: #fecaca;
    background: #fef2f2;
  }

  .booking-summary-location span {
    display: block;
    color: #64748b;
    font-size: 11px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  .booking-summary-location strong {
    display: block;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
    line-height: 1.25;
    margin-top: 3px;
  }

  .booking-summary-location small {
    display: block;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.3;
    margin-top: 3px;
  }

  .booking-summary-location.is-error small {
    color: #dc2626;
  }

  .booking-summary-location button {
    min-height: 36px;
    border: 0;
    border-radius: 999px;
    background: #0f766e;
    color: #ffffff;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 950;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    .booking-desktop-confirm {
      position: static !important;
    }

    .booking-summary-location {
      grid-template-columns: 1fr;
    }

    .booking-summary-location button {
      width: 100%;
    }
  }


  .booking-pro-root {
    max-width: 1120px;
    margin: 0 auto;
    padding: 22px 18px calc(112px + env(safe-area-inset-bottom, 0px));
  }
  .booking-pro-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 22px;
    animation: bookingFadeUp .22s ease both;
  }
  .booking-pro-logo {
    width: 82px;
    height: 82px;
    border-radius: 24px;
    background: #fff;
    border: 1px solid #eef2f7;
    box-shadow: 0 12px 28px rgba(15, 23, 42, .08);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex: 0 0 auto;
  }
  .booking-pro-title {
    font-size: 28px;
    line-height: 1.05;
    font-weight: 950;
    color: #0f172a;
    letter-spacing: -0.04em;
    margin: 0;
  }
  .booking-pro-subtitle {
    margin-top: 5px;
    color: #64748b;
    font-size: 14px;
    font-weight: 650;
  }
  .booking-pro-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(320px, .95fr);
    gap: 18px;
    align-items: start;
  }
  .booking-pro-left {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .booking-pro-card {
    background: #fff;
    border: 1px solid #e9eef4;
    border-radius: 26px;
    box-shadow: 0 16px 40px rgba(15, 23, 42, .065);
    padding: 22px;
    animation: bookingFadeUp .24s ease both;
  }
  .booking-pro-section-title {
    font-size: 22px;
    line-height: 1.1;
    font-weight: 950;
    letter-spacing: -0.03em;
    color: #0f172a;
    margin: 0;
  }
  .booking-pro-section-subtitle {
    margin-top: 8px;
    color: #64748b;
    font-size: 14px;
    line-height: 1.45;
    font-weight: 650;
  }
  .booking-service-card {
    position: relative;
  }
  .booking-service-select {
    width: 100%;
    margin-top: 18px;
    border: 1.5px solid #e2e8f0;
    border-radius: 20px;
    background: #fff;
    min-height: 72px;
    padding: 13px 48px 13px 16px;
    text-align: left;
    box-shadow: 0 8px 22px rgba(15, 23, 42, .045);
    transition: transform .16s ease, border-color .16s ease, box-shadow .16s ease;
  }
  .booking-service-select:active { transform: scale(.992); }
  .booking-service-select.is-selected {
    border-color: #0f766e;
    box-shadow: 0 0 0 4px rgba(20, 184, 166, .12), 0 10px 24px rgba(15, 23, 42, .05);
  }
  .booking-service-chevron {
    position: absolute;
    right: 18px;
    bottom: 32px;
    color: #94a3b8;
    font-size: 20px;
    line-height: 1;
    pointer-events: none;
  }
  .booking-service-menu {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 8px);
    z-index: 30;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    box-shadow: 0 18px 44px rgba(15, 23, 42, .14);
    overflow: hidden;
    animation: bookingDropdown .16s ease both;
  }
  .booking-service-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 15px;
    border: 0;
    background: #fff;
    text-align: left;
    border-bottom: 1px solid #f1f5f9;
  }
  .booking-calendar-head {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) 42px;
    gap: 10px;
    align-items: center;
    margin-bottom: 18px;
  }
  .booking-month-button {
    width: 42px;
    height: 42px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #fff;
    box-shadow: 0 8px 18px rgba(15, 23, 42, .045);
    font-size: 25px;
    line-height: 1;
    font-weight: 700;
    color: #0f172a;
  }
  .booking-month-label {
    text-align: center;
    font-size: 18px;
    line-height: 1.1;
    font-weight: 950;
    color: #0f172a;
    text-transform: capitalize;
    letter-spacing: -0.025em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .booking-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
    margin-bottom: 10px;
    color: #64748b;
    font-size: 12px;
    font-weight: 900;
    text-align: center;
  }
  .booking-days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 7px;
  }
  .booking-day-placeholder {
    width: 100%;
    aspect-ratio: 1;
  }
  .booking-day-button {
    width: 100%;
    aspect-ratio: 1;
    min-height: 42px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: #fff;
    color: #0f172a;
    font-size: 15px;
    font-weight: 850;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform .14s ease, background .14s ease, border-color .14s ease, box-shadow .14s ease;
  }
  .booking-day-button:active { transform: scale(.94); }
  .booking-day-button.is-today {
    border-color: #99f6e4;
    color: #0f766e;
    background: #f0fdfa;
  }
  .booking-day-button.is-selected {
    background: #0f766e;
    color: #fff;
    border-color: #0f766e;
    box-shadow: 0 10px 22px rgba(15, 118, 110, .23);
  }
  .booking-day-button.is-disabled {
    background: #f8fafc;
    color: #cbd5e1;
    cursor: not-allowed;
  }
  .booking-time-section {
    margin-top: 24px;
    scroll-margin-top: 12px;
  }
  .booking-time-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
  }
  .booking-time-button {
    min-width: 74px;
    height: 42px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #0f172a;
    font-size: 14px;
    font-weight: 850;
    box-shadow: 0 7px 16px rgba(15, 23, 42, .04);
    transition: transform .14s ease, background .14s ease, box-shadow .14s ease;
  }
  .booking-time-button:active { transform: scale(.96); }
  .booking-time-button.is-selected {
    background: #0f766e;
    border-color: #0f766e;
    color: #fff;
    box-shadow: 0 10px 22px rgba(15, 118, 110, .22);
  }
  .booking-time-button.is-disabled {
    background: #f8fafc;
    color: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
  }
  .booking-worker-panel {
    min-width: 0 !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }

  .booking-worker-scroll {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(106px, 1fr));
    gap: 12px;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    overflow: visible !important;
    padding: 4px 0 8px;
  }

  .booking-worker-scroll > * {
    min-width: 0 !important;
    width: 100% !important;
  }

  .booking-worker-mobile-nav {
    display: none;
  }

  .booking-worker-hint {
    display: none;
  }

  @media (max-width: 768px) {
    .booking-worker-panel {
      overflow: hidden !important;
    }

    .booking-worker-scroll {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      gap: 12px;
      padding: 4px 2px 10px;
      -webkit-overflow-scrolling: touch;
      scroll-snap-type: x proximity;
      touch-action: pan-x !important;
      overscroll-behavior-x: contain;
      scrollbar-width: none;
      cursor: grab;
      user-select: none;
    }

    .booking-worker-scroll::-webkit-scrollbar {
      display: none;
    }

    .booking-worker-scroll > * {
      flex: 0 0 118px !important;
      width: 118px !important;
      scroll-snap-align: start;
    }

    .booking-worker-mobile-nav {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .booking-worker-nav-btn {
      width: 34px;
      height: 34px;
      border-radius: 999px;
      border: 1px solid #e2e8f0;
      background: #fff;
      color: #0f172a;
      font-weight: 950;
      box-shadow: 0 6px 14px rgba(15,23,42,.06);
    }

    .booking-worker-hint {
      display: block;
      font-size: 11px;
      color: #94a3b8;
      font-weight: 750;
      margin-top: 2px;
    }
  }
  .booking-sticky-confirm {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 800;
    background: rgba(255, 255, 255, .94);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-top: 1px solid #e2e8f0;
    padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 -12px 32px rgba(15, 23, 42, .12);
    display: none;
  }
  .booking-sticky-inner {
    max-width: 1040px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
  }
  .booking-sticky-primary {
    min-width: 154px;
    height: 48px;
    border: 0;
    border-radius: 16px;
    background: #0f766e;
    color: #fff;
    font-weight: 950;
    box-shadow: 0 10px 24px rgba(15, 118, 110, .24);
  }

  /* Worker carousel: web has visible scrollbar, mobile has smooth swipe */
  .booking-worker-area {
    position: relative;
    min-width: 0 !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .booking-worker-carousel-wrap {
    position: relative;
    min-width: 0 !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .booking-worker-scroll {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 14px !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    padding: 4px 4px 16px 4px !important;
    -webkit-overflow-scrolling: touch !important;
    scroll-snap-type: x proximity !important;
    touch-action: pan-x pan-y !important;
    overscroll-behavior-x: contain !important;
    scrollbar-width: auto !important;
    scrollbar-color: #cbd5e1 #f8fafc !important;
    cursor: auto !important;
  }

  .booking-worker-scroll::-webkit-scrollbar {
    height: 8px !important;
    display: block !important;
  }

  .booking-worker-scroll::-webkit-scrollbar-track {
    background: #f8fafc !important;
    border-radius: 999px !important;
  }

  .booking-worker-scroll::-webkit-scrollbar-thumb {
    background: #cbd5e1 !important;
    border-radius: 999px !important;
  }

  .booking-worker-scroll > * {
    flex: 0 0 138px !important;
    width: 138px !important;
    min-width: 138px !important;
    max-width: 138px !important;
    scroll-snap-align: start !important;
  }

  .booking-worker-side-btn {
    display: none !important;
  }

  @media (max-width: 768px) {
    .booking-worker-area,
    .booking-worker-carousel-wrap {
      overflow: hidden !important;
    }

    .booking-worker-scroll {
      gap: 12px !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      padding: 4px 18px 12px 4px !important;
      scroll-snap-type: x mandatory !important;
      touch-action: pan-x !important;
      scrollbar-width: none !important;
      cursor: grab !important;
    }

    .booking-worker-scroll::-webkit-scrollbar {
      display: none !important;
      height: 0 !important;
    }

    .booking-worker-scroll > * {
      flex: 0 0 132px !important;
      width: 132px !important;
      min-width: 132px !important;
      max-width: 132px !important;
    }
  }


  /* Android mobile bottom safety + touch carousel hardening */
  @media (max-width: 768px) {
    .booking-pro-root {
      padding-bottom: calc(190px + env(safe-area-inset-bottom, 0px)) !important;
    }

    body.is-mobile-app .booking-pro-root {
      padding-bottom: calc(220px + env(safe-area-inset-bottom, 0px)) !important;
    }

    body.is-mobile-app .booking-sticky-confirm {
      bottom: 58px !important;
      padding-bottom: 10px !important;
    }

    .booking-worker-scroll {
      touch-action: none !important;
      -ms-touch-action: none !important;
      overscroll-behavior: contain !important;
      user-select: none !important;
    }

    .booking-worker-scroll > button,
    .booking-worker-scroll button {
      touch-action: none !important;
      -webkit-user-drag: none !important;
    }
  }


  /* HARD FIX: worker section must never be wider than the viewport */
  .booking-worker-panel {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    contain: layout paint;
  }

  .booking-worker-area,
  .booking-worker-carousel-wrap {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }

  .booking-worker-scroll {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    display: flex !important;
    flex-wrap: nowrap !important;
  }

  @media (max-width: 768px) {
    .booking-worker-scroll {
      overflow-x: scroll !important;
      overflow-y: hidden !important;
      padding-left: 4px !important;
      padding-right: 56px !important;
      scroll-padding-left: 4px !important;
      -webkit-overflow-scrolling: touch !important;
      touch-action: pan-x !important;
    }

    .booking-worker-scroll > * {
      flex: 0 0 132px !important;
      width: 132px !important;
      min-width: 132px !important;
      max-width: 132px !important;
    }

    .booking-worker-side-btn {
      display: none !important;
    }
  }



  /* =========================================================
     PHASE 3 BOOKING LOCATION MINI-STICKY
     Selector de zona compacto debajo de trabajadores.
     ========================================================= */
  .booking-location-sticky-shell {
    position: sticky;
    top: 14px;
    z-index: 8;
    width: 100%;
    max-width: 100%;
  }

  .booking-location-sticky-shell:empty {
    display: none !important;
  }

  .booking-delivery-sticky-card {
    border-radius: 22px !important;
    padding: 16px !important;
    box-shadow: 0 14px 34px rgba(15,23,42,.075) !important;
    border-color: #e2e8f0 !important;
  }

  .booking-location-pro-title {
    margin: 0;
    color: #0f172a;
    font-size: 18px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .booking-delivery-sticky-card .booking-zone-search {
    height: 42px;
    margin-top: 12px;
    border-radius: 14px;
    font-size: 13px;
  }

  .booking-delivery-sticky-card .booking-zone-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 8px;
    max-height: 238px;
    overflow-y: auto;
    padding-right: 2px;
  }

  .booking-delivery-sticky-card .booking-zone-option {
    min-height: auto;
    padding: 11px 12px;
    border-radius: 17px;
    gap: 6px;
  }

  .booking-delivery-sticky-card .booking-zone-title {
    font-size: 13px;
  }

  .booking-delivery-sticky-card .booking-zone-note {
    font-size: 11px;
  }

  .booking-delivery-sticky-card .booking-address-box {
    margin-top: 10px;
    padding: 12px;
    border-radius: 17px;
  }

  .booking-delivery-sticky-card .booking-address-box textarea {
    min-height: 74px;
    border-radius: 14px;
    font-size: 13px;
  }

  .booking-delivery-fee-box {
    margin-top: 10px;
    border-radius: 16px;
    border: 1px solid #99f6e4;
    background: #f0fdfa;
    padding: 10px 12px;
    color: #0f766e;
  }

  .booking-delivery-fee-box strong,
  .booking-delivery-fee-box span {
    display: block;
  }

  .booking-delivery-fee-box strong {
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
  }

  .booking-delivery-fee-box span {
    margin-top: 2px;
    font-size: 12px;
    font-weight: 800;
  }

  @media (max-width: 768px) {
    .booking-location-sticky-shell {
      position: sticky;
      bottom: calc(86px + env(safe-area-inset-bottom, 0px));
      top: auto;
      z-index: 40;
    }

    .booking-delivery-sticky-card {
      padding: 13px !important;
      border-radius: 20px !important;
      box-shadow: 0 12px 30px rgba(15,23,42,.14) !important;
    }

    .booking-location-pro-title {
      font-size: 16px;
    }

    .booking-delivery-sticky-card .booking-zone-grid {
      grid-template-columns: 1fr;
      max-height: 172px;
    }

    .booking-delivery-sticky-card .booking-zone-option {
      padding: 10px;
    }

    .booking-delivery-sticky-card .booking-address-box textarea {
      min-height: 66px;
    }
  }
  @keyframes bookingFadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes bookingDropdown {
    from { opacity: 0; transform: translateY(-4px) scale(.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @media (max-width: 768px) {
    .booking-pro-root {
      padding: 16px 12px calc(118px + env(safe-area-inset-bottom, 0px));
    }
    .booking-pro-header { display: none; }
    .booking-pro-layout {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    .booking-pro-card {
      width: 100%;
      border-radius: 24px;
      padding: 18px;
    }
    .booking-pro-section-title { font-size: 21px; }
    .booking-calendar-head {
      grid-template-columns: 40px minmax(0, 1fr) 40px;
      gap: 8px;
      margin-bottom: 16px;
    }
    .booking-month-button {
      width: 40px;
      height: 40px;
      font-size: 24px;
    }
    .booking-month-label { font-size: 17px; }
    .booking-days-grid { gap: 6px; }
    .booking-day-button {
      min-height: 40px;
      font-size: 14px;
    }
    .booking-time-button {
      min-width: 68px;
      height: 41px;
    }
    .booking-sticky-confirm { display: block; }
    .booking-desktop-confirm { display: none; }
  }


  /* =========================================================
     FINAL AUTHORITATIVE WORKER CAROUSEL CLEANUP
     Do not add other .booking-worker-scroll rules after this.
     ========================================================= */
  .booking-worker-panel,
  .booking-worker-area,
  .booking-worker-carousel-wrap {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }

  .booking-worker-scroll {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 14px !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    padding: 4px 4px 16px 4px !important;
    -webkit-overflow-scrolling: touch !important;
    touch-action: pan-x !important;
    scroll-snap-type: x proximity !important;
    overscroll-behavior-x: contain !important;
    scrollbar-width: auto !important;
    scrollbar-color: #cbd5e1 #f8fafc !important;
  }

  .booking-worker-scroll::-webkit-scrollbar {
    height: 8px !important;
    display: block !important;
  }

  .booking-worker-scroll::-webkit-scrollbar-track {
    background: #f8fafc !important;
    border-radius: 999px !important;
  }

  .booking-worker-scroll::-webkit-scrollbar-thumb {
    background: #cbd5e1 !important;
    border-radius: 999px !important;
  }

  .booking-worker-scroll > * {
    flex: 0 0 138px !important;
    width: 138px !important;
    min-width: 138px !important;
    max-width: 138px !important;
    scroll-snap-align: start !important;
  }

  .booking-worker-side-btn {
    display: none !important;
  }

  @media (max-width: 768px) {
    .booking-worker-scroll {
      gap: 12px !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      padding: 4px 22px 12px 4px !important;
      -webkit-overflow-scrolling: touch !important;
      touch-action: pan-x !important;
      scroll-snap-type: x mandatory !important;
      scrollbar-width: none !important;
    }

    .booking-worker-scroll::-webkit-scrollbar {
      display: none !important;
      height: 0 !important;
    }

    .booking-worker-scroll > * {
      flex: 0 0 132px !important;
      width: 132px !important;
      min-width: 132px !important;
      max-width: 132px !important;
    }
  }



  /* =========================================================
     WORKER PICKER REBUILD
     Desktop: simple horizontal scroller.
     Mobile: bottom sheet selector, no horizontal scroll dependency.
     ========================================================= */
  .worker-desktop-scroller {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 14px !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    padding: 4px 4px 16px 4px !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: auto !important;
    scrollbar-color: #cbd5e1 #f8fafc !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar {
    height: 8px !important;
    display: block !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar-track {
    background: #f8fafc !important;
    border-radius: 999px !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar-thumb {
    background: #cbd5e1 !important;
    border-radius: 999px !important;
  }

  .worker-desktop-scroller > * {
    flex: 0 0 138px !important;
    width: 138px !important;
    min-width: 138px !important;
    max-width: 138px !important;
  }

  .worker-mobile-picker {
    display: none;
  }

  .worker-mobile-sheet-overlay {
    position: fixed;
    inset: 0;
    z-index: 100010;
    background: rgba(15, 23, 42, 0.42);
    display: flex;
    align-items: flex-end;
  }

  .worker-mobile-sheet {
    width: 100%;
    max-height: 78vh;
    background: #ffffff;
    border-top-left-radius: 26px;
    border-top-right-radius: 26px;
    box-shadow: 0 -24px 60px rgba(15, 23, 42, 0.24);
    padding: 14px 14px calc(18px + env(safe-area-inset-bottom, 0px));
    overflow: hidden;
    animation: workerSheetUp .18s ease both;
  }

  .worker-mobile-sheet-list {
    max-height: 56vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 4px 0 8px;
  }

  .worker-mobile-row {
    width: 100%;
    border: 1px solid #e2e8f0;
    background: #fff;
    border-radius: 18px;
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    text-align: left;
  }

  .worker-mobile-row.is-selected {
    border-color: #0f766e;
    background: #f0fdfa;
    box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.12);
  }

  .worker-mobile-row.is-disabled {
    opacity: .48;
  }

  @keyframes workerSheetUp {
    from { opacity: 0; transform: translateY(18px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 768px) {
    .worker-desktop-scroller {
      display: none !important;
    }

    .worker-mobile-picker {
      display: block;
    }
  }




  /* =========================================================
     FINAL SIMPLE WORKER PICKER
     Desktop: horizontal scroller.
     Mobile: compact selected card + full-screen modal.
     ========================================================= */
  .worker-desktop-scroller {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 14px !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    padding: 4px 4px 16px 4px !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: auto !important;
    scrollbar-color: #cbd5e1 #f8fafc !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar {
    height: 8px !important;
    display: block !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar-track {
    background: #f8fafc !important;
    border-radius: 999px !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar-thumb {
    background: #cbd5e1 !important;
    border-radius: 999px !important;
  }

  .worker-desktop-scroller > * {
    flex: 0 0 138px !important;
    width: 138px !important;
    min-width: 138px !important;
    max-width: 138px !important;
  }

  .worker-mobile-picker {
    display: none !important;
  }

  .worker-mobile-selected-card {
    width: 100%;
    border: 1px solid #e2e8f0;
    background: #fff;
    border-radius: 22px;
    padding: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    box-shadow: 0 10px 24px rgba(15,23,42,.06);
    text-align: left;
  }

  .worker-mobile-avatar {
    width: 52px;
    height: 52px;
    border-radius: 17px;
    background: #f0fdfa;
    color: #0f766e;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 950;
    flex: 0 0 auto;
    overflow: hidden;
  }

  .worker-mobile-modal {
    position: fixed;
    inset: 0;
    z-index: 100010;
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    animation: workerModalIn .16s ease both;
  }

  .worker-mobile-modal-header {
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    padding: calc(12px + env(safe-area-inset-top, 0px)) 16px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex: 0 0 auto;
  }

  .worker-mobile-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 14px 14px calc(24px + env(safe-area-inset-bottom, 0px));
  }

  .worker-search-input {
    width: 100%;
    height: 46px;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    background: #fff;
    padding: 0 14px;
    outline: none;
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    box-shadow: 0 6px 16px rgba(15,23,42,.04);
  }

  .worker-mobile-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 10px;
  }

  .worker-mobile-option-card {
    min-height: 138px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    border-radius: 22px;
    padding: 12px;
    text-align: left;
    box-shadow: 0 8px 20px rgba(15,23,42,.045);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    position: relative;
  }

  .worker-mobile-option-card.is-selected {
    border-color: #0f766e;
    background: #f0fdfa;
    box-shadow: 0 0 0 4px rgba(20,184,166,.12);
  }

  .worker-mobile-option-card.is-disabled {
    opacity: .46;
  }

  .worker-mobile-option-avatar {
    width: 54px;
    height: 54px;
    border-radius: 18px;
    background: #f1f5f9;
    color: #64748b;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 950;
    font-size: 19px;
  }

  .worker-mobile-check {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: #0f766e;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 950;
    font-size: 13px;
  }

  @keyframes workerModalIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 768px) {
    .worker-desktop-scroller {
      display: none !important;
    }

    .worker-mobile-picker {
      display: block !important;
    }
  }




  /* =========================================================
     MOBILE WORKER PICKER SIZE FIX
     Compact card + controlled bottom sheet. Never wider/taller than viewport.
     ========================================================= */
  .worker-mobile-picker {
    display: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }

  .worker-mobile-selected-card {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 74px !important;
    border: 1px solid #e2e8f0 !important;
    background: #fff !important;
    border-radius: 20px !important;
    padding: 12px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 10px !important;
    box-shadow: 0 8px 18px rgba(15,23,42,.055) !important;
    text-align: left !important;
    box-sizing: border-box !important;
  }

  .worker-mobile-avatar {
    width: 46px !important;
    height: 46px !important;
    border-radius: 15px !important;
    background: #f0fdfa !important;
    color: #0f766e !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 20px !important;
    font-weight: 950 !important;
    flex: 0 0 auto !important;
    overflow: hidden !important;
  }

  .worker-mobile-modal {
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    top: auto !important;
    z-index: 100020 !important;
    background: rgba(15, 23, 42, 0.42) !important;
    display: flex !important;
    align-items: flex-end !important;
    flex-direction: row !important;
  }

  .worker-mobile-modal-panel {
    width: 100% !important;
    max-width: 100vw !important;
    max-height: min(72vh, 620px) !important;
    background: #f8fafc !important;
    border-top-left-radius: 26px !important;
    border-top-right-radius: 26px !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    box-shadow: 0 -24px 60px rgba(15, 23, 42, 0.24) !important;
  }

  .worker-mobile-modal-header {
    background: #ffffff !important;
    border-bottom: 1px solid #e2e8f0 !important;
    padding: 14px 16px 12px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 12px !important;
    flex: 0 0 auto !important;
  }

  .worker-mobile-modal-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    padding: 12px 14px calc(18px + env(safe-area-inset-bottom, 0px)) !important;
    box-sizing: border-box !important;
  }

  .worker-search-input {
    width: 100% !important;
    height: 44px !important;
    border-radius: 15px !important;
    border: 1px solid #e2e8f0 !important;
    background: #fff !important;
    padding: 0 13px !important;
    outline: none !important;
    font-size: 14px !important;
    font-weight: 750 !important;
    color: #0f172a !important;
    box-shadow: 0 6px 16px rgba(15,23,42,.04) !important;
    box-sizing: border-box !important;
  }

  .worker-mobile-grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 9px !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }

  .worker-mobile-option-card {
    min-height: 118px !important;
    border: 1px solid #e2e8f0 !important;
    background: #ffffff !important;
    border-radius: 18px !important;
    padding: 10px !important;
    text-align: left !important;
    box-shadow: 0 6px 16px rgba(15,23,42,.04) !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 7px !important;
    position: relative !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
  }

  .worker-mobile-option-avatar {
    width: 48px !important;
    height: 48px !important;
    border-radius: 16px !important;
    background: #f1f5f9 !important;
    color: #64748b !important;
    overflow: hidden !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-weight: 950 !important;
    font-size: 18px !important;
  }

  @media (max-width: 768px) {
    .worker-desktop-scroller {
      display: none !important;
    }

    .worker-mobile-picker {
      display: block !important;
    }
  }




  /* =========================================================
     FINAL WORKERS SAME AS WEB ON MOBILE
     Mobile and web use the same horizontal worker row.
     Mobile cards are smaller and cleaner.
     ========================================================= */
  .worker-mobile-picker,
  .worker-mobile-modal {
    display: none !important;
  }

  .worker-desktop-scroller {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 12px !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    padding: 4px 4px 14px 4px !important;
    -webkit-overflow-scrolling: touch !important;
    touch-action: pan-x !important;
    scrollbar-width: auto !important;
    scrollbar-color: #cbd5e1 #f8fafc !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar {
    height: 7px !important;
    display: block !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar-track {
    background: #f8fafc !important;
    border-radius: 999px !important;
  }

  .worker-desktop-scroller::-webkit-scrollbar-thumb {
    background: #cbd5e1 !important;
    border-radius: 999px !important;
  }

  .worker-desktop-scroller > * {
    flex: 0 0 124px !important;
    width: 124px !important;
    min-width: 124px !important;
    max-width: 124px !important;
  }

  @media (max-width: 768px) {
    .booking-worker-panel {
      padding: 14px !important;
      border-radius: 22px !important;
    }

    .worker-mobile-picker,
    .worker-mobile-modal {
      display: none !important;
    }

    .worker-desktop-scroller {
      display: flex !important;
      gap: 10px !important;
      padding: 4px 4px 12px 4px !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      -webkit-overflow-scrolling: touch !important;
      touch-action: pan-x !important;
      scrollbar-width: thin !important;
      scroll-snap-type: x proximity !important;
    }

    .worker-desktop-scroller::-webkit-scrollbar {
      height: 6px !important;
      display: block !important;
    }

    .worker-desktop-scroller > * {
      flex: 0 0 104px !important;
      width: 104px !important;
      min-width: 104px !important;
      max-width: 104px !important;
      scroll-snap-align: start !important;
    }
  }




  /* =========================================================
     FINAL MOBILE WORKER WIDTH + CAROUSEL CONTROL
     The worker card must match the width of the other booking cards.
     Only the worker row scrolls horizontally.
     ========================================================= */
  .booking-worker-panel {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
  }

  .worker-desktop-scroller {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    -webkit-overflow-scrolling: touch !important;
    touch-action: pan-x !important;
    overscroll-behavior-x: contain !important;
    scrollbar-width: auto !important;
    scrollbar-color: #cbd5e1 #f8fafc !important;
  }

  .worker-desktop-scroller > * {
    flex-shrink: 0 !important;
  }

  @media (max-width: 768px) {
    .booking-pro-root {
      width: 100% !important;
      max-width: 100vw !important;
      overflow-x: hidden !important;
      box-sizing: border-box !important;
    }

    .booking-pro-layout,
    .booking-pro-left,
    .space-y-4 {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      overflow: visible !important;
      box-sizing: border-box !important;
    }

    .booking-worker-panel {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
      padding: 14px !important;
      border-radius: 22px !important;
      contain: paint !important;
    }

    .worker-desktop-scroller {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      gap: 10px !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      padding: 4px 4px 12px 4px !important;
      -webkit-overflow-scrolling: touch !important;
      touch-action: pan-x !important;
      overscroll-behavior-x: contain !important;
      scroll-snap-type: x proximity !important;
      scrollbar-width: thin !important;
      box-sizing: border-box !important;
    }

    .worker-desktop-scroller::-webkit-scrollbar {
      height: 6px !important;
      display: block !important;
    }

    .worker-desktop-scroller > * {
      flex: 0 0 102px !important;
      width: 102px !important;
      min-width: 102px !important;
      max-width: 102px !important;
      scroll-snap-align: start !important;
    }

    .worker-mobile-picker,
    .worker-mobile-modal {
      display: none !important;
    }
  }
    .booking-success-check {
      width: 118px;
      height: 118px;
      margin-bottom: 24px;
    }

    .booking-success-title {
      font-size: 35px;
    }

    .booking-success-actions {
      grid-template-columns: 1fr;
    }
  }


  /* =========================================================
     BOOKING SUCCESS SCREEN
     ========================================================= */
  .booking-success-wrap {
    min-height: min(720px, calc(100vh - 120px));
    display: grid;
    place-items: center;
    padding: 32px 18px calc(48px + env(safe-area-inset-bottom, 0px));
    background:
      radial-gradient(circle at top, rgba(15,118,110,.08), transparent 34%),
      linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  }

  .booking-success-card {
    width: min(520px, 100%);
    text-align: center;
    animation: bookingSuccessIn .32s cubic-bezier(.2,.8,.2,1) both;
  }

  .booking-success-check {
    width: 132px;
    height: 132px;
    border-radius: 999px;
    margin: 0 auto 24px;
    background: linear-gradient(135deg, #10b981, #14b8a6);
    color: #ffffff;
    display: grid;
    place-items: center;
    box-shadow: 0 18px 42px rgba(16,185,129,.28);
    animation: bookingCheckPop .42s cubic-bezier(.2,.9,.2,1.25) .08s both;
  }

  .booking-success-title {
    margin: 0;
    color: #0f172a;
    font-size: clamp(32px, 5vw, 46px);
    line-height: .98;
    font-weight: 950;
    letter-spacing: -.065em;
  }

  .booking-success-subtitle {
    margin: 12px auto 0;
    max-width: 380px;
    color: #64748b;
    font-size: 15px;
    line-height: 1.45;
    font-weight: 750;
  }

  .booking-success-summary {
    margin-top: 22px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    border-radius: 24px;
    padding: 14px;
    display: grid;
    gap: 9px;
    text-align: left;
    box-shadow: 0 14px 34px rgba(15,23,42,.06);
  }

  .booking-success-row {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    color: #64748b;
    font-size: 13px;
    font-weight: 800;
  }

  .booking-success-row strong {
    color: #0f172a;
    font-weight: 950;
    text-align: right;
  }

  .booking-success-membership {
    margin-top: 12px;
    border: 1px solid #99f6e4;
    background: linear-gradient(135deg,#f0fdfa,#fff);
    border-radius: 18px;
    padding: 12px;
    text-align: left;
  }

  .booking-success-membership span {
    display: block;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: .10em;
    text-transform: uppercase;
    color: #00796f;
  }

  .booking-success-membership strong {
    display: block;
    margin-top: 4px;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
  }

  .booking-success-membership p {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
    line-height: 1.4;
  }

  .booking-success-actions {
    margin-top: 22px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .booking-success-primary,
  .booking-success-secondary {
    min-height: 52px;
    border-radius: 18px;
    font-size: 14px;
    font-weight: 950;
    cursor: pointer;
  }

  .booking-success-primary {
    border: 0;
    background: linear-gradient(135deg, #0f766e, #14b8a6);
    color: #ffffff;
    box-shadow: 0 16px 34px rgba(15,118,110,.22);
  }

  .booking-success-secondary {
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #334155;
  }

  @keyframes bookingSuccessIn {
    from { opacity: 0; transform: translateY(14px) scale(.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes bookingCheckPop {
    from { opacity: 0; transform: scale(.72); }
    to { opacity: 1; transform: scale(1); }
  }

  @media (max-width: 640px) {
    .booking-success-wrap {
      min-height: calc(100vh - 120px);
      padding: 24px 16px calc(34px + env(safe-area-inset-bottom, 0px));
    }

    .booking-success-check {
      width: 118px;
      height: 118px;
      margin-bottom: 24px;
    }

    .booking-success-title {
      font-size: 35px;
    }

    .booking-success-actions {
      grid-template-columns: 1fr;
    }
  }



  /* =========================================================
     FINAL FIX: SUCCESS CENTER + TOP SPACE UNDER GREEN NAV
     This is intentionally added last to override older duplicated rules.
     ========================================================= */

  .booking-pro-root {
    padding-top: 72px !important;
  }

  .booking-success-wrap {
    width: 100% !important;
    max-width: 100% !important;
    min-height: min(720px, calc(100vh - 160px)) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-sizing: border-box !important;
    padding: 54px 24px 58px !important;
    margin: 0 auto !important;
    text-align: center !important;
  }

  .booking-success-card {
    width: min(560px, 100%) !important;
    max-width: 560px !important;
    margin: 0 auto !important;
    align-self: center !important;
    justify-self: center !important;
    text-align: center !important;
  }

  .booking-success-summary,
  .booking-success-actions {
    width: 100% !important;
    max-width: 100% !important;
  }

  @media (max-width: 768px) {
    .booking-pro-root {
      padding-top: calc(34px + env(safe-area-inset-top, 0px)) !important;
    }

    .booking-success-wrap {
      min-height: calc(100vh - 145px) !important;
      padding: 42px 18px calc(46px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-success-card {
      width: min(440px, 100%) !important;
      max-width: 440px !important;
    }
  }



  /* =========================================================
     FINAL FIX: BOOKING MOBILE CONFIRM BUTTON IN NORMAL FLOW
     The reserve button should not be fixed/sticky on mobile because
     it can sit under the global bottom navigation in Capacitor/iPhone.
     This override intentionally lives last inside bookingAirbnbStyles.
     ========================================================= */
  @media (max-width: 768px) {
    .booking-pro-root {
      padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
    }

    body.is-mobile-app .booking-pro-root {
      padding-bottom: calc(110px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-sticky-confirm {
      position: static !important;
      left: auto !important;
      right: auto !important;
      bottom: auto !important;
      z-index: auto !important;
      display: block !important;
      width: 100% !important;
      background: transparent !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      border-top: 0 !important;
      box-shadow: none !important;
      padding: 0 0 calc(18px + env(safe-area-inset-bottom, 0px)) !important;
      margin-top: 14px !important;
      box-sizing: border-box !important;
    }

    body.is-mobile-app .booking-sticky-confirm {
      bottom: auto !important;
      padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-sticky-inner {
      max-width: none !important;
      width: 100% !important;
      margin: 0 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 12px !important;
      align-items: stretch !important;
      background: #ffffff !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 24px !important;
      padding: 14px !important;
      box-shadow: 0 14px 34px rgba(15, 23, 42, .07) !important;
      box-sizing: border-box !important;
    }

    .booking-sticky-primary {
      width: 100% !important;
      min-width: 0 !important;
      height: 52px !important;
      border-radius: 18px !important;
      font-size: 15px !important;
    }
  }


  /* =========================================================
     FINAL POLISH: SILENT LOADING + SAFE TOP SPACING
     Removes visible "Cargando..." screen and keeps booking content
     below the global green header when rendered as a page.
     ========================================================= */

  .booking-silent-loading {
    min-height: 42vh !important;
    width: 100% !important;
    background: transparent !important;
  }

  body.is-mobile-app .booking-silent-loading {
    min-height: 30vh !important;
  }

  .booking-pro-root {
    scroll-margin-top: 112px !important;
  }

  @media (min-width: 769px) {
    body:not(.is-mobile-app) .booking-pro-root {
      padding-top: 88px !important;
    }
  }

  @media (max-width: 768px) {
    .booking-silent-loading {
      min-height: 24vh !important;
    }
  }




  /* =========================================================
     MODAL WEB: SUMMARY FIT INSIDE MODAL CONTENT AREA
     Keeps the summary fixed but prevents it from overlapping
     the modal scrollbar/right edge on smaller desktop widths.
     ========================================================= */
  @media (min-width: 921px) {
    .business-booking-body .booking-pro-root {
      max-width: 1040px !important;
      padding-left: 24px !important;
      padding-right: 24px !important;
    }

    .business-booking-body .booking-pro-layout {
      grid-template-columns: minmax(0, 1fr) 320px !important;
      gap: 22px !important;
      align-items: start !important;
    }

    .business-booking-body .booking-desktop-confirm {
      position: fixed !important;
      top: 118px !important;

      /* Modal width is ~1100px and centered. This keeps the summary
         inside the modal, with space for the inner scrollbar. */
      right: max(96px, calc((100vw - 1100px) / 2 + 58px)) !important;

      width: clamp(292px, 27vw, 330px) !important;
      max-height: calc(100vh - 154px) !important;
      overflow-y: auto !important;
      z-index: 30010 !important;
      margin: 0 !important;
      border-radius: 28px !important;
      box-sizing: border-box !important;
    }

    .business-booking-body .booking-desktop-confirm .booking-pro-card,
    .business-booking-body .booking-desktop-confirm > * {
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .business-booking-body .booking-desktop-confirm button {
      max-width: 100% !important;
    }
  }

  @media (min-width: 1200px) {
    .business-booking-body .booking-desktop-confirm {
      right: calc((100vw - 1100px) / 2 + 58px) !important;
      width: 330px !important;
    }
  }

  @media (min-width: 921px) and (max-width: 1080px) {
    .business-booking-body .booking-pro-root {
      max-width: 980px !important;
      padding-left: 18px !important;
      padding-right: 18px !important;
    }

    .business-booking-body .booking-pro-layout {
      grid-template-columns: minmax(0, 1fr) 292px !important;
      gap: 18px !important;
    }

    .business-booking-body .booking-desktop-confirm {
      right: 76px !important;
      width: 292px !important;
    }
  }

  @media (max-width: 920px) {
    .business-booking-body .booking-desktop-confirm {
      position: static !important;
      width: auto !important;
      max-height: none !important;
      overflow: visible !important;
    }
  }


  /* EVENT PACKAGE CUSTOMIZER */
  .booking-event-options{border:1px solid rgba(0,103,95,.14);background:radial-gradient(circle at 8% 0%,rgba(0,137,127,.08),transparent 34%),#fff}
  .booking-event-controls{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:14px}
  .booking-event-control{border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:12px}
  .booking-event-control label{display:block;color:#64748b;font-size:11px;line-height:1;font-weight:950;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px}
  .booking-event-control select{width:100%;min-height:42px;border:1px solid #e2e8f0;border-radius:14px;padding:0 10px;background:#f8fafc;color:#0f172a;font-weight:850}
  .booking-character-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:14px}
  .booking-character-choice{border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:12px;text-align:left;font-weight:900;color:#0f172a}
  .booking-character-choice.is-selected{border-color:#0f766e;background:#f0fdfa;box-shadow:0 0 0 4px rgba(20,184,166,.12)}
  .booking-character-choice small{display:block;margin-top:4px;color:#64748b;font-weight:750}
  .booking-event-summary-pill{display:inline-flex;align-items:center;gap:7px;margin-top:14px;border-radius:999px;background:rgba(0,103,95,.08);color:#00675f;padding:8px 11px;font-size:12px;font-weight:950}
  @media(max-width:768px){.booking-event-controls,.booking-character-grid{grid-template-columns:1fr}}


  /* =========================================================
     PHASE 3.1 — PREMIUM HARMONY OVERRIDES
     Objetivo:
     - "Dónde recibirás el servicio" más minimalista y pequeño.
     - Trabajadores + ubicación + resumen con la misma armonía visual.
     - Si no aplica domicilio/trabajador, no deja huecos ni se monta.
     - Desktop con resumen limpio a la derecha.
     - Mobile sin overlap y con cards compactas.
     ========================================================= */

  .booking-pro-root {
    max-width: 1180px !important;
    padding: 18px 18px calc(96px + env(safe-area-inset-bottom, 0px)) !important;
    background:
      radial-gradient(circle at top left, rgba(15,118,110,.045), transparent 34%),
      linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  }

  .booking-pro-layout {
    display: grid !important;
    grid-template-columns: minmax(0, 1.58fr) minmax(318px, .92fr) !important;
    gap: 18px !important;
    align-items: start !important;
  }

  .booking-pro-left {
    display: flex !important;
    flex-direction: column !important;
    gap: 14px !important;
    min-width: 0 !important;
  }

  .booking-pro-card,
  .booking-visit-mode-card,
  .booking-worker-panel,
  .booking-location-pro-card,
  .booking-final-summary-card {
    border: 1px solid #e8eef5 !important;
    border-radius: 24px !important;
    background: #ffffff !important;
    box-shadow: 0 12px 34px rgba(15,23,42,.055) !important;
    box-sizing: border-box !important;
  }

  .booking-pro-card {
    padding: 20px !important;
  }

  .booking-pro-section-title {
    font-size: 21px !important;
    letter-spacing: -.035em !important;
  }

  .booking-pro-section-subtitle {
    margin-top: 5px !important;
    font-size: 13px !important;
    line-height: 1.38 !important;
    color: #64748b !important;
  }

  /* VISIT MODE — compacto como el mockup */
  .booking-visit-mode-card {
    padding: 16px !important;
    background: #ffffff !important;
  }

  .booking-visit-mode-card .booking-pro-section-title {
    font-size: 20px !important;
    line-height: 1.08 !important;
  }

  .booking-visit-mode-card .booking-pro-section-subtitle {
    font-size: 13px !important;
    margin-top: 5px !important;
  }

  .booking-visit-mode-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 10px !important;
    margin-top: 14px !important;
  }

  .booking-visit-mode-option {
    min-height: 66px !important;
    border-radius: 17px !important;
    padding: 11px 13px !important;
    display: flex !important;
    align-items: center !important;
    gap: 11px !important;
    background: #ffffff !important;
    border: 1.5px solid #e2e8f0 !important;
    box-shadow: none !important;
  }

  .booking-visit-mode-option:hover {
    transform: translateY(-1px) !important;
    border-color: rgba(15,118,110,.35) !important;
    box-shadow: 0 10px 22px rgba(15,23,42,.055) !important;
  }

  .booking-visit-mode-option.is-active {
    border-color: #10b981 !important;
    background: #f0fdfa !important;
    box-shadow: 0 0 0 3px rgba(16,185,129,.10) !important;
  }

  .booking-visit-mode-icon {
    width: 36px !important;
    height: 36px !important;
    border-radius: 13px !important;
    background: #ecfdf5 !important;
    color: #0f766e !important;
    font-size: 17px !important;
    flex: 0 0 auto !important;
  }

  .booking-visit-mode-option.is-active .booking-visit-mode-icon {
    background: #d1fae5 !important;
    color: #059669 !important;
  }

  .booking-visit-mode-copy strong {
    font-size: 14.5px !important;
    line-height: 1.1 !important;
    font-weight: 950 !important;
    color: #0f172a !important;
  }

  .booking-visit-mode-copy span {
    display: none !important;
  }

  /* WORKERS — misma proporción que el resto */
  .booking-worker-panel {
    padding: 18px !important;
    overflow: hidden !important;
  }

  .worker-desktop-scroller {
    gap: 10px !important;
    padding: 4px 4px 12px 4px !important;
  }

  .worker-desktop-scroller > * {
    flex: 0 0 112px !important;
    width: 112px !important;
    min-width: 112px !important;
    max-width: 112px !important;
  }

  /* LOCATION — compacto, elegante, debajo de trabajador */
  .booking-location-sticky-shell {
    position: static !important;
    z-index: auto !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .booking-location-sticky-shell:empty {
    display: none !important;
  }

  .booking-delivery-sticky-card,
  .booking-location-pro-card {
    padding: 18px !important;
    border-radius: 24px !important;
  }

  .booking-location-pro-title {
    font-size: 20px !important;
    line-height: 1.08 !important;
    letter-spacing: -.035em !important;
  }

  .booking-delivery-sticky-card .booking-pro-section-subtitle {
    max-width: 520px !important;
  }

  .booking-delivery-sticky-card .booking-zone-search {
    height: 42px !important;
    margin-top: 12px !important;
    border-radius: 14px !important;
    font-size: 13px !important;
  }

  .booking-delivery-sticky-card .booking-zone-grid {
    grid-template-columns: 1fr !important;
    gap: 9px !important;
    margin-top: 11px !important;
    max-height: none !important;
    overflow: visible !important;
    padding-right: 0 !important;
  }

  .booking-zone-option {
    min-height: 76px !important;
    border-radius: 17px !important;
    padding: 12px 13px !important;
    gap: 5px !important;
    box-shadow: none !important;
  }

  .booking-zone-title {
    font-size: 14px !important;
  }

  .booking-zone-badge {
    font-size: 9.5px !important;
    padding: 5px 8px !important;
  }

  .booking-zone-note,
  .booking-zone-fee {
    font-size: 11.5px !important;
  }

  .booking-address-box {
    margin-top: 11px !important;
    border-radius: 17px !important;
    padding: 12px !important;
    background: #f8fafc !important;
  }

  .booking-address-box label {
    font-size: 13px !important;
    margin-bottom: 7px !important;
  }

  .booking-address-box textarea {
    min-height: 72px !important;
    border-radius: 14px !important;
    font-size: 13px !important;
    line-height: 1.38 !important;
    padding: 11px !important;
  }

  .booking-address-helper {
    font-size: 10.5px !important;
    margin-top: 6px !important;
  }

  .booking-delivery-fee-box {
    margin-top: 10px !important;
    border-radius: 15px !important;
    padding: 10px 12px !important;
  }

  /* SUMMARY — sidebar armónico, no se monta encima */
  .booking-desktop-confirm {
    display: block !important;
    position: sticky !important;
    top: 16px !important;
    z-index: 2 !important;
    width: 100% !important;
    max-width: 100% !important;
    align-self: start !important;
  }

  .booking-final-summary-card {
    padding: 20px !important;
    margin-top: 0 !important;
    overflow: hidden !important;
  }

  .booking-summary-location {
    border-radius: 15px !important;
    padding: 10px 11px !important;
    margin-top: 9px !important;
  }

  .booking-summary-price-row {
    font-size: 13px !important;
    padding: 5px 0 !important;
  }

  .booking-summary-price-row.total {
    font-size: 16px !important;
    margin-top: 7px !important;
    padding-top: 12px !important;
  }

  /* Evita espacios visuales cuando una sección no aplica */
  .booking-location-sticky-shell:empty,
  .booking-worker-panel:empty,
  .booking-desktop-confirm:empty {
    display: none !important;
  }

  @media (max-width: 768px) {
    .booking-pro-root {
      width: 100% !important;
      max-width: 100vw !important;
      overflow-x: hidden !important;
      padding: 12px 10px calc(126px + env(safe-area-inset-bottom, 0px)) !important;
      background: #f8fafc !important;
    }

    .booking-pro-layout {
      display: flex !important;
      flex-direction: column !important;
      gap: 12px !important;
    }

    .booking-pro-left {
      gap: 12px !important;
    }

    .booking-pro-card,
    .booking-visit-mode-card,
    .booking-worker-panel,
    .booking-location-pro-card,
    .booking-final-summary-card {
      width: 100% !important;
      max-width: 100% !important;
      border-radius: 20px !important;
      box-shadow: 0 9px 24px rgba(15,23,42,.055) !important;
    }

    .booking-pro-card {
      padding: 15px !important;
    }

    .booking-pro-section-title {
      font-size: 19px !important;
    }

    .booking-pro-section-subtitle {
      font-size: 12.5px !important;
      margin-top: 4px !important;
    }

    .booking-visit-mode-card {
      padding: 14px !important;
    }

    .booking-visit-mode-card .booking-pro-section-title {
      font-size: 18px !important;
    }

    .booking-visit-mode-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 8px !important;
      margin-top: 11px !important;
    }

    .booking-visit-mode-option {
      min-height: 58px !important;
      border-radius: 15px !important;
      padding: 9px 10px !important;
      gap: 8px !important;
    }

    .booking-visit-mode-icon {
      width: 31px !important;
      height: 31px !important;
      border-radius: 11px !important;
      font-size: 15px !important;
    }

    .booking-visit-mode-copy strong {
      font-size: 13px !important;
    }

    .booking-worker-panel {
      padding: 14px !important;
    }

    .worker-desktop-scroller > * {
      flex: 0 0 100px !important;
      width: 100px !important;
      min-width: 100px !important;
      max-width: 100px !important;
    }

    .booking-location-sticky-shell {
      position: static !important;
      bottom: auto !important;
      top: auto !important;
      z-index: auto !important;
    }

    .booking-delivery-sticky-card,
    .booking-location-pro-card {
      padding: 14px !important;
      border-radius: 20px !important;
      box-shadow: 0 9px 24px rgba(15,23,42,.055) !important;
    }

    .booking-location-pro-title {
      font-size: 18px !important;
    }

    .booking-zone-option {
      min-height: 68px !important;
      border-radius: 15px !important;
      padding: 10px 11px !important;
    }

    .booking-address-box {
      padding: 10px !important;
      border-radius: 15px !important;
    }

    .booking-address-box textarea {
      min-height: 62px !important;
      font-size: 12.5px !important;
    }

    .booking-desktop-confirm {
      display: none !important;
      position: static !important;
    }

    .booking-sticky-confirm {
      display: block !important;
      z-index: 800 !important;
    }
  }

  @media (max-width: 390px) {
    .booking-visit-mode-grid {
      grid-template-columns: 1fr !important;
    }
  }


  /* =========================================================
     EVENT PACKAGES V2 — temática + experiencia base + personajes
     ========================================================= */
  .booking-event-options {
    border-radius: 24px !important;
    border: 1px solid #e8eef5 !important;
    box-shadow: 0 12px 34px rgba(15,23,42,.055) !important;
  }

  .booking-event-controls {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 10px !important;
    margin-top: 14px !important;
  }

  .booking-event-control {
    display: grid !important;
    gap: 7px !important;
  }

  .booking-event-control label {
    color: #334155 !important;
    font-size: 12px !important;
    font-weight: 950 !important;
  }

  .booking-event-control select {
    width: 100% !important;
    min-height: 48px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 16px !important;
    background: #ffffff !important;
    color: #0f172a !important;
    font-size: 14px !important;
    font-weight: 850 !important;
    padding: 0 12px !important;
    outline: none !important;
  }

  .booking-character-section {
    margin-top: 14px !important;
    display: grid !important;
    gap: 10px !important;
  }

  .booking-character-section-head {
    display: grid !important;
    gap: 3px !important;
  }

  .booking-character-section-head strong {
    color: #0f172a !important;
    font-size: 14px !important;
    font-weight: 950 !important;
  }

  .booking-character-section-head span {
    color: #64748b !important;
    font-size: 12px !important;
    line-height: 1.35 !important;
    font-weight: 750 !important;
  }

  .booking-character-grid {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) !important;
    gap: 9px !important;
    margin-top: 0 !important;
  }

  .booking-character-choice {
    min-height: 74px !important;
    border-radius: 18px !important;
    padding: 12px !important;
    border: 1.5px solid #e2e8f0 !important;
    background: #ffffff !important;
    text-align: left !important;
    font-size: 14px !important;
    font-weight: 950 !important;
    color: #0f172a !important;
    box-shadow: none !important;
    transition: border-color .15s ease, background .15s ease, box-shadow .15s ease, transform .15s ease !important;
  }

  .booking-character-choice:hover {
    transform: translateY(-1px) !important;
    border-color: rgba(15,118,110,.35) !important;
    box-shadow: 0 10px 24px rgba(15,23,42,.055) !important;
  }

  .booking-character-choice.is-selected {
    border-color: #10b981 !important;
    background: #f0fdfa !important;
    box-shadow: 0 0 0 3px rgba(16,185,129,.10) !important;
  }

  .booking-character-choice small {
    display: block !important;
    margin-top: 5px !important;
    color: #0f766e !important;
    font-size: 12px !important;
    font-weight: 850 !important;
  }

  .booking-event-summary-pill {
    margin-top: 14px !important;
    border: 1px solid #ccfbf1 !important;
    background: #f0fdfa !important;
    color: #0f766e !important;
    border-radius: 16px !important;
    padding: 11px 12px !important;
    font-size: 13px !important;
    font-weight: 950 !important;
  }

  @media (max-width:768px) {
    .booking-event-controls { grid-template-columns: 1fr !important; }
    .booking-character-grid { grid-template-columns: 1fr !important; }
    .booking-character-choice { min-height: 64px !important; }
  }

  /* =========================================================
     PHASE LOCATION FIX — DROPDOWN FOR MANY ZONES
     ========================================================= */
  .booking-zone-dropdown-shell {
    margin-top: 12px;
    display: grid;
    gap: 7px;
  }

  .booking-zone-dropdown-label {
    color: #0f172a;
    font-size: 12px;
    font-weight: 950;
    letter-spacing: -.01em;
  }

  .booking-zone-dropdown {
    width: 100%;
    min-height: 46px;
    border: 1.5px solid #e2e8f0;
    border-radius: 16px;
    background: #ffffff;
    color: #0f172a;
    padding: 0 13px;
    font-size: 14px;
    font-weight: 850;
    outline: none;
    box-sizing: border-box;
    box-shadow: 0 8px 20px rgba(15,23,42,.04);
  }

  .booking-zone-dropdown:focus,
  .booking-zone-dropdown.is-selected {
    border-color: rgba(15,118,110,.42);
    box-shadow: 0 0 0 4px rgba(15,118,110,.10);
  }

  .booking-zone-dropdown-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.35;
  }

  @media (max-width: 768px) {
    .booking-zone-dropdown {
      min-height: 44px;
      border-radius: 15px;
      font-size: 13px;
    }
  }


  /* =========================================================
     BOOKING INTERFACE V2 — PHASE 1.1 SAFE VISUAL COMPACT REVAMP
     UI-only override. Does not touch booking logic.
     ========================================================= */

  .booking-pro-root {
    max-width: 1040px !important;
    padding: 6px 14px calc(96px + env(safe-area-inset-bottom, 0px)) !important;
  }

  .booking-modal-header,
  .booking-public-modal-header,
  .booking-topbar,
  .booking-shell-header {
    min-height: 42px !important;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }

  .booking-pro-header {
    gap: 10px !important;
    margin-top: 0 !important;
    margin-bottom: 8px !important;
    padding: 0 !important;
  }

  .booking-pro-logo {
    width: 52px !important;
    height: 52px !important;
    border-radius: 16px !important;
    box-shadow: 0 7px 16px rgba(15, 23, 42, .055) !important;
  }

  .booking-pro-title {
    font-size: clamp(21px, 3vw, 27px) !important;
    line-height: 1.02 !important;
    letter-spacing: -.045em !important;
  }

  .booking-pro-subtitle {
    margin-top: 2px !important;
    font-size: 12px !important;
    line-height: 1.28 !important;
    font-weight: 750 !important;
  }

  .booking-pro-layout {
    gap: 13px !important;
    grid-template-columns: minmax(0, 1.55fr) minmax(300px, .9fr) !important;
  }

  .booking-pro-left {
    gap: 10px !important;
  }

  .booking-pro-card,
  .booking-visit-mode-card,
  .booking-location-pro-card {
    border-radius: 22px !important;
    padding: 15px !important;
    box-shadow: 0 9px 23px rgba(15, 23, 42, .042) !important;
    border-color: #edf2f7 !important;
  }

  .booking-pro-section-title {
    font-size: 18px !important;
    line-height: 1.08 !important;
  }

  .booking-pro-section-subtitle {
    margin-top: 4px !important;
    font-size: 12px !important;
    line-height: 1.34 !important;
  }

  .booking-calendar-head {
    grid-template-columns: 34px minmax(0, 1fr) 34px !important;
    gap: 8px !important;
    margin-bottom: 9px !important;
  }

  .booking-month-button {
    width: 34px !important;
    height: 34px !important;
    font-size: 20px !important;
    box-shadow: none !important;
    background: #f8fafc !important;
  }

  .booking-month-label {
    font-size: 15px !important;
  }

  .booking-weekdays {
    gap: 4px !important;
    margin-bottom: 6px !important;
    font-size: 10px !important;
  }

  .booking-days-grid {
    gap: 4px !important;
  }

  .booking-day-button {
    min-height: 31px !important;
    max-height: 35px !important;
    font-size: 12px !important;
    border-radius: 12px !important;
    aspect-ratio: 1.15 !important;
    box-shadow: none !important;
  }

  .booking-day-placeholder {
    aspect-ratio: 1.15 !important;
  }

  .booking-time-section {
    margin-top: 15px !important;
  }

  .booking-time-grid {
    gap: 7px !important;
  }

  .booking-time-button {
    min-width: 66px !important;
    height: 36px !important;
    border-radius: 13px !important;
    font-size: 12px !important;
    box-shadow: none !important;
  }

  .booking-visit-mode-grid {
    margin-top: 10px !important;
    gap: 8px !important;
  }

  .booking-visit-mode-option {
    min-height: 66px !important;
    border-radius: 16px !important;
    padding: 10px !important;
    grid-template-columns: 34px minmax(0, 1fr) !important;
    gap: 9px !important;
  }

  .booking-visit-mode-icon {
    width: 34px !important;
    height: 34px !important;
    border-radius: 12px !important;
    font-size: 16px !important;
  }

  .booking-location-pro-title {
    font-size: 15px !important;
  }

  .booking-zone-search {
    height: 38px !important;
    margin-top: 9px !important;
    border-radius: 13px !important;
    font-size: 12px !important;
  }

  .booking-zone-grid {
    margin-top: 8px !important;
    grid-template-columns: 1fr !important;
    gap: 6px !important;
    max-height: 190px !important;
    overflow-y: auto !important;
  }

  .booking-zone-option {
    min-height: 0 !important;
    border-radius: 14px !important;
    padding: 9px 10px !important;
  }

  .booking-zone-note {
    display: none !important;
  }

  .booking-address-box {
    margin-top: 8px !important;
    padding: 10px !important;
    border-radius: 14px !important;
    background: #fff !important;
  }

  .booking-address-box textarea {
    min-height: 48px !important;
    max-height: 92px !important;
    border-radius: 12px !important;
    font-size: 13px !important;
  }

  .booking-address-helper {
    display: none !important;
  }

  @media (max-width: 768px) {
    .booking-pro-root {
      padding: 6px 10px calc(132px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-pro-header {
      display: flex !important;
      margin-bottom: 8px !important;
    }

    .booking-pro-logo {
      width: 44px !important;
      height: 44px !important;
      border-radius: 14px !important;
    }

    .booking-pro-title {
      font-size: 19px !important;
    }

    .booking-pro-subtitle {
      font-size: 11px !important;
      max-width: 250px !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .booking-pro-card,
    .booking-visit-mode-card,
    .booking-location-pro-card {
      padding: 13px !important;
      border-radius: 19px !important;
    }

    .booking-day-button {
      min-height: 30px !important;
      max-height: 34px !important;
      font-size: 11px !important;
      border-radius: 10px !important;
    }

    .booking-visit-mode-icon,
    .booking-visit-mode-copy span {
      display: none !important;
    }

    .booking-visit-mode-option {
      grid-template-columns: 1fr !important;
      min-height: 62px !important;
    }
  }


  /* =========================================================
     BOOKING INTERFACE V2 — PHASE 1.2 SERVICE-FIRST + TOP SPACE FIX
     Authoritative final visual override. UI-only.
     ========================================================= */

  .booking-pro-root {
    max-width: 1040px !important;
    margin: 0 auto !important;
    padding: 0 14px calc(96px + env(safe-area-inset-bottom, 0px)) !important;
  }

  .booking-pro-root > *:first-child {
    margin-top: 0 !important;
  }

  .booking-modal-header,
  .booking-public-modal-header,
  .booking-topbar,
  .booking-shell-header,
  .booking-modal-titlebar {
    min-height: 38px !important;
    height: 38px !important;
    padding: 4px 12px !important;
    align-items: center !important;
  }

  .booking-modal-header h1,
  .booking-public-modal-header h1,
  .booking-topbar h1,
  .booking-shell-header h1,
  .booking-modal-titlebar h1 {
    font-size: 15px !important;
    font-weight: 950 !important;
  }

  .booking-modal-header h1::after,
  .booking-public-modal-header h1::after,
  .booking-topbar h1::after,
  .booking-shell-header h1::after,
  .booking-modal-titlebar h1::after {
    content: "" !important;
  }

  .booking-pro-header {
    gap: 10px !important;
    margin-top: 0 !important;
    margin-bottom: 8px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .booking-pro-logo {
    width: 48px !important;
    height: 48px !important;
    border-radius: 15px !important;
    box-shadow: 0 6px 14px rgba(15, 23, 42, .05) !important;
  }

  .booking-pro-title {
    font-size: 24px !important;
    line-height: 1 !important;
    letter-spacing: -.045em !important;
  }

  .booking-pro-subtitle {
    margin-top: 2px !important;
    font-size: 11px !important;
    line-height: 1.25 !important;
  }

  .booking-pro-layout {
    gap: 12px !important;
    align-items: start !important;
  }

  .booking-pro-left {
    gap: 9px !important;
  }

  .booking-pro-card,
  .booking-service-card,
  .booking-visit-mode-card,
  .booking-location-pro-card {
    border-radius: 20px !important;
    padding: 14px !important;
    box-shadow: 0 7px 18px rgba(15, 23, 42, .04) !important;
    border-color: #edf2f7 !important;
  }

  .booking-service-card {
    display: block !important;
    order: -50 !important;
  }

  .booking-pro-section-title {
    font-size: 17px !important;
    line-height: 1.08 !important;
    margin: 0 !important;
  }

  .booking-pro-section-subtitle {
    margin-top: 4px !important;
    font-size: 11px !important;
    line-height: 1.3 !important;
  }

  .booking-service-select {
    display: block !important;
    margin-top: 10px !important;
    min-height: 50px !important;
    border-radius: 15px !important;
    padding: 9px 40px 9px 12px !important;
    box-shadow: none !important;
    background: #ffffff !important;
  }

  .booking-service-chevron {
    right: 14px !important;
    bottom: 20px !important;
    font-size: 17px !important;
  }

  .booking-service-menu {
    border-radius: 16px !important;
  }

  .booking-service-option {
    padding: 10px 12px !important;
  }

  .booking-calendar-head {
    grid-template-columns: 32px minmax(0, 1fr) 32px !important;
    gap: 7px !important;
    margin-bottom: 8px !important;
  }

  .booking-month-button {
    width: 32px !important;
    height: 32px !important;
    font-size: 19px !important;
    box-shadow: none !important;
    background: #f8fafc !important;
  }

  .booking-month-label {
    font-size: 14px !important;
  }

  .booking-weekdays {
    gap: 4px !important;
    margin-bottom: 5px !important;
    font-size: 9px !important;
  }

  .booking-days-grid {
    gap: 4px !important;
  }

  .booking-day-button {
    min-height: 30px !important;
    max-height: 34px !important;
    font-size: 11px !important;
    border-radius: 10px !important;
    aspect-ratio: 1.15 !important;
    box-shadow: none !important;
  }

  .booking-day-placeholder {
    aspect-ratio: 1.15 !important;
  }

  .booking-time-section {
    margin-top: 14px !important;
  }

  .booking-time-grid {
    gap: 7px !important;
  }

  .booking-time-button {
    min-width: 62px !important;
    height: 34px !important;
    border-radius: 12px !important;
    font-size: 12px !important;
    box-shadow: none !important;
  }

  .booking-visit-mode-grid {
    margin-top: 9px !important;
    gap: 8px !important;
  }

  .booking-visit-mode-option {
    min-height: 62px !important;
    border-radius: 15px !important;
    padding: 9px !important;
    grid-template-columns: 32px minmax(0, 1fr) !important;
    gap: 8px !important;
  }

  .booking-visit-mode-icon {
    width: 32px !important;
    height: 32px !important;
    border-radius: 11px !important;
    font-size: 15px !important;
  }

  .booking-visit-mode-copy strong {
    font-size: 12px !important;
  }

  .booking-visit-mode-copy span {
    margin-top: 2px !important;
    font-size: 10px !important;
  }

  .booking-location-pro-title {
    font-size: 14px !important;
  }

  .booking-zone-search {
    height: 36px !important;
    margin-top: 8px !important;
    border-radius: 12px !important;
    font-size: 12px !important;
  }

  .booking-zone-grid {
    margin-top: 7px !important;
    grid-template-columns: 1fr !important;
    gap: 6px !important;
    max-height: 172px !important;
    overflow-y: auto !important;
  }

  .booking-zone-option {
    min-height: 0 !important;
    border-radius: 13px !important;
    padding: 8px 10px !important;
  }

  .booking-zone-title {
    font-size: 12px !important;
  }

  .booking-zone-badge {
    font-size: 9px !important;
    padding: 3px 7px !important;
  }

  .booking-zone-note {
    display: none !important;
  }

  .booking-address-box {
    margin-top: 8px !important;
    padding: 9px !important;
    border-radius: 13px !important;
    background: #ffffff !important;
  }

  .booking-address-box label {
    font-size: 11px !important;
    margin-bottom: 5px !important;
  }

  .booking-address-box textarea {
    min-height: 44px !important;
    max-height: 88px !important;
    border-radius: 11px !important;
    padding: 9px !important;
    font-size: 12px !important;
    line-height: 1.25 !important;
  }

  .booking-address-helper {
    display: none !important;
  }

  .booking-worker-panel {
    padding: 13px !important;
    border-radius: 20px !important;
  }

  .worker-desktop-scroller > * {
    flex-basis: 102px !important;
    width: 102px !important;
    min-width: 102px !important;
    max-width: 102px !important;
  }

  @media (max-width: 768px) {
    .booking-pro-root {
      padding: 0 10px calc(128px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-pro-header {
      display: flex !important;
      margin-bottom: 7px !important;
    }

    .booking-pro-logo {
      width: 40px !important;
      height: 40px !important;
      border-radius: 13px !important;
    }

    .booking-pro-title {
      font-size: 18px !important;
    }

    .booking-pro-subtitle {
      font-size: 10px !important;
      max-width: 230px !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .booking-pro-card,
    .booking-service-card,
    .booking-visit-mode-card,
    .booking-location-pro-card {
      padding: 12px !important;
      border-radius: 18px !important;
    }

    .booking-pro-section-title {
      font-size: 15px !important;
    }

    .booking-service-select {
      min-height: 48px !important;
    }

    .booking-visit-mode-grid {
      grid-template-columns: 1fr 1fr !important;
    }

    .booking-visit-mode-option {
      grid-template-columns: 1fr !important;
      min-height: 58px !important;
    }

    .booking-visit-mode-icon,
    .booking-visit-mode-copy span {
      display: none !important;
    }
  }


  /* =========================================================
     BOOKING INTERFACE V2 — PHASE 2 DELIVERY UX PRO
     Location as premium compact selector. UI-only.
     ========================================================= */

  .booking-delivery-sticky-card,
  .booking-location-pro-card {
    background: #ffffff !important;
    border: 1px solid #e6edf5 !important;
  }

  .booking-location-pro-title {
    font-size: 14px !important;
    letter-spacing: -.02em !important;
  }

  .booking-location-pro-card .booking-pro-section-subtitle,
  .booking-delivery-sticky-card .booking-pro-section-subtitle {
    display: none !important;
  }

  .booking-zone-search {
    height: 38px !important;
    border-radius: 13px !important;
    background: #f8fafc !important;
    border: 1px solid #e2e8f0 !important;
    box-shadow: none !important;
  }

  .booking-zone-search::placeholder {
    color: #94a3b8 !important;
  }

  .booking-zone-grid {
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 6px !important;
    max-height: 210px !important;
    overflow-y: auto !important;
    padding-right: 2px !important;
  }

  .booking-zone-option {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) auto !important;
    align-items: center !important;
    min-height: 46px !important;
    border-radius: 14px !important;
    padding: 9px 10px !important;
    border: 1px solid #e2e8f0 !important;
    background: #ffffff !important;
    box-shadow: none !important;
    gap: 8px !important;
  }

  .booking-zone-option:hover {
    transform: none !important;
    box-shadow: none !important;
    border-color: rgba(15,118,110,.35) !important;
    background: #fbfefd !important;
  }

  .booking-zone-option.is-active {
    border-color: #0f766e !important;
    background: #f0fdfa !important;
    box-shadow: 0 0 0 3px rgba(15,118,110,.08) !important;
  }

  .booking-zone-top {
    display: contents !important;
  }

  .booking-zone-title {
    font-size: 12px !important;
    line-height: 1.2 !important;
    font-weight: 950 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  .booking-zone-badge {
    justify-self: end !important;
    background: #eef2f7 !important;
    color: #475569 !important;
    border-radius: 999px !important;
    padding: 4px 8px !important;
    font-size: 9px !important;
    line-height: 1 !important;
    max-width: 88px !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .booking-zone-option.is-active .booking-zone-badge {
    background: #0f766e !important;
    color: #ffffff !important;
  }

  .booking-zone-fee {
    grid-column: 1 / -1 !important;
    margin-top: -2px !important;
    color: #0f766e !important;
    font-size: 11px !important;
    font-weight: 900 !important;
  }

  .booking-zone-fee:empty {
    display: none !important;
  }

  .booking-zone-note {
    display: none !important;
  }

  .booking-address-box {
    margin-top: 8px !important;
    border-radius: 14px !important;
    background: #ffffff !important;
    border: 1px solid #e2e8f0 !important;
    padding: 9px !important;
  }

  .booking-address-box label {
    font-size: 11px !important;
    color: #334155 !important;
    margin-bottom: 5px !important;
  }

  .booking-address-box textarea {
    min-height: 42px !important;
    max-height: 76px !important;
    border-radius: 12px !important;
    border: 1px solid #e2e8f0 !important;
    background: #f8fafc !important;
    padding: 9px 10px !important;
    font-size: 12px !important;
    line-height: 1.25 !important;
    box-shadow: none !important;
  }

  .booking-address-box textarea:focus {
    background: #ffffff !important;
    border-color: rgba(15,118,110,.45) !important;
    box-shadow: 0 0 0 3px rgba(15,118,110,.08) !important;
  }

  .booking-address-helper {
    display: none !important;
  }

  .booking-delivery-fee-box {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 10px !important;
    margin-top: 8px !important;
    padding: 8px 10px !important;
    border-radius: 13px !important;
    background: #f0fdfa !important;
    border: 1px solid #99f6e4 !important;
  }

  .booking-delivery-fee-box strong,
  .booking-delivery-fee-box span {
    margin: 0 !important;
    display: inline !important;
  }

  .booking-delivery-fee-box strong {
    font-size: 12px !important;
  }

  .booking-delivery-fee-box span {
    font-size: 11px !important;
    color: #0f766e !important;
  }

  .booking-summary-location {
    grid-template-columns: minmax(0,1fr) auto !important;
    border-radius: 13px !important;
    padding: 8px 10px !important;
  }

  .booking-summary-location span {
    font-size: 9px !important;
  }

  .booking-summary-location strong {
    font-size: 12px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .booking-summary-location small {
    display: none !important;
  }

  .booking-summary-location button {
    min-height: 30px !important;
    padding: 0 10px !important;
    font-size: 11px !important;
  }

  @media (max-width: 768px) {
    .booking-zone-grid {
      max-height: 180px !important;
    }

    .booking-zone-option {
      min-height: 44px !important;
      padding: 8px 9px !important;
    }

    .booking-address-box textarea {
      min-height: 40px !important;
    }

    .booking-summary-location {
      grid-template-columns: 1fr !important;
    }
  }


  /* =========================================================
     BOOKING INTERFACE V2 — PHASE 3 SAFE ADAPTIVE BUSINESS UX
     CSS-only prioritization by vertical. No booking logic changes.
     ========================================================= */

  .booking-pro-root.booking-vertical-events .booking-service-card h2::after {
    content: " para tu evento";
  }

  .booking-pro-root.booking-vertical-home .booking-location-sticky-shell,
  .booking-pro-root.booking-vertical-home .booking-location-pro-card,
  .booking-pro-root.booking-vertical-home .booking-delivery-sticky-card {
    order: -60 !important;
    border-color: #bbf7d0 !important;
    background: linear-gradient(180deg, #ffffff 0%, #f7fef9 100%) !important;
  }

  .booking-pro-root.booking-vertical-home .booking-location-pro-title::after {
    content: " / dirección";
  }

  .booking-pro-root.booking-vertical-events .booking-service-card,
  .booking-pro-root.booking-vertical-events .booking-location-pro-card,
  .booking-pro-root.booking-vertical-events .booking-delivery-sticky-card {
    border-color: #dbeafe !important;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%) !important;
  }

  .booking-pro-root.booking-vertical-beauty .booking-worker-panel {
    border-color: #fce7f3 !important;
    background: linear-gradient(180deg, #ffffff 0%, #fff7fb 100%) !important;
  }

  .booking-pro-root.booking-vertical-fitness .booking-time-section {
    margin-top: 12px !important;
  }

  .booking-pro-root.booking-vertical-events .booking-service-select,
  .booking-pro-root.booking-vertical-beauty .booking-service-select,
  .booking-pro-root.booking-vertical-home .booking-service-select {
    border-color: rgba(15,118,110,.20) !important;
  }

  .booking-pro-root.booking-vertical-events .booking-zone-option.is-active {
    border-color: #2563eb !important;
    background: #eff6ff !important;
    box-shadow: 0 0 0 3px rgba(37,99,235,.08) !important;
  }

  .booking-pro-root.booking-vertical-home .booking-zone-grid {
    max-height: 230px !important;
  }

  .booking-pro-root.booking-vertical-home .booking-address-box textarea {
    min-height: 50px !important;
  }

  /* keep service visually first unless the business is primarily home/delivery */
  .booking-pro-root:not(.booking-vertical-home) .booking-service-card {
    order: -50 !important;
  }

  .booking-pro-root.booking-vertical-beauty .booking-worker-panel {
    order: -15 !important;
  }

  @media (max-width: 768px) {
    .booking-pro-root.booking-vertical-home .booking-location-sticky-shell,
    .booking-pro-root.booking-vertical-home .booking-location-pro-card,
    .booking-pro-root.booking-vertical-home .booking-delivery-sticky-card {
      order: -80 !important;
    }

    .booking-pro-root:not(.booking-vertical-home) .booking-service-card {
      order: -70 !important;
    }

    .booking-pro-root.booking-vertical-beauty .booking-worker-panel {
      order: -20 !important;
    }
  }


  /* =========================================================
     ULTRA HARD TOP GAP FIX — Booking modal
     Removes remaining blank area between modal titlebar and business header.
     ========================================================= */

  .business-booking-body,
  .business-booking-content,
  .business-booking-scroll,
  .business-booking-inner,
  .business-booking-panel-body,
  .booking-modal-body,
  .booking-modal-content,
  .booking-modal-scroll,
  .booking-modal-inner {
    padding-top: 0 !important;
    margin-top: 0 !important;
  }

  .business-booking-body > *,
  .business-booking-content > *,
  .business-booking-scroll > *,
  .business-booking-inner > *,
  .booking-modal-body > *,
  .booking-modal-content > *,
  .booking-modal-scroll > *,
  .booking-modal-inner > * {
    margin-top: 0 !important;
  }

  .business-booking-body .booking-pro-root,
  .business-booking-content .booking-pro-root,
  .business-booking-scroll .booking-pro-root,
  .business-booking-inner .booking-pro-root,
  .booking-modal-body .booking-pro-root,
  .booking-modal-content .booking-pro-root,
  .booking-modal-scroll .booking-pro-root,
  .booking-modal-inner .booking-pro-root,
  .booking-pro-root {
    padding-top: 0 !important;
    margin-top: 0 !important;
    transform: translateY(-54px);
  }

  .business-booking-body .booking-pro-header,
  .business-booking-content .booking-pro-header,
  .business-booking-scroll .booking-pro-header,
  .business-booking-inner .booking-pro-header,
  .booking-modal-body .booking-pro-header,
  .booking-modal-content .booking-pro-header,
  .booking-modal-scroll .booking-pro-header,
  .booking-modal-inner .booking-pro-header,
  .booking-pro-header {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .booking-pro-root::after {
    content: "";
    display: block;
    height: 54px;
  }

  @media (max-width: 768px) {
    .business-booking-body .booking-pro-root,
    .business-booking-content .booking-pro-root,
    .business-booking-scroll .booking-pro-root,
    .business-booking-inner .booking-pro-root,
    .booking-modal-body .booking-pro-root,
    .booking-modal-content .booking-pro-root,
    .booking-modal-scroll .booking-pro-root,
    .booking-modal-inner .booking-pro-root,
    .booking-pro-root {
      transform: translateY(-36px);
    }

    .booking-pro-root::after {
      height: 36px;
    }
  }

/* =========================================================
   HOTFIX BOOKING MOBILE SERVICE DROPDOWN
   Keeps the service list above dates/calendar on mobile.
   ========================================================= */
.booking-service-card {
  isolation: isolate !important;
}

.booking-service-card.is-open {
  z-index: 100030 !important;
}

.booking-service-backdrop {
  display: none;
}

.booking-service-menu {
  z-index: 100040 !important;
  max-height: 360px;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 768px) {
  .booking-service-card.is-open {
    position: relative !important;
    z-index: 100050 !important;
  }

  .booking-service-card.is-open .booking-service-backdrop {
    display: block !important;
    position: fixed !important;
    inset: 0 !important;
    z-index: 100045 !important;
    border: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    background: rgba(15, 23, 42, .28) !important;
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
  }

  .booking-service-card.is-open .booking-service-menu {
    position: fixed !important;
    left: 12px !important;
    right: 12px !important;
    top: auto !important;
    bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
    z-index: 100060 !important;
    max-height: min(58vh, 480px) !important;
    overflow-y: auto !important;
    border-radius: 24px !important;
    border: 1px solid rgba(226, 232, 240, .96) !important;
    box-shadow: 0 -20px 60px rgba(15, 23, 42, .26), 0 20px 55px rgba(15, 23, 42, .16) !important;
    animation: bookingServiceSheetUp .18s ease both !important;
  }

  .booking-service-card.is-open .booking-service-option {
    min-height: 68px !important;
    padding: 12px 14px !important;
  }
}

@keyframes bookingServiceSheetUp {
  from { opacity: 0; transform: translateY(14px) scale(.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}


  /* =========================================================
     BOOKING 1A — Service preselect + premium mobile/tablet sheet
     ========================================================= */

  .booking-service-list {
    max-height: 360px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .booking-service-sheet-head {
    display: none;
  }

  .booking-service-option {
    cursor: pointer;
  }

  @media (max-width: 920px) {
    .booking-service-card.is-open {
      position: relative !important;
      z-index: 100050 !important;
    }

    .booking-service-card.is-open .booking-service-backdrop {
      display: block !important;
      position: fixed !important;
      inset: 0 !important;
      z-index: 100045 !important;
      border: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      background: rgba(15, 23, 42, .36) !important;
      backdrop-filter: blur(8px) !important;
      -webkit-backdrop-filter: blur(8px) !important;
    }

    .booking-service-card.is-open .booking-service-menu {
      position: fixed !important;
      left: 10px !important;
      right: 10px !important;
      top: auto !important;
      bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important;
      z-index: 100060 !important;
      max-height: min(76dvh, 620px) !important;
      overflow: hidden !important;
      border-radius: 28px !important;
      border: 1px solid rgba(226, 232, 240, .96) !important;
      background: rgba(255, 255, 255, .985) !important;
      box-shadow: 0 -24px 70px rgba(15, 23, 42, .28), 0 24px 65px rgba(15, 23, 42, .18) !important;
      display: flex !important;
      flex-direction: column !important;
      animation: bookingServiceSheetUp .18s ease both !important;
    }

    .booking-service-sheet-head {
      display: flex !important;
      align-items: flex-start !important;
      justify-content: space-between !important;
      gap: 14px !important;
      padding: 16px 18px 13px !important;
      border-bottom: 1px solid #eef2f7 !important;
      flex: 0 0 auto !important;
    }

    .booking-service-sheet-head strong {
      display: block !important;
      color: #0f172a !important;
      font-size: 20px !important;
      line-height: 1.05 !important;
      letter-spacing: -.04em !important;
      font-weight: 950 !important;
    }

    .booking-service-sheet-head span {
      display: block !important;
      margin-top: 5px !important;
      color: #64748b !important;
      font-size: 12.5px !important;
      font-weight: 750 !important;
      line-height: 1.35 !important;
    }

    .booking-service-sheet-head button {
      width: 38px !important;
      height: 38px !important;
      border-radius: 999px !important;
      border: 1px solid #e2e8f0 !important;
      background: #ffffff !important;
      color: #0f172a !important;
      font-size: 24px !important;
      line-height: 1 !important;
      flex: 0 0 auto !important;
    }

    .booking-service-list {
      flex: 1 1 auto !important;
      min-height: 0 !important;
      max-height: none !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch !important;
      overscroll-behavior: contain !important;
      padding: 8px 10px calc(14px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-service-card.is-open .booking-service-option {
      min-height: 68px !important;
      padding: 11px 10px !important;
      border-radius: 18px !important;
      border-bottom: 0 !important;
      margin-bottom: 6px !important;
    }
  }

  @media (max-width: 520px) {
    .booking-service-card.is-open .booking-service-menu {
      left: 8px !important;
      right: 8px !important;
      max-height: min(82dvh, 640px) !important;
      border-radius: 26px !important;
    }

    .booking-service-sheet-head {
      padding: 14px 16px 12px !important;
    }
  }


  /* =========================================================
     BOOKING SERVICE SELECTOR — MOBILE PORTAL BOTTOM SHEET FIX
     Professional fix:
     - Mobile/tablet service list renders in document.body via portal.
     - Prevents clipping caused by transformed/overflow containers.
     - Desktop keeps normal inline dropdown.
     ========================================================= */

  :root {
    --booking-service-sheet-safe-bottom: max(env(safe-area-inset-bottom, 0px), 34px);
    --booking-service-sheet-safe-top: max(env(safe-area-inset-top, 0px), 10px);
  }

  .booking-service-portal-backdrop {
    position: fixed !important;
    inset: 0 !important;
    z-index: 100045 !important;
    border: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: rgba(15, 23, 42, .38) !important;
    backdrop-filter: blur(7px) !important;
    -webkit-backdrop-filter: blur(7px) !important;
  }

  .booking-service-portal-menu {
    position: fixed !important;
    left: 12px !important;
    right: 12px !important;
    bottom: calc(var(--booking-service-sheet-safe-bottom) + 10px) !important;
    z-index: 100060 !important;

    max-height: min(68dvh, 590px) !important;
    min-height: min(320px, 58dvh) !important;

    background: rgba(255,255,255,.985) !important;
    border: 1px solid rgba(226, 232, 240, .96) !important;
    border-radius: 28px !important;
    box-shadow:
      0 -24px 70px rgba(15, 23, 42, .28),
      0 24px 65px rgba(15, 23, 42, .18) !important;

    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
    animation: bookingServiceSheetUp .18s ease both !important;
  }

  .booking-service-portal-menu .booking-service-sheet-head {
    display: flex !important;
    align-items: flex-start !important;
    justify-content: space-between !important;
    gap: 14px !important;
    padding: 16px 18px 13px !important;
    border-bottom: 1px solid #eef2f7 !important;
    flex: 0 0 auto !important;
    background: rgba(255,255,255,.98) !important;
  }

  .booking-service-portal-menu .booking-service-list {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    overscroll-behavior: contain !important;
    padding: 8px 10px 14px !important;
    box-sizing: border-box !important;
  }

  .booking-service-portal-menu .booking-service-option {
    width: 100% !important;
    min-height: 68px !important;
    padding: 11px 10px !important;
    border-radius: 18px !important;
    border: 0 !important;
    border-bottom: 0 !important;
    margin-bottom: 6px !important;
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    box-sizing: border-box !important;
  }

  @media (max-width: 520px) {
    .booking-service-portal-menu {
      left: 8px !important;
      right: 8px !important;
      bottom: calc(var(--booking-service-sheet-safe-bottom) + 8px) !important;
      max-height: min(70dvh, 620px) !important;
      min-height: min(340px, 60dvh) !important;
      border-radius: 26px !important;
    }

    .booking-service-portal-menu .booking-service-sheet-head {
      padding: 14px 16px 12px !important;
    }
  }

  /* When the mobile service sheet is open, don't let older inline dropdown rules show. */
  @media (max-width: 920px) {
    .booking-service-card.is-open > .booking-service-menu {
      display: none !important;
    }

    .booking-service-card.is-open > .booking-service-backdrop {
      display: none !important;
    }
  }

  /* =========================================================
     BOOKING SERVICE SELECTOR — NATIVE APP HARD FIX
     Avoids the gray overlay without the selectable service sheet on iOS/Android.
     The whole layer lives in document.body and uses a single high z-index context.
     ========================================================= */
  .booking-service-portal-layer {
    position: fixed !important;
    inset: 0 !important;
    z-index: 2147483000 !important;
    display: flex !important;
    align-items: flex-end !important;
    justify-content: center !important;
    pointer-events: auto !important;
    isolation: isolate !important;
  }

  .booking-service-portal-layer .booking-service-portal-backdrop {
    position: absolute !important;
    inset: 0 !important;
    z-index: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: rgba(15, 23, 42, .42) !important;
    backdrop-filter: blur(7px) !important;
    -webkit-backdrop-filter: blur(7px) !important;
  }

  .booking-service-portal-layer .booking-service-portal-menu {
    position: relative !important;
    z-index: 1 !important;
    left: auto !important;
    right: auto !important;
    top: auto !important;
    bottom: auto !important;
    width: calc(100vw - 18px) !important;
    max-width: 560px !important;
    max-height: min(74dvh, 640px) !important;
    min-height: min(320px, 58dvh) !important;
    margin: 0 9px calc(max(env(safe-area-inset-bottom, 0px), 12px) + 8px) !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    opacity: 1 !important;
    visibility: visible !important;
    transform: none !important;
    background: rgba(255,255,255,.99) !important;
    border: 1px solid rgba(226, 232, 240, .96) !important;
    border-radius: 26px !important;
    box-shadow: 0 -24px 70px rgba(15, 23, 42, .28), 0 24px 65px rgba(15, 23, 42, .18) !important;
    box-sizing: border-box !important;
  }

  .booking-service-portal-layer .booking-service-list {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px)) !important;
  }

  .booking-service-portal-layer .booking-service-option {
    pointer-events: auto !important;
    touch-action: manipulation !important;
  }


  /* =========================================================
     HOTFIX V52 — SERVICE SELECTOR CTA WITHOUT CHEVRON
     Keeps the clear Cambiar/Ver servicios CTA and removes the extra arrow pill.
     ========================================================= */
  .booking-service-card .booking-service-select-pro {
    position: relative !important;
    cursor: pointer !important;
    padding-right: 144px !important;
    min-height: 74px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
  }

  .booking-service-card .booking-service-select-pro:hover {
    border-color: rgba(15, 118, 110, .42) !important;
    box-shadow: 0 0 0 4px rgba(15, 118, 110, .08), 0 12px 26px rgba(15, 23, 42, .06) !important;
  }

  .booking-service-card .booking-service-select-pro:focus-visible {
    outline: none !important;
    border-color: #0f766e !important;
    box-shadow: 0 0 0 4px rgba(15, 118, 110, .14), 0 12px 26px rgba(15, 23, 42, .07) !important;
  }

  .booking-service-card .booking-service-select-pro::after {
    content: "Ver servicios";
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    height: 40px;
    min-width: 108px;
    padding: 0 16px;
    border-radius: 999px;
    background: #0f766e;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    line-height: 1;
    font-weight: 950;
    box-shadow: 0 12px 24px rgba(15, 118, 110, .18);
    pointer-events: none;
    white-space: nowrap;
  }

  .booking-service-card .booking-service-select-pro.is-selected::after {
    content: "Cambiar";
    min-width: 94px;
    background: #ffffff;
    color: #0f766e;
    border: 1px solid rgba(15, 118, 110, .22);
    box-shadow: 0 8px 18px rgba(15, 23, 42, .06);
  }

  .booking-service-card .booking-service-select-pro:disabled {
    cursor: progress !important;
    opacity: .78;
  }

  .booking-service-card .booking-service-select-pro:disabled::after {
    content: "Cargando";
    min-width: 86px;
    background: #e2e8f0;
    color: #64748b;
    box-shadow: none;
  }

  .booking-service-card > .booking-service-chevron {
    display: none !important;
  }

  @media (max-width: 640px) {
    .booking-service-card .booking-service-select-pro {
      padding-right: 104px !important;
      min-height: 68px !important;
    }

    .booking-service-card .booking-service-select-pro::after {
      content: "Elegir";
      right: 12px;
      min-width: 66px;
      height: 36px;
      padding: 0 12px;
      font-size: 11px;
    }

    .booking-service-card .booking-service-select-pro.is-selected::after {
      content: "Cambiar";
      min-width: 74px;
    }
  }

  @media (max-width: 380px) {
    .booking-service-card .booking-service-select-pro {
      padding-right: 96px !important;
    }

    .booking-service-card .booking-service-select-pro::after {
      right: 10px;
      min-width: 62px;
      padding: 0 10px;
    }
  }


  /* =========================================================
     HOTFIX V53 — BOOKING MODAL ABOVE GLOBAL HEADER
     The reservation modal is opened from BusinessPage, but
     BookingInterface owns the injected booking CSS. This keeps
     the booking overlay above the fixed Loop header without
     changing booking logic.
     ========================================================= */
  .business-booking-overlay {
    z-index: 2147483642 !important;
    isolation: isolate !important;
  }

  .business-booking-modal {
    position: relative !important;
    z-index: 2147483643 !important;
  }

  .business-booking-modal > div:first-child {
    position: relative !important;
    z-index: 2147483644 !important;
  }

  .business-booking-body {
    position: relative !important;
    z-index: 1 !important;
  }

  /* =========================================================
     HOTFIX V54B — MODAL HEADER SINGLE SOURCE
     BusinessPage already renders the premium booking modal header.
     Hide BookingInterface's internal business header only when the
     flow is embedded inside that modal, so the business name does
     not appear twice.
     ========================================================= */
  .business-booking-modal .booking-pro-header {
    display: none !important;
  }

  .business-booking-modal .booking-pro-root {
    padding-top: 14px !important;
  }

  @media (max-width: 768px) {
    .business-booking-modal .booking-pro-root {
      padding-top: 12px !important;
    }
  }


  /* Keep service selector sheet above the booking modal too. */
  .booking-service-portal-layer {
    z-index: 2147483646 !important;
  }

  @media (max-width: 920px) {
    .business-booking-overlay {
      padding-top: max(10px, env(safe-area-inset-top, 0px)) !important;
      padding-bottom: max(10px, env(safe-area-inset-bottom, 0px)) !important;
      align-items: center !important;
    }

    .business-booking-modal {
      width: min(100vw - 20px, 1100px) !important;
      height: min(100dvh - 20px, 860px) !important;
      max-height: calc(100dvh - 20px) !important;
    }
  }


  /* =========================================================
     HOTFIX V54A — MOBILE BOOKING POLISH
     Header/CTA polish is shared with BusinessPage. This part
     removes duplicate mobile confirmation, standardizes phone
     input through PhoneKit, and makes sticky CTA state-aware.
     ========================================================= */
  .booking-inline-confirm .loop-phone-input {
    width: 100% !important;
  }

  .booking-inline-confirm .loop-phone-control {
    min-height: 48px !important;
    border-radius: 18px !important;
    box-shadow: 0 10px 24px rgba(15, 23, 42, .045) !important;
  }

  .booking-inline-confirm .loop-phone-country {
    height: 46px !important;
    border-radius: 17px 0 0 17px !important;
  }

  .booking-inline-confirm .loop-phone-control input {
    height: 46px !important;
    font-weight: 900 !important;
    font-variant-numeric: tabular-nums !important;
  }

  .booking-sticky-confirm-v54a .booking-sticky-inner {
    gap: 12px !important;
  }

  .booking-sticky-kicker {
    font-size: 11px !important;
    color: #64748b !important;
    font-weight: 900 !important;
    line-height: 1.1 !important;
    letter-spacing: .01em !important;
  }

  .booking-sticky-summary {
    margin-top: 3px !important;
    font-size: 14px !important;
    color: #0f172a !important;
    font-weight: 950 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  .booking-sticky-confirm-v54a .booking-sticky-primary:disabled {
    opacity: .72 !important;
    cursor: not-allowed !important;
    background: #94a3b8 !important;
    box-shadow: none !important;
  }

  @media (max-width: 768px) {
    .booking-inline-confirm {
      margin-bottom: calc(86px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-inline-confirm .text-base.font-semibold,
    .booking-inline-confirm .booking-inline-confirm-button,
    .booking-inline-confirm .booking-inline-clear-button {
      display: none !important;
    }

    .booking-inline-confirm .space-y-2 {
      margin-bottom: 12px !important;
      font-size: 12px !important;
    }

    .booking-inline-confirm .booking-confirm-phone-field {
      margin-bottom: 0 !important;
    }

    .booking-sticky-confirm {
      z-index: 2147483645 !important;
      padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px)) !important;
      background: rgba(255,255,255,.94) !important;
      border-top: 1px solid rgba(226,232,240,.95) !important;
    }

    .booking-sticky-inner {
      grid-template-columns: minmax(0, 1fr) minmax(132px, auto) !important;
      gap: 10px !important;
    }

    .booking-sticky-primary {
      min-width: 132px !important;
      height: 50px !important;
      border-radius: 18px !important;
      font-size: 13px !important;
      white-space: nowrap !important;
    }
  }

  @media (max-width: 380px) {
    .booking-sticky-inner {
      grid-template-columns: 1fr !important;
    }

    .booking-sticky-primary {
      width: 100% !important;
    }
  }


  /* =========================================================
     LOOP BOOKING MOBILE SCROLL END LOCK V1
     - Static CTA no longer needs huge safe padding.
     - Removes blank scroll tail caused by old transform/after spacer.
     - Tightens gap between phone/summary card and confirm CTA.
     Mobile-only; desktop booking layout remains untouched.
     ========================================================= */
  @media (max-width: 768px) {
    .business-booking-body,
    .business-booking-content,
    .business-booking-scroll,
    .business-booking-inner,
    .booking-modal-body,
    .booking-modal-content,
    .booking-modal-scroll,
    .booking-modal-inner {
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }

    .business-booking-body .booking-pro-root,
    .business-booking-content .booking-pro-root,
    .business-booking-scroll .booking-pro-root,
    .business-booking-inner .booking-pro-root,
    .booking-modal-body .booking-pro-root,
    .booking-modal-content .booking-pro-root,
    .booking-modal-scroll .booking-pro-root,
    .booking-modal-inner .booking-pro-root,
    .booking-pro-root {
      /* Use layout-affecting margin instead of visual transform so the page
         does not keep an invisible tail at the bottom. */
      transform: none !important;
      margin-top: -36px !important;
      margin-bottom: 0 !important;
      padding-bottom: calc(14px + env(safe-area-inset-bottom, 0px)) !important;
      min-height: 0 !important;
      overflow-x: hidden !important;
    }

    body.is-mobile-app .booking-pro-root {
      padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-pro-root::after {
      content: none !important;
      display: none !important;
      height: 0 !important;
    }

    .booking-pro-layout,
    .booking-pro-left {
      margin-bottom: 0 !important;
      padding-bottom: 0 !important;
    }

    .booking-inline-confirm {
      margin-bottom: 6px !important;
    }

    .booking-inline-confirm.booking-pro-card {
      padding: 14px !important;
    }

    .booking-sticky-confirm,
    .booking-sticky-confirm-v54a {
      position: static !important;
      display: block !important;
      margin-top: 6px !important;
      margin-bottom: 0 !important;
      padding: 0 0 calc(8px + env(safe-area-inset-bottom, 0px)) !important;
      background: transparent !important;
      border-top: 0 !important;
      box-shadow: none !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    body.is-mobile-app .booking-sticky-confirm,
    body.is-mobile-app .booking-sticky-confirm-v54a {
      padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .booking-sticky-confirm-v54a .booking-sticky-inner {
      padding: 12px !important;
      gap: 10px !important;
      border-radius: 22px !important;
      box-shadow: 0 10px 26px rgba(15, 23, 42, .065) !important;
    }

    .booking-sticky-kicker {
      font-size: 10.5px !important;
    }

    .booking-sticky-summary {
      margin-top: 2px !important;
      font-size: 13px !important;
    }

    .booking-sticky-primary {
      height: 48px !important;
      border-radius: 17px !important;
      font-size: 13px !important;
    }
  }


  /* =========================================================
     LOOP BOOKING MOBILE HEADER SAFE START V2
     The previous mobile scroll-lock removed the blank bottom tail by using
     a negative top margin. Inside the BusinessPage modal that margin can
     slide the first booking card underneath the premium modal header.
     Keep the compact layout, but let the scroll body start below the header.
     Mobile-only; no booking/Firebase logic changes.
     ========================================================= */
  @media (max-width: 768px) {
    .business-booking-modal .business-booking-body,
    .business-booking-modal .business-booking-content,
    .business-booking-modal .business-booking-scroll,
    .business-booking-modal .business-booking-inner,
    .business-booking-modal .booking-modal-body,
    .business-booking-modal .booking-modal-content,
    .business-booking-modal .booking-modal-scroll,
    .business-booking-modal .booking-modal-inner {
      padding-top: 0 !important;
      margin-top: 0 !important;
      scroll-padding-top: 12px !important;
    }

    .business-booking-modal .booking-pro-root {
      transform: none !important;
      margin-top: 0 !important;
      padding-top: 10px !important;
      padding-bottom: calc(14px + env(safe-area-inset-bottom, 0px)) !important;
    }

    body.is-mobile-app .business-booking-modal .booking-pro-root {
      margin-top: 0 !important;
      padding-top: 10px !important;
      padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .business-booking-modal .booking-pro-layout,
    .business-booking-modal .booking-pro-left {
      padding-top: 0 !important;
      margin-top: 0 !important;
    }

    .business-booking-modal .booking-pro-root > *:first-child,
    .business-booking-modal .booking-pro-left > *:first-child {
      margin-top: 0 !important;
    }

    .business-booking-modal .booking-pro-root::after {
      content: none !important;
      display: none !important;
      height: 0 !important;
    }
  }


`;

const bookingSpacingStyles = `
  .booking-pro-root .booking-pro-layout {
    gap: 16px !important;
    align-items: start;
  }
  .booking-pro-root .booking-pro-left,
  .booking-pro-root .booking-pro-sidebar {
    display: flex;
    flex-direction: column;
    gap: 12px !important;
    min-width: 0;
  }
  .booking-pro-root .booking-pro-left > *,
  .booking-pro-root .booking-pro-sidebar > * {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  .booking-pro-root .booking-pro-card,
  .booking-pro-root .booking-visit-mode-card {
    padding: 16px !important;
    border-radius: 18px !important;
    box-shadow: 0 4px 18px rgba(15, 23, 42, .035) !important;
  }
  .booking-pro-root .booking-service-card {
    padding: 0 !important;
    border: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  .booking-pro-root .booking-pro-section-title {
    margin: 0 0 4px !important;
    font-size: 16px !important;
    line-height: 1.4 !important;
  }
  .booking-pro-root .booking-pro-section-subtitle {
    margin: 0 0 12px !important;
    line-height: 1.45 !important;
  }
  .booking-pro-root .booking-visit-mode-card > .booking-pro-section-subtitle:last-child {
    margin-bottom: 0 !important;
  }
  .booking-pro-root .booking-days-grid { row-gap: 4px !important; }
  .booking-pro-root .booking-time-grid { gap: 8px !important; }
  .booking-pro-root .booking-time-button { min-height: 44px; }
  .booking-pro-root .booking-pro-sidebar > * { flex-shrink: 0; }
  .booking-pro-root .booking-membership-benefits {
    height: auto !important;
    min-width: 0;
    box-sizing: border-box;
  }
  .booking-pro-root .booking-membership-benefits button {
    min-width: 0;
    max-width: 100%;
    height: auto !important;
    box-sizing: border-box;
  }
  .booking-pro-root .booking-membership-benefits button > span:first-child { flex: 1; min-width: 0; }
  .booking-pro-root .booking-membership-benefits button > span:last-child { flex-shrink: 0; }
  .booking-pro-root .booking-membership-benefits button strong {
    white-space: normal !important;
    overflow-wrap: anywhere;
    line-height: 1.4;
  }
  @media (max-width: 768px) {
    .booking-pro-root .booking-pro-layout { gap: 12px !important; }
    .booking-pro-root .booking-pro-card,
    .booking-pro-root .booking-visit-mode-card { padding: 14px !important; }
  }

  /* Keep intrinsic card widths inside the booking column, not outside the viewport. */
  .booking-pro-root .booking-pro-layout > .booking-pro-sidebar,
  .booking-pro-root .booking-pro-layout > .booking-pro-left { width:100%!important; max-width:100%!important; min-width:0!important; box-sizing:border-box!important; }
  .booking-pro-root .booking-pro-sidebar > *,
  .booking-pro-root .booking-pro-left > * { min-width:0!important; max-width:100%!important; box-sizing:border-box; }
  .booking-confirm-summary { width:100%;min-width:0;overflow-wrap:anywhere;font-size:14px; }
  .booking-confirm-summary strong { min-width:0;max-width:65%;white-space:normal!important;overflow-wrap:anywhere;font-weight:600!important; }
  .booking-confirm-summary .space-y-2 > div { min-width:0; }
  .booking-pro-root .booking-inline-confirm .text-base.font-semibold { display:block!important;font-size:16px!important;font-weight:600!important;margin-bottom:16px!important; }
  .booking-pro-root .booking-worker-panel { contain:none!important;background:#fff!important;border-color:#e3e9e6!important; }
  .booking-pro-root .worker-desktop-scroller { width:100%!important;max-width:100%!important;touch-action:pan-x pan-y!important;scroll-padding:4px; }
  .booking-pro-root .worker-desktop-scroller button { touch-action:pan-x pan-y!important; }
  .booking-worker-navigation { display:flex;justify-content:space-between;align-items:center;gap:12px;margin:10px 0; }
  .booking-worker-navigation>div { display:flex;gap:6px; }
  .booking-worker-navigation button { min-height:40px;min-width:40px;padding:8px 12px;border:1px solid #dce6e1;border-radius:8px;background:white;color:#226256;font:inherit;font-size:13px;cursor:pointer; }
  .booking-worker-dialog.worker-mobile-modal { display:flex!important;position:fixed!important;inset:0!important;z-index:2147483646!important;align-items:center!important;justify-content:center!important;padding:16px!important;background:#172b3b70!important; }
  .booking-worker-dialog .worker-mobile-modal-panel { width:min(620px,100%)!important;max-width:100%!important;max-height:calc(100dvh - 32px)!important;display:flex!important;flex-direction:column!important;overflow:hidden!important;min-width:0!important;border-radius:18px!important; }
  .booking-worker-dialog .worker-mobile-modal-header { flex-shrink:0; }
  .booking-worker-dialog .worker-mobile-modal-body { min-height:0;overflow-y:auto!important;overscroll-behavior:contain; }
  .booking-worker-dialog .worker-mobile-grid { grid-template-columns:repeat(2,minmax(0,1fr))!important; }
  .booking-worker-dialog .worker-mobile-option-card { min-width:0!important;max-width:100%!important; }
  .booking-worker-dialog .worker-search-input { width:100%;box-sizing:border-box; }
  .booking-pro-root :is(button,input,select,textarea):focus-visible { outline:2px solid #008779;outline-offset:3px; }
  .booking-pro-root .booking-pro-section-title { font-weight:600!important;letter-spacing:0!important; }
  .booking-pro-root .booking-pro-section-subtitle { font-weight:400!important;font-size:13px!important; }
  @media(max-width:768px) {
    .business-booking-modal { width:100%!important;max-width:100%!important;box-sizing:border-box!important; }
    .business-booking-modal .business-booking-body { overflow-y:auto!important;overflow-x:hidden!important;padding:0!important; }
    .business-booking-modal .booking-pro-root { width:100%!important;max-width:100%!important;height:auto!important;max-height:none!important;overflow:visible!important;padding:14px 12px 18px!important;margin:0!important;box-sizing:border-box!important; }
    .booking-pro-root .booking-pro-layout { width:100%!important;max-width:100%!important;min-width:0!important; }
    .booking-pro-root .booking-pro-card { border-radius:14px!important; }
    .booking-pro-root .booking-time-grid { grid-template-columns:repeat(3,minmax(0,1fr))!important; }
    .booking-pro-root .booking-inline-confirm .space-y-2 { font-size:13px!important;line-height:1.6; }
    .booking-pro-root .booking-sticky-summary { white-space:normal!important;overflow-wrap:anywhere;line-height:1.4;font-weight:600!important; }
    .booking-pro-root .booking-sticky-inner { min-width:0!important;width:100%!important;box-sizing:border-box; }
    .booking-pro-root .booking-sticky-inner>div { min-width:0; }
    .booking-worker-dialog.worker-mobile-modal { padding:12px!important; }
    .booking-worker-dialog .worker-mobile-modal-panel { max-height:calc(100dvh - 24px)!important; }
  }

  html:has(.business-booking-overlay),body:has(.business-booking-overlay) { overflow:hidden!important; }
  .booking-pro-root .booking-worker-panel.booking-pro-card { background:#fff!important; }
  .booking-pro-root .worker-desktop-scroller>button { box-shadow:none!important;border-radius:12px!important; }
  .booking-pro-root .worker-desktop-scroller>button[aria-pressed="true"] { border-color:#008779!important;background:#eff9f5!important; }
  .booking-worker-dialog .worker-mobile-option-card { font-weight:500!important;box-shadow:none!important;border-radius:12px!important; }

  .booking-pro-root .booking-days-grid,.booking-pro-root .booking-weekdays { grid-template-columns:repeat(7,minmax(0,1fr))!important;width:100%!important;min-width:0!important; }
  .booking-pro-root .booking-day-button,.booking-pro-root .booking-day-placeholder { width:100%!important;min-width:0!important;max-width:100%!important;padding-left:2px!important;padding-right:2px!important;box-sizing:border-box!important; }

  .booking-summary-edit { display:flex;flex-wrap:wrap;gap:6px 14px;margin:0 0 18px;padding:0 0 14px;border-bottom:1px solid #e4ebe7; }
  .booking-summary-edit button { font:inherit;font-size:12px;font-weight:500;color:#006c60;background:none;border:0;padding:6px 0;text-decoration:underline;text-underline-offset:3px;cursor:pointer;min-height:32px; }
  .booking-pro-root .event-time-periods { display:flex;flex-wrap:wrap;gap:6px;margin:12px 0; }
  .booking-pro-root .event-time-periods button { min-height:40px;padding:8px 12px;border-radius:8px;font:inherit;font-size:13px;border:1px solid #dce7e1;background:#fff;color:#44655c;cursor:pointer; }
  .booking-pro-root .event-time-periods button[aria-pressed="true"] { background:#eaf6f0;border-color:#008779;color:#006c60; }
  .booking-pro-root #booking-service-section,.booking-pro-root #booking-date-section,.booking-pro-root #booking-workers-section,.booking-pro-root #booking-location-section { scroll-margin-top:16px; }
  .booking-pro-root .booking-delivery-fee-box>div { display:grid;gap:5px; }

  .booking-selection-notice { display:flex;align-items:center;gap:12px;padding:12px 14px;margin:0 0 16px;background:#f0f7f4;border:1px solid #d8e7df;border-radius:10px;color:#365b4c;font-size:13px;line-height:1.5; }
  .booking-selection-notice button { flex-shrink:0;margin-left:auto;border:0;background:none;min-width:36px;min-height:36px;font-size:20px;color:inherit;cursor:pointer; }
  .booking-empty-slots { padding:14px;background:#f6f8f7;border-radius:10px;font-size:13px;color:#52695e; }
  .booking-empty-slots button { min-height:40px;margin-top:8px;border:1px solid #d5e2da;border-radius:8px;background:white;padding:8px 12px;color:#006c60;cursor:pointer; }
`;

function ServiceDropdownPro({ services, loading, selectedServiceId, selectedService, onSelectService, compact=false }) {
  const [open, setOpen] = React.useState(false);
  const [useMobilePortal, setUseMobilePortal] = React.useState(() => {
    try {
      if (typeof window === "undefined") return false;
      if (window.innerWidth <= 920) return true;
      if (window.Capacitor && typeof window.Capacitor.getPlatform === "function") {
        const platform = window.Capacitor.getPlatform();
        return platform === "ios" || platform === "android";
      }
    } catch (_) {}
    return false;
  });

  React.useEffect(() => {
    const updateMode = () => {
      try {
        setUseMobilePortal(
          typeof window !== "undefined" &&
          typeof window.matchMedia === "function" &&
          window.matchMedia("(max-width: 920px)").matches
        );
      } catch (_) {
        setUseMobilePortal(false);
      }
    };

    updateMode();
    window.addEventListener("resize", updateMode);
    window.addEventListener("orientationchange", updateMode);
    return () => {
      window.removeEventListener("resize", updateMode);
      window.removeEventListener("orientationchange", updateMode);
    };
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  React.useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [open]);

  const list = Array.isArray(services) ? services : [];

  const selectedLabel =
    selectedService?.name ||
    selectedService?.title ||
    (selectedServiceId ? "Servicio seleccionado" : "Selecciona un servicio");

  const selectedMeta = selectedService
    ? [
        selectedService.duration ? `${selectedService.duration} min` : null,
        formatBookingServicePrice(selectedService),
      ].filter(Boolean).join(" · ")
    : "Toca para ver servicios disponibles";

  const menuContent = (
    <>
      <div className="booking-service-sheet-head">
        <div>
          <strong>{t("Servicios disponibles")}</strong>
          <span>{t("Elige una opción para continuar con tu reserva.")}</span>
        </div>
        <button type="button" onClick={() => setOpen(false)} aria-label={t("Cerrar servicios")}>×</button>
      </div>
      <div className="booking-service-list">
        {list.length === 0 ? (
          <div style={{ padding: 15, color: "#64748b", fontSize: 14 }}>{t("No hay servicios disponibles.")}</div>
        ) : (
          list.map((s) => {
            const id = getBookingServiceOptionId(s);
            const active = String(id) === String(selectedServiceId);
            const meta = [
              s.duration ? `${s.duration} min` : null,
              formatBookingServicePrice(s),
            ].filter(Boolean).join(" · ");

            return (
              <button
                key={id}
                type="button"
                className="booking-service-option"
                onClick={() => {
                  onSelectService(s);
                  setOpen(false);
                }}
                style={{ background: active ? "#f0fdfa" : "#fff" }}
              >
                {s.image || s.photoUrl ? (
                  <img
                    src={s.image || s.photoUrl}
                    alt={s.name || s.title || t("Servicio")}
                    style={{ width: 44, height: 44, borderRadius: 14, objectFit: "cover", flex: "0 0 auto" }}
                  />
                ) : (
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 14,
                      background: active ? "#ccfbf1" : "#f1f5f9",
                      color: active ? "#0f766e" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 950,
                      flex: "0 0 auto",
                    }}
                  >
                    {(s.name || s.title || "S").slice(0, 1).toUpperCase()}
                  </div>
                )}

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a" }}>
                    {s.name || s.title || t("Servicio")}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, fontWeight: 700 }}>
                    {meta || "Tiempo · Precio"}
                  </div>
                </div>

                {active ? <div style={{ color: "#0f766e", fontWeight: 950 }}>✓</div> : null}
              </button>
            );
          })
        )}
      </div>
    </>
  );

  const mobilePortal =
    open && useMobilePortal && typeof document !== "undefined"
      ? createPortal(
          <div className="booking-service-portal-layer" role="presentation">
            <button
              type="button"
              className="booking-service-portal-backdrop"
              onClick={() => setOpen(false)}
              aria-label={t("Cerrar lista de servicios")}
            />
            <div className="booking-service-menu booking-service-portal-menu" role="listbox" aria-label={t("Servicios disponibles")}>
              {menuContent}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`booking-service-card ${open ? "is-open" : ""}`}>
      {!compact && <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
        <div>
          <h2 className="booking-pro-section-title">{t("Elige tu servicio")}</h2>
          <div className="booking-pro-section-subtitle">{t("Toca el selector para ver los servicios disponibles.")}</div>
        </div>
      </div>}

      <button
        type="button"
        className={`booking-service-select booking-service-select-pro ${selectedServiceId ? "is-selected" : ""} ${open ? "is-open" : ""}`}
        onClick={() => setOpen((s) => !s)}
        disabled={loading}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={selectedServiceId ? "Cambiar servicio seleccionado" : "Abrir lista de servicios disponibles"}
      >
        <div style={{ fontSize: 17, fontWeight: 950, color: "#0f172a", lineHeight: 1.15 }}>
          {loading ? t("Cargando servicios...") : selectedLabel}
        </div>
        <div style={{ marginTop: 5, color: "#64748b", fontSize: 14, fontWeight: 700 }}>
          {selectedMeta || "Tiempo · Precio"}
        </div>
      </button>
      {open && !useMobilePortal ? (
        <>
          <button
            type="button"
            className="booking-service-backdrop"
            onClick={() => setOpen(false)}
            aria-label={t("Cerrar lista de servicios")}
          />
          <div className="booking-service-menu" role="listbox" aria-label={t("Servicios disponibles")}>
            {menuContent}
          </div>
        </>
      ) : null}

      {mobilePortal}
    </div>
  );
}


/* helpers (unchanged) */
const TIME_SLOT_INTERVAL = 30;
const DAY_NAMES = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
function pad(n){ return n < 10 ? `0${n}` : `${n}`; }
function timeToMinutes(t){ const [hh,mm] = (t||"00:00").split(":").map(Number); return (Number.isFinite(hh)?hh:0)*60 + (Number.isFinite(mm)?mm:0); }
function minutesToTime(min){ return `${pad(Math.floor(min/60))}:${pad(min%60)}`; }
function isoDateFromDate(d){ return d.toISOString().slice(0,10); }
function normalizeAvailabilityRanges(ranges){
  if(!ranges) return [];

  // Most common: [{ from:"09:00", to:"18:00" }]
  if(Array.isArray(ranges)) return ranges;

  // Sometimes Firestore stores a single object: { from:"09:00", to:"18:00" }
  if(typeof ranges === "object"){
    if(ranges.closed === true || ranges.enabled === false) return [];

    if(Array.isArray(ranges.ranges)) return ranges.ranges;
    if(Array.isArray(ranges.blocks)) return ranges.blocks;
    if(Array.isArray(ranges.slots)) return ranges.slots;
    if(Array.isArray(ranges.hours)) return ranges.hours;

    if(ranges.from || ranges.start || ranges.fromTime || ranges.startTime){
      return [ranges];
    }
  }

  return [];
}

function generateSlotsFromRanges(ranges, interval = TIME_SLOT_INTERVAL){
  const slots = [];
  normalizeAvailabilityRanges(ranges).forEach(r=>{
    if(!r || (!r.from && !r.start && !r.fromTime && !r.startTime)) return;
    const start = (r.start || r.from || r.fromTime || r.startTime);
    const end = (r.end || r.to || r.toTime || r.endTime);
    if(!start || !end) return;
    let cur = timeToMinutes(start);
    const endM = timeToMinutes(end);
    while(cur + 1 <= endM){
      slots.push(minutesToTime(cur));
      cur += interval;
    }
  });
  return slots;
}
function normalizeAvailabilityObjectForBooking(raw){
  if(!raw || typeof raw !== "object") return null;
  const out = {};
  let hasDayKey = false;
  DAY_NAMES.forEach((day) => {
    if (Object.prototype.hasOwnProperty.call(raw, day)) hasDayKey = true;
    out[day] = normalizeAvailabilityRanges(raw[day]).map((r) => ({
      from: r?.from || r?.start || r?.fromTime || r?.startTime || "",
      to: r?.to || r?.end || r?.toTime || r?.endTime || "",
    })).filter((r) => r.from && r.to && timeToMinutes(r.to) > timeToMinutes(r.from));
  });
  return hasDayKey ? out : null;
}
function availabilityObjectHasAnyRange(avail){
  if(!avail || typeof avail !== "object") return false;
  return DAY_NAMES.some((day) => Array.isArray(avail[day]) && avail[day].length > 0);
}
function getBusinessAvailabilityForBooking(businessDoc){
  if(!businessDoc || typeof businessDoc !== "object") return null;
  return normalizeAvailabilityObjectForBooking(
    businessDoc.availability ||
    businessDoc.openingHours ||
    businessDoc.hours ||
    businessDoc.schedule ||
    businessDoc.businessAvailability ||
    businessDoc.workingHours ||
    businessDoc.weeklyHours ||
    businessDoc.weeklySchedule ||
    businessDoc.availabilityByDay
  );
}
function getWorkerAvailabilityForBooking(emp, businessDoc = null){
  const workerAvailability = normalizeAvailabilityObjectForBooking(
    emp?.availability ||
    emp?.schedule ||
    emp?.workingHours ||
    emp?.hours ||
    emp?.weeklyHours ||
    emp?.weeklySchedule ||
    emp?.availabilityByDay
  );
  if(availabilityObjectHasAnyRange(workerAvailability)) return workerAvailability;

  // Legacy-safe fallback: workers created before this update may not have their own schedule.
  // New workers receive a copied business schedule in WorkersSection, so after backfill the worker schedule remains the source of truth.
  const businessAvailability = getBusinessAvailabilityForBooking(businessDoc);
  if(availabilityObjectHasAnyRange(businessAvailability)) return businessAvailability;

  return null;
}
function businessRangesForDate(businessDoc, date){
  if(!date) return [];
  const availability = getBusinessAvailabilityForBooking(businessDoc);
  if(!availability) return [];
  const weekday = DAY_NAMES[date.getDay()];
  return Array.isArray(availability[weekday]) ? availability[weekday] : [];
}
function workerRangesForDate(emp, date, businessDoc = null){
  if(!emp || !date) return [];
  const availability = getWorkerAvailabilityForBooking(emp, businessDoc);
  if(!availability) return [];
  const weekday = DAY_NAMES[date.getDay()];
  return Array.isArray(availability[weekday]) ? availability[weekday] : [];
}
function employeeBaseSlotsForDate(emp, date, businessDoc = null){
  if(!emp || !date) return [];
  return generateSlotsFromRanges(workerRangesForDate(emp, date, businessDoc), TIME_SLOT_INTERVAL);
}
function normalizeAny(v){
  if(v == null) return "";
  if(typeof v === "object"){
    if(v.id) return String(v.id).toLowerCase();
    try { return JSON.stringify(v).toLowerCase(); } catch { return String(v).toLowerCase(); }
  }
  const s = String(v).trim();
  if(s.includes("/")) { const parts = s.split("/").filter(Boolean); return parts[parts.length-1].toLowerCase(); }
  return s.toLowerCase();
}
function normalizeEmployeeServices(raw){
  const arr = Array.isArray(raw) ? raw : [];
  const ids = [];
  const names = [];
  const namesOriginal = [];
  for(const item of arr){
    if(!item) continue;
    if(typeof item === "string" || typeof item === "number"){
      ids.push(String(item));
      names.push(String(item).toLowerCase());
    } else if(typeof item === "object"){
      const id = item.id || item.serviceId || item.value || (item.path && String(item.path).split("/").filter(Boolean).pop()) || item.slug || item.code || null;
      if(id) ids.push(String(id));
      if(item.name){ names.push(String(item.name).toLowerCase()); namesOriginal.push(String(item.name)); }
      else if(item.title) names.push(String(item.title).toLowerCase());
      if(item._path && !id){ const parts = String(item._path).split("/").filter(Boolean); if(parts.length) ids.push(parts[parts.length-1]); }
    }
  }
  return { _serviceIds: Array.from(new Set(ids)), _serviceNames: Array.from(new Set(names)), _serviceNamesOriginal: namesOriginal };
}
function employeeOffersService(emp, serviceIdToCheck){
  if(!emp || !serviceIdToCheck) return false;
  const svc = normalizeAny(serviceIdToCheck);
  if(emp._serviceIds && emp._serviceIds.some(id => normalizeAny(id) === svc)) return true;
  if(emp._serviceNames && emp._serviceNames.some(n => normalizeAny(n) === svc)) return true;
  const list = Array.isArray(emp.services) ? emp.services : [];
  for(const item of list){
    if(!item) continue;
    if(typeof item === "string" || typeof item === "number"){
      if(normalizeAny(item) === svc) return true;
    } else if(typeof item === "object"){
      if(item.id && normalizeAny(item.id) === svc) return true;
      if(item.serviceId && normalizeAny(item.serviceId) === svc) return true;
      if(item.name && normalizeAny(item.name) === svc) return true;
      if(item.path && normalizeAny(item.path) === svc) return true;
    }
  }
  return false;
}
function isEmployeeUnavailableOnDate(emp, date){
  if(!emp || !date) return false;
  const dateKey = date.toISOString().slice(0,10);
  if(Array.isArray(emp.blockedDates) && emp.blockedDates.includes(dateKey)) return true;
  if(Array.isArray(emp.blockedRanges)){
    for(const r of emp.blockedRanges){
      if(!r) continue;
      const from = String(r.from || "").slice(0,10);
      const to = String(r.to || "").slice(0,10);
      if(!from || !to) continue;
      if(dateKey >= from && dateKey <= to) return true;
    }
  }
  return false;
}
function isBlockingBookingForAvailability(booking){
  const raw = String(booking?.status || "confirmed").trim().toLowerCase();
  // These statuses must NOT block future availability.
  return ![
    "cancelled", "canceled", "cancelada", "cancelado",
    "rejected", "rechazada", "rechazado",
    "declined", "deleted", "void", "no_show", "noshow"
  ].includes(raw);
}

function expandBookingSlots(booking){
  const duration = Number(booking.duration || 30);
  const needed = Math.ceil(duration / TIME_SLOT_INTERVAL);
  const slots = [];
  const startMinutes = timeToMinutes(booking.time);
  for(let i=0;i<needed;i++){
    slots.push(minutesToTime(startMinutes + i*TIME_SLOT_INTERVAL));
  }
  return slots;
}
function bookedSlotsForEmployeeDate(bookingsMap, empId, dateKey){
  const arr = bookingsMap[dateKey] || [];
  const set = new Set();
  arr.filter(b => isBlockingBookingForAvailability(b) && String(b.employeeId) === String(empId)).forEach(b => {
    const exp = expandBookingSlots(b);
    exp.forEach(s => set.add(s));
  });
  return set;
}
function bookedSlotsForNoWorkerDate(bookingsMap, dateKey){
  const arr = bookingsMap[dateKey] || [];
  const set = new Set();
  arr.filter(b => isBlockingBookingForAvailability(b) && (b.employeeId == null || b.employeeId === "")).forEach(b => {
    const exp = expandBookingSlots(b);
    exp.forEach(s => set.add(s));
  });
  return set;
}
function bookedSlotCountsForNoWorkerDate(bookingsMap, dateKey){
  const arr = bookingsMap[dateKey] || [];
  const counts = {};
  arr.filter(b => isBlockingBookingForAvailability(b) && (b.employeeId == null || b.employeeId === "")).forEach(b => {
    expandBookingSlots(b).forEach(s => { counts[s] = (counts[s] || 0) + 1; });
  });
  return counts;
}
function bookedSlotsForNoWorkerServiceDate(bookingsMap, dateKey, serviceId){
  const arr = bookingsMap[dateKey] || [];
  const set = new Set();
  arr.filter(b => isBlockingBookingForAvailability(b) && (b.employeeId == null || b.employeeId === "") && String(b.serviceId||"") === String(serviceId||"")).forEach(b => {
    expandBookingSlots(b).forEach(s => set.add(s));
  });
  return set;
}
function normalizeEventText(value){return String(value||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase().trim();}
function isEventsBusinessDoc(businessDoc){
  const fields=[businessDoc?.businessType,businessDoc?.type,businessDoc?.category,businessDoc?.serviceCategory,businessDoc?.vertical,businessDoc?.eventCategory].map(normalizeEventText).filter(Boolean);
  return fields.some(x=>x.includes("evento")||x.includes("events"));
}

function serviceAllowsConcurrentSameService(service){
  if(!service) return false;
  return service.allowConcurrentSameService === true ||
    service.sameServiceCanOverlap === true ||
    service.repeatable === true ||
    service.concurrentSameService === true;
}
function getBusinessEventBufferBefore(businessDoc){
  const n=Number(businessDoc?.eventBufferBeforeMinutes ?? businessDoc?.bufferBeforeMinutes ?? 0);
  return Number.isFinite(n)&&n>0?n:0;
}
function getBusinessEventBufferAfter(businessDoc){
  const n=Number(businessDoc?.eventBufferAfterMinutes ?? businessDoc?.bufferAfterMinutes ?? 0);
  return Number.isFinite(n)&&n>0?n:0;
}
function getMinimumBookingNoticeHours(businessDoc){
  const n=Number(businessDoc?.minimumBookingNoticeHours ?? businessDoc?.minBookingNoticeHours ?? 0);
  return Number.isFinite(n)&&n>0?n:0;
}
function isBusinessBlockedOnDate(businessDoc, date){
  if(!businessDoc || !date) return false;
  const dateKey = isoDateFromDate(date);
  if(Array.isArray(businessDoc.blockedDates) && businessDoc.blockedDates.includes(dateKey)) return true;
  const ranges = Array.isArray(businessDoc.blockedRanges) ? businessDoc.blockedRanges : [];
  for(const r of ranges){
    const from = String(r?.from || r?.date || "").slice(0,10);
    const to = String(r?.to || r?.from || r?.date || "").slice(0,10);
    if(from && to && dateKey >= from && dateKey <= to) return true;
  }
  return false;
}
function slotDateTimeFromDateAndTime(date, time, business = {}){
  return localInstant(isoDateFromDate(date),time,businessZone(business));
}
function eventBookingRangeMinutes(booking, businessDoc){
  const start = timeToMinutes(booking.time);
  const duration = Number(booking.duration || booking.durationMinutes || 30);
  const before = getBusinessEventBufferBefore(businessDoc);
  const after = getBusinessEventBufferAfter(businessDoc);
  return { start: start - before, end: start + duration + after };
}
function slotRangeMinutes(slot, duration, bufferBefore, bufferAfter){
  const start = timeToMinutes(slot);
  return { start: start - Number(bufferBefore||0), end: start + Number(duration||30) + Number(bufferAfter||0) };
}
function rangesOverlap(a,b){ return a.start < b.end && b.start < a.end; }
function eventBookingsOverlappingSlot(bookingsMap, dateKey, slot, duration, businessDoc){
  const arr = bookingsMap[dateKey] || [];
  const candidate = slotRangeMinutes(slot, duration, getBusinessEventBufferBefore(businessDoc), getBusinessEventBufferAfter(businessDoc));
  return arr.filter(b => {
    if(!isBlockingBookingForAvailability(b)) return false;
    if(b.employeeId != null && b.employeeId !== "") return false;
    const br = eventBookingRangeMinutes(b, businessDoc);
    return rangesOverlap(candidate, br);
  });
}

function getGlobalEventCapacity(businessDoc){
  const n=Number(businessDoc?.eventMaxSimultaneousBookings||businessDoc?.maxSimultaneousEvents||businessDoc?.eventSpotsAtOnce||3);
  return Number.isFinite(n)&&n>0?Math.max(1,Math.floor(n)):3;
}

/* Draft helpers */
const DRAFT_KEY = "booking_draft_v1";
const PHONE_PROFILE_HANDOFF_KEY = "booking_phone_profile_handoff_v1";

function saveDraftToSession(draft) { try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch (e) { } }

function savePhoneProfileHandoff({ uid, phone }) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return false;
    }

    const safeUid = String(uid || "").trim();
    const safePhone = String(phone || "").trim();

    if (!safeUid || !safePhone) {
      return false;
    }

    window.sessionStorage.setItem(
      PHONE_PROFILE_HANDOFF_KEY,
      JSON.stringify({
        uid: safeUid,
        phone: safePhone,
        createdAt: Date.now(),
      })
    );

    return true;
  } catch (_) {
    return false;
  }
}
function loadDraftFromSession() { try { const raw = sessionStorage.getItem(DRAFT_KEY); if (!raw) return null; return JSON.parse(raw); } catch (e) { return null; } }
function clearDraftFromSession() { try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) { } }

/* Timeout helper to avoid infinite "Cargando..." on iOS */
function withTimeout(promise, ms, label = "timeout") {
  let t = null;
  const timeout = new Promise((_, reject) => {
    t = setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([
    Promise.resolve(promise).finally(() => { try { if (t) clearTimeout(t); } catch (_) {} }),
    timeout,
  ]);
}

function getEffectiveUser(user) {
  if (user && (user.uid || user.email)) return user;
  if (isIosCapacitor()) {
    try {
      const ma = getManualAuth();
      if (ma && isManualAuthLikelyValid(ma)) {
        return {
          uid: ma.uid || null,
          email: ma.email || "",
          displayName: ma.displayName || "",
        };
      }
    } catch (_) {}
  }
  return user || null;
}

function employeeVisualState(emp, selectedEmployeeId, selectedDate, selectedTime, selectedService, selectedServiceId, localServices, bookingsMap, businessDoc = null) {
  const employeeVisualServicesSafe = [];
  if (!emp) return { selected: false, available: false, offers: false };
  const selected = Boolean(selectedEmployeeId && String(emp.id) === String(selectedEmployeeId));
  const offers = selectedServiceId ? employeeOffersService(emp, selectedServiceId) : true;

  let available = offers;
  if (available && selectedDate && selectedTime) {
    if (isEmployeeUnavailableOnDate(emp, selectedDate)) {
      available = false;
    } else {
      const slots = employeeBaseSlotsForDate(emp, selectedDate, businessDoc);
      const idx = slots.indexOf(selectedTime);
      if (idx === -1) {
        available = false;
      } else {
        let duration = 30;
        let bufferBefore = 0;
        let bufferAfter = 0;
        if (selectedService) {
          duration = Number(selectedService.duration || 30);
          bufferBefore = Number(selectedService.bufferBeforeMinutes || 0);
          bufferAfter = Number(selectedService.bufferAfterMinutes || 0);
        } else {
          const sdoc = employeeVisualServicesSafe.find(s => String(s.id) === String(selectedServiceId));
          if (sdoc) duration = Number(sdoc.duration || 30);
        }
        const needed = Math.ceil(duration / TIME_SLOT_INTERVAL);
        const pre = Math.ceil(bufferBefore / TIME_SLOT_INTERVAL);
        const post = Math.ceil(bufferAfter / TIME_SLOT_INTERVAL);
        const booked = bookedSlotsForEmployeeDate(bookingsMap, emp.id, isoDateFromDate(selectedDate));
        for (let offset = -pre; offset < needed + post; offset++) {
          const s = slots[idx + offset];
          if (!s || booked.has(s)) {
            available = false;
            break;
          }
        }
      }
    }
  }
  return { selected, available, offers };
}

function WorkerPill({ emp, state, onClick }) {
  const initials = ((emp?.name || emp?.displayName || "U").split(/\s+/).filter(Boolean).map(x => x[0]).slice(0,2).join("") || "U").toUpperCase();
  const photo = emp?.photoUrl || emp?.photo || emp?.image || emp?.avatarUrl || null;
  const selected = !!state?.selected;
  const available = !!state?.available;
  const offers = !!state?.offers;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        flex: "0 0 auto",
        borderRadius: 18,
        border: selected ? "2px solid #2563eb" : "1.5px solid rgba(148,163,184,0.45)",
        background: selected ? "linear-gradient(180deg,#eff6ff 0%,#ffffff 100%)" : "#ffffff",
        boxShadow: selected ? "0 0 0 3px rgba(37,99,235,0.14), 0 10px 22px rgba(15,23,42,0.10)" : "0 6px 16px rgba(15,23,42,0.06)",
        padding: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        transition: "all 140ms ease",
        opacity: available || selected ? 1 : 0.55,
        transform: selected ? "translateY(-1px)" : "none",
        position: "relative",
        overflow: "visible",
      }}
      aria-pressed={selected}
      title={emp?.name || t("Trabajador")}
    >
      {selected ? (
        <span
          style={{
            position: "absolute",
            top: -6,
            right: -6,
            background: "#2563eb",
            color: "#fff",
            borderRadius: 999,
            padding: "2px 7px",
            fontSize: 10,
            fontWeight: 700,
            boxShadow: "0 4px 12px rgba(37,99,235,0.28)",
            zIndex: 2,
          }}
        >{t("Seleccionado")}</span>
      ) : null}

      <div
        style={{
          width: 58,
          height: 58,
          borderRadius: 18,
          overflow: "hidden",
          background: "#f8fafc",
          border: selected ? "3px solid #2563eb" : "2px solid rgba(226,232,240,0.95)",
          boxShadow: selected ? "inset 0 0 0 1px rgba(255,255,255,0.7)" : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {photo ? (
          <img
            src={photo}
            alt={emp?.name || t("Trabajador")}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: selected ? "#dbeafe" : "#e2e8f0",
              color: selected ? "#1d4ed8" : "#475569",
              fontWeight: 800,
              fontSize: 22,
            }}
          >
            {initials}
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", lineHeight: 1.2 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
          {emp?.name || emp?.displayName || t("Trabajador")}
        </div>
        {emp?.role || emp?.title ? (
          <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>
            {emp.role || emp.title}
          </div>
        ) : null}
        {!offers ? (
          <div style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>{t("No ofrece servicio")}</div>
        ) : !available && !selected ? (
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{t("No disponible")}</div>
        ) : null}
      </div>
    </button>
  );
}

function AnyWorkerPill({ selected, available, disabledReason, onClick }) {
  return (
    <button
      type="button"
      onClick={available ? onClick : undefined}
      disabled={!available}
      style={{
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        flex: "0 0 auto",
        borderRadius: 18,
        border: selected ? "2px solid #2563eb" : "1.5px solid rgba(148,163,184,0.45)",
        background: selected ? "linear-gradient(180deg,#eff6ff 0%,#ffffff 100%)" : "#ffffff",
        boxShadow: selected ? "0 0 0 3px rgba(37,99,235,0.14), 0 10px 22px rgba(15,23,42,0.10)" : "0 6px 16px rgba(15,23,42,0.06)",
        padding: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        transition: "all 140ms ease",
        opacity: available || selected ? 1 : 0.5,
        transform: selected ? "translateY(-1px)" : "none",
        position: "relative",
        overflow: "visible",
        cursor: available ? "pointer" : "not-allowed",
      }}
      aria-pressed={selected}
      title={available ? "Asignar automáticamente cualquier trabajador disponible" : disabledReason}
    >
      {selected ? (
        <span
          style={{
            position: "absolute",
            top: -6,
            right: -6,
            background: "#2563eb",
            color: "#fff",
            borderRadius: 999,
            padding: "2px 7px",
            fontSize: 10,
            fontWeight: 700,
            boxShadow: "0 4px 12px rgba(37,99,235,0.28)",
            zIndex: 2,
          }}
        >{t("Seleccionado")}</span>
      ) : null}

      <div
        style={{
          width: 58,
          height: 58,
          borderRadius: 18,
          overflow: "hidden",
          background: selected ? "#dbeafe" : "#f1f5f9",
          border: selected ? "3px solid #2563eb" : "2px solid rgba(226,232,240,0.95)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: 28, color: selected ? "#1d4ed8" : "#64748b" }}>👥</span>
      </div>

      <div style={{ textAlign: "center", lineHeight: 1.2 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>{t("Cualquiera")}</div>
        <div style={{ fontSize: 11, color: available ? "#0f766e" : "#94a3b8", marginTop: 3 }}>
          {available ? "Disponible" : disabledReason || "Elige fecha y hora"}
        </div>
      </div>
    </button>
  );
}


function getEventVisitTypeLabelFromSelection(selectedService, eventOptions) {
  const options = Array.isArray(selectedService?.visitTypeOptions) ? selectedService.visitTypeOptions : [];
  const selectedId = eventOptions?.visitType || options.find((x) => x?.enabled !== false)?.id || "show";
  const found = options.find((x) => String(x.id) === String(selectedId));
  if (found?.label) return found.label;
  if (selectedId === "visit") return "Solo visita";
  if (selectedId === "photos") return "Fotos y saludo";
  return "Show completo";
}

function getEventPriceSummary(selectedService, eventOptions) {
  const service = selectedService || {};
  const baseMinutes = Math.max(1, Number(service.duration || 60));
  const options = Array.isArray(service.visitTypeOptions) && service.visitTypeOptions.length
    ? service.visitTypeOptions
    : [
        { id: "show", label: "Show completo", enabled: true, price: Number(service.price || 0) },
        { id: "visit", label: "Solo visita", enabled: true, price: Number(service.price || 0) ? Math.round(Number(service.price || 0) * 0.65) : 0 },
        { id: "photos", label: "Fotos y saludo", enabled: false, price: Number(service.price || 0) ? Math.round(Number(service.price || 0) * 0.5) : 0 },
      ];

  const enabled = options.filter((x) => x && x.enabled !== false);
  const visitType = eventOptions?.visitType || enabled[0]?.id || "show";
  const selectedOption = enabled.find((x) => String(x.id) === String(visitType)) || enabled[0] || { id: "show", label: "Show completo", price: Number(service.price || 0) };
  const visitHours = Number(eventOptions?.visitHours || Math.max(Number(service.minHours || 0.5), baseMinutes / 60));
  const durationMinutes = Math.max(30, Math.round(visitHours * 60));
  let durationPrice = 0, pricingError = '';
  try { durationPrice = require('./utils/eventPackageSelectionsV1').eventDurationPrice(service, selectedOption, visitHours); }
  catch(error) { pricingError = error.message; }

  const selectedIds = Array.isArray(eventOptions?.selectedCharacterIds) ? eventOptions.selectedCharacterIds : [];
  const characters = Array.isArray(service.characters) ? service.characters : [];
  const selections = require('./utils/eventPackageSelectionsV1').eventPackagePreview(service, eventOptions || {});
  const selectedCharacters = service.charactersMode === 'choose_primary' ? selections.selectedCharacters || [] : characters.filter((c) => selectedIds.includes(c.id || c.name));
  const charactersPrice = selectedCharacters.reduce((sum, c) => sum + Number(c.price || 0), 0);
  pricingError = pricingError || selections.error || '';
  if (!enabled.some(option => String(option.id) === String(visitType))) pricingError = 'Elige una experiencia disponible.';
  else if (!require('./utils/eventPackageSelectionsV1').eventDurationOptions(service, selectedOption).includes(visitHours)) pricingError = 'Elige una duración disponible para esta experiencia.';
  else if (selectedIds.some(id => !characters.some(character => (character.id || character.name) === id))) pricingError = 'Uno de los personajes ya no está disponible. Revisa tu selección.';

  return {
    ...selections,
    pricingError,
    requiresQuote: ['quote','from','cotizacion','cotización'].includes(String(selectedOption.priceType || selectedOption.pricingType || service.priceType || service.pricingType || '').toLowerCase()),
    visitType,
    visitTypeLabel: selectedOption.label || getEventVisitTypeLabelFromSelection(service, eventOptions),
    visitHours,
    durationMinutes,
    durationPrice,
    selectedCharacters,
    charactersPrice,
    totalPrice: Number(durationPrice || 0) + Number(charactersPrice || 0) + selections.extrasPrice,
  };
}


function BookingVisitLocationSection({
  businessDoc,
  visitMode,
  onVisitModeChange,
  customerLocation,
  onCustomerLocationChange,
  customerExactAddress,
  onCustomerExactAddressChange,
  deliveryFee = 0,
  variant = "full",
}) {
  const formatBookingMoney = value => formatMarketMoney(value,businessDoc?.currency || getMarket(businessDoc?.countryCode).currency,businessDoc?.locale || getMarket(businessDoc?.countryCode).locale);
  const [coverageSearch, setCoverageSearch] = useState("");
  const businessMode = getBusinessServiceModeForBooking(businessDoc);
  const canLocal = businessMode === "onsite" || businessMode === "hybrid" || !businessMode;
  const canDelivery = businessMode === "delivery" || businessMode === "hybrid";
  const coverageOptions = useMemo(() => getCoverageOptionsForBooking(businessDoc), [businessDoc]);
  const deliverySelected = isDeliveryVisitMode(visitMode);
  const showModePicker = variant !== "location";
  const showDeliveryFields = deliverySelected && variant !== "mode";

  const visibleCoverageOptions = useMemo(() => {
    const q = String(coverageSearch || "").trim().toLowerCase();
    if (!q) return coverageOptions;
    return coverageOptions.filter((area) => bookingAreaSearchText(area).includes(q));
  }, [coverageOptions, coverageSearch]);

  const useCoverageDropdown = coverageOptions.length > 6;

  const selectCoverage = (area) => {
    onCustomerLocationChange(area || null);
  };

  const selectCoverageById = (id) => {
    const found = coverageOptions.find((area) => String(area.id || area.label || "") === String(id || ""));
    selectCoverage(found || null);
  };

  if (!showModePicker && !showDeliveryFields) return null;

  return (
    <section className={showDeliveryFields ? "booking-location-sticky-shell" : "booking-visit-mode-card"}>
      {showModePicker ? (
        <>
          <h2 className="booking-pro-section-title">{t("Tipo de atención")}</h2>
          <p className="booking-pro-section-subtitle">
            {businessMode === "hybrid"
              ? "Elige primero si visitarás el local o si prefieres atención a domicilio."
              : businessMode === "delivery"
                ? "Este negocio atiende a domicilio. Elige una zona disponible."
                : "Esta reserva será en el local del negocio."}
          </p>
        </>
      ) : null}

      {showModePicker && businessMode === "hybrid" ? (
        <div className="booking-visit-mode-grid">
          {canLocal ? (
            <button
              type="button"
              className={`booking-visit-mode-option ${visitMode === "onsite" ? "is-active" : ""}`}
              onClick={() => {
                onVisitModeChange("onsite");
                onCustomerLocationChange(null);
                onCustomerExactAddressChange("");
              }}
            >
              <span className="booking-visit-mode-icon">🏢</span>
              <span className="booking-visit-mode-copy">
                <strong>{t("En el local")}</strong>
                <span>{t("Vas al negocio para recibir el servicio.")}</span>
              </span>
            </button>
          ) : null}

          {canDelivery ? (
            <button
              type="button"
              className={`booking-visit-mode-option ${visitMode === "delivery" ? "is-active" : ""}`}
              onClick={() => onVisitModeChange("delivery")}
            >
              <span className="booking-visit-mode-icon">🚗</span>
              <span className="booking-visit-mode-copy">
                <strong>{t("A domicilio")}</strong>
                <span>{t("El negocio va a la zona que selecciones.")}</span>
              </span>
            </button>
          ) : null}
        </div>
      ) : null}

      {showDeliveryFields ? (
        <div className="booking-delivery-fields booking-delivery-fields-pro">
          <div className="booking-location-pro-card booking-delivery-sticky-card">
            <h3 className="booking-location-pro-title">{t("¿Dónde será el servicio?")}</h3>
            <p className="booking-zone-note">{t("Solo mostramos las zonas que este negocio configuró como disponibles.")}</p>

            {useCoverageDropdown ? (
              <div className="booking-zone-dropdown-shell">
                <label className="booking-zone-dropdown-label">{t("Zona de atención")}</label>
                <select
                  className={`booking-zone-dropdown ${customerLocation ? "is-selected" : ""}`}
                  value={customerLocation ? String(customerLocation.id || customerLocation.label || "") : ""}
                  onChange={(e) => selectCoverageById(e.target.value)}
                >
                  <option value="">{t("Selecciona tu zona")}</option>
                  {coverageOptions.map((area) => {
                    const fee = bookingMoneyNumber(area.extraFee ?? area.fee ?? area.deliveryFee ?? 0, 0);
                    return (
                      <option key={area.id || area.label} value={String(area.id || area.label || "")}>
                        {area.label} · {bookingCoverageLevelLabel(area)} · {fee > 0 ? `+${formatBookingMoney(fee)}` : "sin costo adicional"}
                      </option>
                    );
                  })}
                </select>
                <span className="booking-zone-dropdown-helper">{t("El negocio tiene muchas zonas configuradas, por eso las mostramos en una lista compacta.")}</span>
              </div>
            ) : (
              <>
                {coverageOptions.length > 5 ? (
                  <input
                    className="booking-zone-search"
                    value={coverageSearch}
                    onChange={(e) => setCoverageSearch(e.target.value)}
                    placeholder={t("Buscar zona disponible...")}
                  />
                ) : null}

                <div className="booking-zone-grid">
                  {visibleCoverageOptions.length ? visibleCoverageOptions.map((area) => {
                    const active = String(customerLocation?.id || "") === String(area.id || "");
                    const fee = bookingMoneyNumber(area.extraFee ?? area.fee ?? area.deliveryFee ?? 0, 0);
                    return (
                      <button
                        key={area.id || area.label}
                        type="button"
                        className={`booking-zone-option ${active ? "is-active" : ""}`}
                        onClick={() => selectCoverage(area)}
                      >
                        <span className="booking-zone-top">
                          <span className="booking-zone-title">{area.label}</span>
                          <span className="booking-zone-badge">{bookingCoverageLevelLabel(area)}</span>
                        </span>
                        <span className="booking-zone-fee">{fee > 0 ? `+${formatBookingMoney(fee)} adicional` : "Sin costo adicional"}</span>
                        <span className="booking-zone-note">{t("Disponible para atención a domicilio.")}</span>
                      </button>
                    );
                  }) : (
                    <div className="booking-zone-option">
                      <span className="booking-zone-title">{t("No encontramos zonas")}</span>
                      <span className="booking-zone-note">{t("Prueba con otra búsqueda o elige atención en local si está disponible.")}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="booking-address-box">
              <label>{t("Dirección y referencia")}</label>
              <textarea
                value={customerExactAddress || ""}
                onChange={(e) => onCustomerExactAddressChange(e.target.value)}
                rows={3}
                placeholder={t("Ej: PH Rivage, torre 2, apto 18B. Tocar intercom 1802. Referencia de entrada...")}
                maxLength={260}
              />
              <div className="booking-address-helper">
                <span>{t("Obligatorio para domicilio: edificio, calle, apartamento o referencia.")}</span>
                <span>{String(customerExactAddress || "").length}/260</span>
              </div>
            </div>

            {customerLocation ? (
              <div className="booking-delivery-fee-box">
                <div>
                  <strong>{customerLocation.label}</strong>
                  <span>
                    {Number(deliveryFee || 0) > 0
                      ? `Costo adicional por zona: ${formatBookingMoney(deliveryFee)}`
                      : "Esta zona no tiene costo adicional."}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function CompactReservationConfirm({
  businessDoc = {},
  selectedService,
  selectedDate,
  selectedTime,
  selectedEmployeeId,
  employees = [],
  isSaving,
  onConfirm,
  onClear,
  isEventsBusiness = false,
  eventOptions = null,
  noWorkerMode = false,
  locationRequired = false,
  customerLocation = null,
  matchedServiceArea = null,
  deliveryFee = 0,
  isOutsideCoverage = false,
  onChooseLocation = () => {},
  finalBookingTotal = 0,
  visitMode = "onsite",
  customerExactAddress = "",
  paymentPreview = null,
  paymentStatus = "",
  paymentInlineActive = false,
  configurationReady = true,
  onEditSelection,
  canChangeService = true,
}) {
  const formatBookingMoney = value => formatMarketMoney(value,businessDoc?.currency || getMarket(businessDoc?.countryCode).currency,businessDoc?.locale || getMarket(businessDoc?.countryCode).locale);
  const selectedEmployee = employees.find((e) => String(e.id) === String(selectedEmployeeId));
  const dateText = selectedDate
    ? selectedDate.toLocaleDateString(interfaceLocale(), { weekday: "short", day: "numeric", month: "short" })
    : t("Por elegir");

  const eventSummary = isEventsBusiness && selectedService ? getEventPriceSummary(selectedService, eventOptions || {}) : null;
  const selectedCharacterNames = eventSummary?.selectedCharacters?.map((c) => c.id === eventOptions?.primaryCharacterId ? `${c.name} (principal)` : c.name).filter(Boolean) || [];
  const priceIsEstimate = !isEventsBusiness && bookingServicePriceIsEstimate(selectedService);
  const displayedServiceAmount = Math.max(0, Number(finalBookingTotal || 0) - Number(deliveryFee || 0));

  return (
    <div className="booking-confirm-summary" aria-label={t("Resumen de tu reserva")}>
      <div className="text-base font-semibold text-slate-800 mb-3">{t("Confirma tu reserva")}</div>

      {onEditSelection && <nav className="booking-summary-edit" aria-label={t("Cambiar datos de la reserva")}>
        {canChangeService && <button type="button" onClick={()=>onEditSelection('service')}>{t("Cambiar servicio")}</button>}
        {isEventsBusiness && <button type="button" onClick={()=>onEditSelection('experience')}>{t("Cambiar personajes y extras")}</button>}
        {!isEventsBusiness && !noWorkerMode && <button type="button" onClick={()=>onEditSelection('worker')}>{t("Cambiar profesional")}</button>}
        <button type="button" onClick={()=>onEditSelection('date')}>{t("Cambiar fecha y hora")}</button>
        {locationRequired && <button type="button" onClick={()=>onEditSelection('location')}>{t("Cambiar dirección")}</button>}
      </nav>}
      <div className="space-y-2 text-sm text-slate-600 mb-4">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span>{t("Servicio")}</span>
          <strong style={{ color: "#0f172a", textAlign: "right" }}>
            {selectedService?.name || selectedService?.title || t("Elige un servicio")}
          </strong>
        </div>

        {eventSummary ? (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>{t("Tipo")}</span>
              <strong style={{ color: "#0f172a", textAlign: "right" }}>{eventSummary.visitTypeLabel}</strong>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>{t("Duración")}</span>
              <strong style={{ color: "#0f172a", textAlign: "right" }}>
                {eventSummary.visitHours} {eventSummary.visitHours === 1 ? "hora" : "horas"}
              </strong>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>{t("Personajes")}</span>
              <strong style={{ color: "#0f172a", textAlign: "right" }}>
                {[...eventSummary.includedCharacters, ...selectedCharacterNames].join(", ") || t("Ver inclusiones del paquete")}
              </strong>
            </div>
          </>
        ) : null}

        {eventSummary?.selectedExtras.length > 0 && <div><span>{t("Extras:") + " "}</span><strong>{eventSummary.selectedExtras.map(extra => `${extra.name} (${formatBookingMoney(extra.price)})`).join(", ")}</strong></div>}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span>{t("Fecha")}</span>
          <strong style={{ color: "#0f172a", textAlign: "right" }}>{dateText}</strong>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span>{t("Hora")}</span>
          <strong style={{ color: "#0f172a", textAlign: "right" }}>{selectedTime || t("Por elegir")}</strong>
        </div>

        {!isEventsBusiness && !noWorkerMode ? (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span>{t("Trabajador")}</span>
            <strong style={{ color: "#0f172a", textAlign: "right" }}>
              {selectedEmployee ? (selectedEmployee.name || selectedEmployee.displayName || t("Trabajador")) : t("Cualquiera disponible")}
            </strong>
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span>{t("Atención")}</span>
          <strong style={{ color: "#0f172a", textAlign: "right" }}>
            {isDeliveryVisitMode(visitMode) ? t("A domicilio") : t("En el local")}
          </strong>
        </div>

        {locationRequired ? (
          <div className={`booking-summary-location booking-summary-location-legacy ${isOutsideCoverage ? "is-error" : customerLocation ? "is-ok" : ""}`}>
            <div>
              <span>{t("Zona de atención")}</span>
              <strong>{customerLocation?.label || t("Selecciona zona arriba")}</strong>
              <small>
                {isOutsideCoverage
                  ? t("Este negocio no cubre esta zona.")
                  : matchedServiceArea
                    ? `${t("Zona cubierta")}${Number(deliveryFee || 0) > 0 ? ` · +${formatBookingMoney(deliveryFee)}` : t(" · sin costo adicional")}`
                    : t("Necesaria para confirmar la reserva.")}
              </small>
            </div>
          </div>
        ) : null}

        {locationRequired && customerExactAddress ? (
          <div style={{ display: "grid", gap: 3 }}>
            <span>{t("Dirección")}</span>
            <strong style={{ color: "#0f172a", textAlign: "left", lineHeight: 1.35 }}>
              {customerExactAddress}
            </strong>
          </div>
        ) : null}

        {!eventSummary?.requiresQuote && Number(finalBookingTotal || 0) > 0 ? (
          <div style={{ marginTop: 12, borderTop: "1px solid #e2e8f0", paddingTop: 12, display: "grid", gap: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>{priceIsEstimate ? t("Precio base") : isEventsBusiness ? "Paquete" : t("Precio")}</span>
              <strong style={{ color: "#0f172a" }}>
                {priceIsEstimate ? `Desde ${formatBookingMoney(displayedServiceAmount)}` : formatBookingMoney(displayedServiceAmount)}
              </strong>
            </div>
            {Number(deliveryFee || 0) > 0 ? (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <span>{t("Costo por zona")}</span>
                <strong style={{ color: "#0f172a" }}>{formatBookingMoney(deliveryFee)}</strong>
              </div>
            ) : null}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 15 }}>
              <span style={{ fontWeight: 900, color: "#0f172a" }}>{priceIsEstimate ? "Precio estimado" : "Total"}</span>
              <strong style={{ color: "#00675f" }}>
                {priceIsEstimate ? `Desde ${formatBookingMoney(finalBookingTotal)}` : formatBookingMoney(finalBookingTotal)}
              </strong>
            </div>
            {priceIsEstimate ? (
              <div style={{ borderRadius: 14, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", padding: "10px 11px", fontSize: 11.5, fontWeight: 750, lineHeight: 1.4 }}>{t("El precio final puede variar según el servicio realizado. El negocio confirmará el monto final.")}</div>
            ) : null}
          </div>
        ) : null}

        {paymentPreview?.requiresPayment ? (
          <div
            style={{
              marginTop: 12,
              borderRadius: 16,
              border: paymentPreview.supported ? "1px solid #99f6e4" : "1px solid #fde68a",
              background: paymentPreview.supported ? "#f0fdfa" : "#fffbeb",
              padding: "12px 13px",
              display: "grid",
              gap: 8,
            }}
          >
            {paymentPreview.supported ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span style={{ color: "#0f766e", fontWeight: 850 }}>{paymentPreview.approvalBeforePayment ? 'Abono después de la aprobación' : 'Para reservar hoy'}</span>
                  <strong style={{ color: "#0f766e", fontSize: 16 }}>
                    {formatBookingMoney(Number(paymentPreview.dueNowCents || 0) / 100)}
                  </strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span style={{ color: "#475569", fontWeight: 750 }}>{t("Saldo restante")}</span>
                  <strong style={{ color: "#0f172a" }}>
                    {formatBookingMoney(Number(paymentPreview.balanceCents || 0) / 100)}
                  </strong>
                </div>
                <div style={{ color: "#0f766e", fontSize: 11.5, fontWeight: 750, lineHeight: 1.4 }}>
                  {paymentPreview.approvalBeforePayment ? 'Envía tu solicitud sin pagar. Cuando el negocio la apruebe, podrás completar el abono desde Mis reservas.' : `El horario se reservará por ${paymentPreview.holdMinutes || 35} minutos mientras completas el pago.`}
                </div>
              </>
            ) : (
              <div style={{ color: "#92400e", fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                {publicBookingPaymentUnsupportedMessage(paymentPreview.reason)}
              </div>
            )}
            {paymentStatus ? (
              <div style={{ color: paymentPreview.supported ? "#0f766e" : "#92400e", fontSize: 11.5, fontWeight: 800, lineHeight: 1.4 }}>
                {paymentStatus}
              </div>
            ) : null}
          </div>
        ) : null}

        {eventSummary?.requiresQuote && <p>{t("Precio final por cotizar con el negocio.")}</p>}
        {eventSummary?.pricingError && <p role="alert">{eventSummary.pricingError}</p>}
      </div>


      {paymentInlineActive ? (
        <div
          style={{
            marginTop: 12,
            borderRadius: 13,
            border: "1px solid #dbe7e5",
            background: "#f8fafc",
            padding: "10px 11px",
            color: "#475569",
            fontSize: 11.5,
            fontWeight: 800,
            lineHeight: 1.4,
          }}
        >{t("Tu horario ya está reservado temporalmente. Completa el pago seguro abajo.")}</div>
      ) : (
        <>
          <button
            type="button"
            onClick={onConfirm}
            disabled={
              isSaving ||
              !configurationReady ||
              !selectedService ||
              !selectedDate ||
              !selectedTime ||
              (locationRequired && (!customerLocation || isOutsideCoverage)) ||
              paymentPreview?.supported === false
            }
            className="booking-inline-confirm-button w-full px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-60"
          >
            {!selectedService
              ? "Elige servicio"
              : !selectedDate
                ? "Elige fecha"
                : !selectedTime
                  ? "Elige hora"
                  : locationRequired && !customerLocation
                    ? "Elige ubicación"
                    : isOutsideCoverage
                      ? "Zona no cubierta"
                      : requiredPaymentButtonLabel(paymentPreview || {}, isSaving)}
          </button>

          <button
            type="button"
            onClick={onClear}
            className="booking-inline-clear-button w-full mt-3 text-sm text-slate-500 hover:text-slate-700"
          >{t("Limpiar selección")}</button>
        </>
      )}
    </div>
  );
}

function bookingDateKeyFromRecord(b) {
  try {
    if (b && b.date) return String(b.date);
    const sa = b && (b.scheduledAt || b.scheduled_at || b.scheduled);
    if (sa && typeof sa.toDate === "function") {
      return sa.toDate().toISOString().slice(0, 10);
    }
    if (typeof sa === "string" || typeof sa === "number") {
      const d = new Date(sa);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
  } catch (_) {}
  return null;
}




function BookingSuccessScreen({ summary, onViewBookings, onClose, businessDoc = {} }) {
  const formatBookingMoney = value => formatMarketMoney(value,businessDoc?.currency || getMarket(businessDoc?.countryCode).currency,businessDoc?.locale || getMarket(businessDoc?.countryCode).locale);
  const serviceName = summary?.serviceName || "Servicio reservado";
  const dateText = summary?.dateText || "";
  const timeText = summary?.timeText || "";
  const workerText = summary?.workerText || "El negocio asignará el trabajador";
  const locationText = summary?.locationText || "";
  const totalPrice = summary?.totalPrice || null;
  const isAutoConfirmed = summary?.confirmedAutomatically === true || String(summary?.status || "").toLowerCase() === "confirmed";
  const membershipBenefitLabel = summary?.membershipBenefitLabel || "";
  const paymentRequired = summary?.paymentRequired === true;
  const paymentPaidNow = Number(summary?.paymentPaidNow || 0);
  const paymentBalance = Number(summary?.paymentBalance || 0);
  const isGuestBooking = summary?.isGuestBooking === true;
  const guestReferenceCode = summary?.guestReferenceCode || "";

  return (
    <>
      <style>{bookingAirbnbStyles}</style>
      <main className="booking-success-wrap" aria-live="polite">
        <section className="booking-success-card">
          <div className="booking-success-check" aria-hidden="true">
            <svg width="74" height="74" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h1 className="booking-success-title">{isAutoConfirmed ? "Reserva confirmada" : "Solicitud enviada"}</h1>
          <p className="booking-success-subtitle">
            {isGuestBooking
              ? (isAutoConfirmed
                ? "El negocio confirmó la reserva. Guarda tu referencia; también usaremos tu WhatsApp para mantenerte al tanto."
                : "El negocio revisará tu solicitud. Guarda tu referencia; te avisaremos por WhatsApp cuando cambie su estado.")
              : (isAutoConfirmed
                ? "El negocio confirma automáticamente sus reservas online. Ya puedes verla en Mis Reservas."
                : "El negocio revisará tu solicitud. Te aparecerá en Mis Reservas mientras queda pendiente de confirmación.")}
          </p>

          <div className="booking-success-summary">
            <div className="booking-success-row">
              <span>{t("Servicio")}</span>
              <strong>{serviceName}</strong>
            </div>

            {dateText || timeText ? (
              <div className="booking-success-row">
                <span>{t("Fecha y hora")}</span>
                <strong>{[dateText, timeText].filter(Boolean).join(" · ")}</strong>
              </div>
            ) : null}

            <div className="booking-success-row">
              <span>{t("Trabajador")}</span>
              <strong>{workerText}</strong>
            </div>

            {paymentRequired ? (
              <>
                <div className="booking-success-row">
                  <span>{t("Pagado al reservar")}</span>
                  <strong>{formatBookingMoney(paymentPaidNow)}</strong>
                </div>
                <div className="booking-success-row">
                  <span>{t("Saldo en el negocio")}</span>
                  <strong>{formatBookingMoney(paymentBalance)}</strong>
                </div>
              </>
            ) : null}

            {isGuestBooking && guestReferenceCode ? (
              <div className="booking-success-row">
                <span>{t("Referencia")}</span>
                <strong>{guestReferenceCode}</strong>
              </div>
            ) : null}
          </div>

          {membershipBenefitLabel ? (
            <div className="booking-success-membership">
              <span>{t("Membresía")}</span>
              <strong>{t("Reserva enviada usando tu membresía")}</strong>
              <p>{membershipBenefitLabel}{t(". El crédito se descontará cuando el negocio complete/pague el servicio.")}</p>
            </div>
          ) : null}

          <div className="booking-success-actions">
            <button type="button" className="booking-success-primary" onClick={onViewBookings}>
              {isGuestBooking ? "Copiar referencia" : "Ver mis reservas"}
            </button>
            <button type="button" className="booking-success-secondary" onClick={onClose}>{t("Cerrar")}</button>
          </div>
        </section>
      </main>
    </>
  );
}


/* BookingInterface */
function EventBookingOptions({ selectedService, value, onChange }) {
 const formatBookingMoney=value=>formatMarketMoney(value,selectedService?.currency || 'USD',selectedService?.locale || 'es-PA');
  const service = selectedService || {};
  const current = value || {};
  const baseMinutes = Math.max(1, Number(service.duration || 60));
  const minHours = Math.max(0.5, Number(service.minHours || 0.5));
  const maxHours = Math.max(minHours, Number(service.maxHours || 2));

  const visitTypeOptions = (Array.isArray(service.visitTypeOptions) && service.visitTypeOptions.length
    ? service.visitTypeOptions
    : [
        { id: "show", label: "Show completo", enabled: true, price: Number(service.price || 0) },
        { id: "visit", label: "Solo visita", enabled: true, price: Number(service.price || 0) ? Math.round(Number(service.price || 0) * 0.65) : 0 },
        { id: "photos", label: "Fotos y saludo", enabled: false, price: Number(service.price || 0) ? Math.round(Number(service.price || 0) * 0.5) : 0 },
      ]
  ).filter((x) => x && x.enabled !== false);

  const defaultVisitType = visitTypeOptions[0]?.id || "show";
  const visitType = visitTypeOptions.some((x) => x.id === current.visitType) ? current.visitType : defaultVisitType;
  const selectedVisitTypeOption = visitTypeOptions.find((x) => x.id === visitType) || visitTypeOptions[0] || { price: Number(service.price || 0), label: "Show completo", id: "show" };
  const hourOptions = require('./utils/eventPackageSelectionsV1').eventDurationOptions(service, selectedVisitTypeOption);
  const basePriceForType = Number(selectedVisitTypeOption.price || 0);

  const characters = Array.isArray(service.characters) ? service.characters : [];
  const selectedCharacterIds = Array.isArray(current.selectedCharacterIds) ? current.selectedCharacterIds : [];
  const visitHours = Number(current.visitHours || Math.max(minHours, baseMinutes / 60));

  const update = (patch) => onChange({ ...current, ...patch });

  const durationMinutes = Math.max(30, Math.round(visitHours * 60));
  let durationPrice = 0, pricingError = '';
  try { durationPrice = require('./utils/eventPackageSelectionsV1').eventDurationPrice(service, selectedVisitTypeOption, visitHours); }
  catch(error) { pricingError = error.message; }
  const choosePrimary = service.charactersMode === 'choose_primary';
  const packageSelections = require("./utils/eventPackageSelectionsV1").eventPackagePreview(service, current);
  const selectedCharacters = choosePrimary ? packageSelections.selectedCharacters || [] : characters.filter((c) => selectedCharacterIds.includes(c.id || c.name));
  const charactersPrice = selectedCharacters.reduce((sum, c) => sum + Number(c.price || 0), 0);
  pricingError = pricingError || packageSelections.error || '';
  const totalPrice = durationPrice + charactersPrice + packageSelections.extrasPrice;

  const toggleCharacter = (character) => {
    const id = character.id || character.name;
    const exists = selectedCharacterIds.includes(id);
    const next = exists ? selectedCharacterIds.filter(x => x !== id) : [...selectedCharacterIds, id];
    update({ selectedCharacterIds: next });
  };

  return (
    <div id="booking-experience-section" className="booking-pro-card booking-event-options">
      <h2 className="booking-pro-section-title">{t("Prepara tu evento")}</h2>
      <div className="booking-pro-section-subtitle">{t("Revisa lo que incluye el paquete y agrega los complementos que quieras.")}</div>

      {packageSelections.includedCharacters.length > 0 && <p><strong>{t("Incluido:")}</strong> {packageSelections.includedCharacters.join(", ")}</p>}
      <div className="booking-event-controls">
        <div className="booking-event-control">
          <label>{t("Experiencia base")}</label>
          <div className="event-experience-choices">{visitTypeOptions.map(option=><button type="button" key={option.id} aria-pressed={visitType===option.id} onClick={()=>{const hours=require('./utils/eventPackageSelectionsV1').eventDurationOptions(service,option);update({visitType:option.id,visitHours:hours.includes(visitHours)?visitHours:hours[0]});}}><strong>{option.label}</strong><span>{['quote','from','cotizacion','cotización'].includes(String(option.priceType || option.pricingType || service.priceType || '').toLowerCase()) ? t("Solicitar cotización") : '$'+Number(option.price || 0)}</span></button>)}</div>
        </div>
        <div className="booking-event-control">
          <label>{t("Duración")}</label>
          {hourOptions.length===1 ? <p className="event-fixed-duration">{hourOptions[0]} {hourOptions[0]===1?'hora':'horas'}</p> : <>          <select value={visitHours} onChange={(e)=>update({visitHours:Number(e.target.value || minHours)})}>
            {!hourOptions.includes(visitHours) && <option value={visitHours} disabled>{t("Elige una duración")}</option>}
            {hourOptions.map(h => <option key={h} value={h}>{h} {h === 1 ? "hora" : "horas"}</option>)}
          </select></>}
        </div>
      </div>

      {choosePrimary && <section className="booking-character-section" aria-label={t("Personaje principal")}>
        <div className="booking-character-section-head"><strong>{t("Elige tu personaje principal")}</strong><span>{t("Uno incluido en el precio, con su animador.")}</span></div>
        <div className="booking-character-grid">{characters.filter(c=>c.enabled!==false).map(c=>{
          const id=c.id||c.name,selected=current.primaryCharacterId===id;
          return <button type="button" key={id} aria-pressed={selected} className={`booking-character-choice ${selected?'is-selected':''}`} onClick={()=>update({primaryCharacterId:id,selectedCharacterIds:[id,...selectedCharacterIds.filter(item=>item!==id&&item!==current.primaryCharacterId)]})}>{c.name}<small><span aria-hidden="true">{selected ? "✓ " : ""}</span>{t("Incluido")}</small></button>;
        })}</div>
      </section>}
      {characters.length && !['included','none'].includes(service.charactersMode) && (!choosePrimary || current.primaryCharacterId) ? (
        <details className="booking-character-section event-optional-characters">
          <summary>{t("¿Quieres agregar más personajes?") + " "}<small>{selectedCharacterIds.filter(id=>id!==current.primaryCharacterId).length ? selectedCharacterIds.filter(id=>id!==current.primaryCharacterId).length+" seleccionados" : t("Opcional")}</small></summary>
        <div className="booking-character-grid">
          {characters.filter(c=>c.enabled!==false && (!choosePrimary || (c.id||c.name)!==current.primaryCharacterId)).map((c,idx)=>{
            const id = c.id || c.name || `char-${idx}`;
            const selected = selectedCharacterIds.includes(id);
            return (
              <button key={id} type="button" aria-pressed={selected} className={`booking-character-choice ${selected ? "is-selected" : ""}`} onClick={()=>toggleCharacter(c)}>
                {c.name || "Personaje"}
                <small>{Number(c.price || 0) > 0 ? `+ ${formatBookingMoney(c.price || 0)}` : "Sin costo adicional"}</small>
              </button>
            );
          })}
        </div>
        </details>
      ) : null}

      {(service.extras || service.optionalExtras || []).some(extra => extra.enabled !== false) && <section className="booking-character-section" aria-label={t("Extras opcionales")}>
        <strong>{t("Extras opcionales")}</strong>
        <div className="booking-character-grid">{(service.extras || service.optionalExtras || []).filter(extra => extra.enabled !== false).map(extra => {
          const selected = packageSelections.selectedExtraIds.includes(String(extra.id));
          return <button type="button" key={extra.id} aria-pressed={selected} className={`booking-character-choice ${selected ? "is-selected" : ""}`} onClick={() => update({selectedExtraIds: selected ? packageSelections.selectedExtraIds.filter(id => id !== String(extra.id)) : [...packageSelections.selectedExtraIds, String(extra.id)]})}>{extra.name}<small>+ {formatBookingMoney(extra.price || 0)}</small></button>;
        })}</div>
      </section>}
      <div className="booking-event-summary-pill">
        {pricingError ? <span role="alert">{pricingError}</span> : ['quote','from','cotizacion','cotización'].includes(String(selectedVisitTypeOption.priceType || selectedVisitTypeOption.pricingType || service.priceType || '').toLowerCase()) ? <span>{t("El negocio te enviará una cotización con estas opciones. Revisa y acepta el precio antes de pagar.")}</span> : <>{t("Experiencia base:")}{formatBookingMoney(durationPrice)}{charactersPrice ? ` + personajes ${formatBookingMoney(charactersPrice)}` : ""}{packageSelections.extrasPrice > 0 ? ` + extras ${formatBookingMoney(packageSelections.extrasPrice)}` : ""} · Total {formatBookingMoney(totalPrice)}</>}
      </div>
    </div>
  );
}

export default function BookingInterface(props) {
  useLanguage();
  const [eventBookingStep,setEventBookingStep]=useState(0);
  const [eventTimePeriod,setEventTimePeriod]=useState('all');
  const eventJourneyRef=useRef(null);
  const changeEventStep=step=>{setEventBookingStep(step);requestAnimationFrame(()=>{eventJourneyRef.current?.scrollIntoView({block:'start',behavior:'smooth'});eventJourneyRef.current?.focus({preventScroll:true});});};

  const {
    db: dbProp = defaultDb,
    user = null,
    businessId: propBusinessId,
    businessName: propBusinessName = "",
    services: servicesProp = [],
    servicesLoading: servicesLoadingProp = false,
    initialServiceId = null,
    initialService = null,
    initialWorkerId = null,
    initialDate = null,
    initialTime = null,
    onBookingCompleted = null,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const db = dbProp;

  // Resolve business
  const qs = new URLSearchParams(location.search || "");
  let resolvedBusinessId = propBusinessId && String(propBusinessId).trim() !== "" ? propBusinessId : null;
  let resolvedBusinessName = propBusinessName && String(propBusinessName).trim() !== "" ? propBusinessName : null;
  const qBusinessId = qs.get("businessId");
  const qBusinessName = qs.get("businessName");
  if(!resolvedBusinessId && qBusinessId) resolvedBusinessId = qBusinessId;
  if((!resolvedBusinessName || resolvedBusinessName==="") && qBusinessName){ try{ resolvedBusinessName = decodeURIComponent(qBusinessName); }catch{ resolvedBusinessName = qBusinessName; } }
  if(!resolvedBusinessId && location && location.state && location.state.businessId) resolvedBusinessId = location.state.businessId;
  if((!resolvedBusinessName || resolvedBusinessName==="") && location && location.state && location.state.businessName) resolvedBusinessName = location.state.businessName;
  if(!resolvedBusinessId){ try{ const s = sessionStorage.getItem("booking_businessId"); if(s) resolvedBusinessId = s; }catch(e){} }
  if(!resolvedBusinessName){ try{ const s = sessionStorage.getItem("booking_businessName"); if(s) resolvedBusinessName = s; }catch(e){} }
  useEffect(()=>{ try{ if(resolvedBusinessId) sessionStorage.setItem("booking_businessId", String(resolvedBusinessId)); else sessionStorage.removeItem("booking_businessId"); if(resolvedBusinessName) sessionStorage.setItem("booking_businessName", String(resolvedBusinessName)); else sessionStorage.removeItem("booking_businessName"); }catch(e){} }, [resolvedBusinessId, resolvedBusinessName]);

  const businessId = resolvedBusinessId;
  const businessDisplayName = resolvedBusinessName;

  /* State */
  const [businessDoc, setBusinessDoc] = useState(null);
  const formatBookingMoney = value => formatMarketMoney(value,businessDoc?.currency || getMarket(businessDoc?.countryCode).currency,businessDoc?.locale || getMarket(businessDoc?.countryCode).locale);
  const [acceptedRefundPolicyKey, setAcceptedRefundPolicyKey] = useState("");
  // Refund policies are paused; retain historical data without offering new terms.
  const refundPolicy = null;
  const refundPolicyKey = refundPolicy?.version === 1 && typeof refundPolicy.enabled === "boolean" && Number.isInteger(refundPolicy.noticeHours)
    ? JSON.stringify({version: 1, enabled: refundPolicy.enabled, noticeHours: refundPolicy.noticeHours}) : "";
  const [loadingBusinessDoc, setLoadingBusinessDoc] = useState(true);
  const [localServices, setLocalServices] = useState(servicesProp || []);
  const [loadingServices, setLoadingServices] = useState(!!servicesLoadingProp || false);
  const [employees, setEmployees] = useState([]);
  const [, setLoadingEmployees] = useState(false);
  const [bookingsMap, setBookingsMap] = useState({});
  const [availabilityStatus, setAvailabilityStatus] = useState('loading');
  const availabilityRefreshRef = useRef(null);
  const availabilityCheckingRef = useRef(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(initialWorkerId || "");
  const [workerChoiceMode, setWorkerChoiceMode] = useState(initialWorkerId ? "specific" : "any");
  const [workerPickerOpen, setWorkerPickerOpen] = useState(false);
  const [workerSearch, setWorkerSearch] = useState("");
  const [selectionNotice,setSelectionNotice]=useState("");
  useEffect(() => {
    if (!workerPickerOpen) return;
    const previousFocus = document.activeElement;
    return () => { if (previousFocus?.isConnected) previousFocus.focus(); };
  }, [workerPickerOpen]);

  const chooseAnyWorker = () => {
    setWorkerChoiceMode("any");
    setSelectedEmployeeId("");
  };

  const chooseSpecificWorker = (employeeId) => {
    setWorkerChoiceMode("specific");
    setSelectedEmployeeId(employeeId || "");
  };

  const [selectedServiceId, setSelectedServiceId] = useState(initialServiceId || getBookingServiceOptionId(initialService) || null);
  const [selectedService, setSelectedService] = useState(initialService || null);
  const bookingVertical = useMemo(
    () => getBookingBusinessVerticalSafe(businessDoc || {}, selectedService),
    [businessDoc, selectedService]
  );
  const today = wallDate(new Date(),businessDoc || {});
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(initialDate ? new Date(initialDate) : null);
  const [selectedTime, setSelectedTime] = useState(initialTime || null);
  const [eventOptions, setEventOptions] = useState({});
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerPhoneVerified, setCustomerPhoneVerified] = useState(false);
  const [visitMode, setVisitMode] = useState(() => getInitialVisitModeForBusiness(null));
  const [customerLocation, setCustomerLocation] = useState(null);
  const [customerExactAddress, setCustomerExactAddress] = useState("");
  const [coverageSearch, setCoverageSearch] = useState("");
  const [showCustomerLocationPicker, setShowCustomerLocationPicker] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [bookingSuccessSummary, setBookingSuccessSummary] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showLoginModalLocal, setShowLoginModalLocal] = useState(false);
  const [showGuestBookingModal, setShowGuestBookingModal] = useState(false);
  const [guestWhatsappConsent, setGuestWhatsappConsent] = useState(false);
  const [showPhoneContactModal, setShowPhoneContactModal] = useState(false);
  const [requiredPaymentStatus, setRequiredPaymentStatus] = useState("");
  const [eventPaymentCapability, setEventPaymentCapability] = useState(null);
  const [requiredPaymentInline, setRequiredPaymentInline] = useState(null);
  const bookingDiagRef = useRef(null);
  // R33 P2: preserve one random request id only while the exact same booking
  // attempt is being retried. A changed form creates a fresh request id.
  const bookingRequestAttemptRef = useRef(null);
  const restoredDraftRef = useRef(false);
  const workerScrollRef = useRef(null);
  const workerTouchRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    lock: null,
    moved: false,
  });
  const workerDragRef = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
    moved: false,
  });
  const analyticsStartedRef = useRef(false);
  const analyticsServiceTrackedRef = useRef(new Set());
  const analyticsWorkerTrackedRef = useRef(new Set());

  useEffect(() => {
    const visibleFromProps = Array.isArray(servicesProp) ? servicesProp.filter(isServiceVisibleForBooking) : [];
    if (visibleFromProps.length > 0) {
      setLocalServices(visibleFromProps);
    }
  }, [servicesProp]);

  useEffect(() => {
    if (!businessId || analyticsStartedRef.current) return;
    analyticsStartedRef.current = true;
    trackBookingFunnelEventSafe({
      db,
      businessId,
      type: "booking_started",
      payload: {
        sourceArea: "booking_interface",
        bookingSource: "client_online",
        createdFrom: "booking_interface",
        funnelStep: "booking_started",
        serviceId: selectedServiceId || null,
        serviceName: selectedService?.name || selectedService?.title || selectedService?.serviceName || null,
      },
    });
  }, [db, businessId]);

  useEffect(() => {
    if (!businessId || !selectedServiceId) return;
    const key = String(selectedServiceId);
    if (analyticsServiceTrackedRef.current.has(key)) return;
    analyticsServiceTrackedRef.current.add(key);
    trackBookingFunnelEventSafe({
      db,
      businessId,
      type: "service_view",
      payload: {
        sourceArea: "booking_interface",
        funnelStep: "service_view",
        serviceId: selectedServiceId || null,
        serviceName: selectedService?.name || selectedService?.title || selectedService?.serviceName || null,
        servicePrice: selectedService?.price ?? selectedService?.basePrice ?? selectedService?.amount ?? null,
        serviceDuration: selectedService?.duration ?? selectedService?.durationMins ?? selectedService?.minutes ?? null,
      },
    });
  }, [db, businessId, selectedServiceId, selectedService]);

  useEffect(() => {
    if (!businessId || !selectedEmployeeId) return;
    const key = String(selectedEmployeeId);
    if (analyticsWorkerTrackedRef.current.has(key)) return;
    analyticsWorkerTrackedRef.current.add(key);
    const emp = Array.isArray(employees) ? employees.find((item) => String(item.id) === key) : null;
    trackBookingFunnelEventSafe({
      db,
      businessId,
      type: "worker_selected",
      payload: {
        sourceArea: "booking_interface",
        funnelStep: "worker_selected",
        workerId: key,
        workerName: emp?.name || emp?.displayName || emp?.employeeName || null,
        serviceId: selectedServiceId || null,
        serviceName: selectedService?.name || selectedService?.title || selectedService?.serviceName || null,
      },
    });
  }, [db, businessId, selectedEmployeeId, employees, selectedServiceId, selectedService]);

  useEffect(() => {
    const nextMode = getInitialVisitModeForBusiness(businessDoc);
    setVisitMode((prev) => {
      if (isVisitModeAvailableForBusiness(businessDoc, prev)) return prev;
      return nextMode;
    });
  }, [businessDoc?.serviceMode, businessDoc?.mode]);

  const setDiagSafe = (obj) => {
    // Diagnóstico interno solamente. Nunca se renderiza ni se muestra completo al cliente.
    const safeDiagnostic = { time: new Date().toISOString(), ...(obj || {}) };
    bookingDiagRef.current = safeDiagnostic;
    try { window.__BOOKING_DIAG__ = safeDiagnostic; } catch (_) {}
    try { console.warn("[BookingInterface] internal booking diagnostic", safeDiagnostic); } catch (_) {}
  };

  const handleGoToLogin = () => {
    setShowLoginModalLocal(false);
    try {
      const draft = {
        businessId: businessId || null,
        selectedEmployeeId: workerChoiceMode === "specific" ? (selectedEmployeeId || null) : null,
        workerChoiceMode: workerChoiceMode || "any",
        selectedServiceId: selectedServiceId || null,
        eventOptions,
        selectedDate: selectedDate ? selectedDate.toISOString() : null,
        selectedTime: selectedTime || null,
        customerName: customerName || "",
        customerEmail: customerEmail || "",
        customerPhone: customerPhone || "",
        visitMode: visitMode || null,
        customerLocation: customerLocation || null,
        customerExactAddress: customerExactAddress || "",
        savedAt: new Date().toISOString(),
      };
      saveDraftToSession(draft);
    } catch (e) { /* ignore */ }
    navigate("/login", { state: { from: location.pathname + location.search } });
  };

  // Restore draft
  useEffect(() => {
    if (restoredDraftRef.current) return;
    const draft = loadDraftFromSession();
    if (!draft) return;
    if (draft.businessId && businessId && String(draft.businessId) !== String(businessId)) return;
    try {
      if (draft.workerChoiceMode === "specific" && draft.selectedEmployeeId) {
        setWorkerChoiceMode("specific");
        setSelectedEmployeeId(draft.selectedEmployeeId);
      } else {
        chooseAnyWorker();
      }
      if (draft.selectedServiceId) setSelectedServiceId(draft.selectedServiceId);
      if (draft.eventOptions && typeof draft.eventOptions==='object')setEventOptions(draft.eventOptions);
      if (draft.selectedDate) {
        const d = new Date(draft.selectedDate);
        if (!isNaN(d.getTime())) {
          setSelectedDate(d);
          setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
        }
      }
      if (draft.selectedTime) setSelectedTime(draft.selectedTime);
      if (draft.customerName) setCustomerName(draft.customerName);
      if (draft.customerEmail) setCustomerEmail(draft.customerEmail);
      if (draft.customerPhone) {
        setCustomerPhone(draft.customerPhone);
        setCustomerPhoneVerified(false);
      }
      if (draft.visitMode) setVisitMode(draft.visitMode);
      if (draft.customerLocation) setCustomerLocation(draft.customerLocation);
      if (draft.customerExactAddress) setCustomerExactAddress(draft.customerExactAddress);
      restoredDraftRef.current = true;
    } catch (e) { console.warn("restore draft failed", e); }
  }, [businessId]);

  // Load business
  useEffect(()=> {
    let mounted = true;
    (async ()=> {
      if(!db || !businessId){
        if(mounted){
          setBusinessDoc(null);
          setLoadingBusinessDoc(false);
          setStatusMessage({ type: "error", text: "Falta businessId. Vuelve atrás y selecciona una empresa." });
          setDiagSafe({ where: "loadBusiness", businessId: businessId || null, note: "missing businessId" });
        }
        return;
      }
      setLoadingBusinessDoc(true);
      try{
        let data = null;
        if (isIosCapacitor()) {
          data = await withTimeout(fsClient.getDocByPath(`businesses/${businessId}`, { db }), 7000, "ios-get-business-timeout");
        } else {
          const snap = await withTimeout(getDoc(doc(db, "businesses", businessId)), 15000, "getDoc-business-timeout");
          if (snap && snap.exists()) data = snap.data();
        }

        if(mounted) {
          setBusinessDoc((prev) => {
            if (data) return data;
            if (isIosCapacitor() && prev) return prev;
            return null;
          });
        }
        if(mounted && !data) {
          setStatusMessage({ type: "error", text: "No se encontró la empresa en Firestore." });
          setDiagSafe({ where: "loadBusiness", businessId });
        }
      }catch(e){
        console.warn("load business doc", e);
        if(mounted){
          setStatusMessage({ type: "error", text: "Error cargando empresa (Firestore)." });
          setDiagSafe({ where: "loadBusiness", businessId, error: String(e && e.message ? e.message : e) });
        }
      }
      finally{ if(mounted) setLoadingBusinessDoc(false); }
    })();
    return ()=> { mounted = false; };
  }, [db, businessId]);

  // Load employees
  useEffect(()=> {
    let mounted = true;
    (async ()=> {
      if(!db || !businessId) return;
      setLoadingEmployees(true);
      try{
        let arr = [];
        if (isIosCapacitor()) {
          const docs = await withTimeout(fsClient.listCollectionByPath(`businesses/${businessId}/employees`, {}, { db }), 7000, "ios-list-employees-timeout");
          arr = (Array.isArray(docs) ? docs : []).map(d => {
            const data = { ...d, id: d.id || d.employeeId || d._id };
            const norm = normalizeEmployeeServices(data.services);
            data._serviceIds = norm._serviceIds;
            data._serviceNames = norm._serviceNames;
            data._serviceNamesOriginal = norm._serviceNamesOriginal || [];
            return data;
          });
        } else {
          const snap = await withTimeout(getDocs(collection(db, `businesses/${businessId}/employees`)), 15000, "getDocs-employees-timeout");
          arr = snap.docs.map(d => {
            const data = { id: d.id, ...d.data() };
            const norm = normalizeEmployeeServices(data.services);
            data._serviceIds = norm._serviceIds;
            data._serviceNames = norm._serviceNames;
            data._serviceNamesOriginal = norm._serviceNamesOriginal || [];
            return data;
          });
        }
        if(mounted) {
          setEmployees((prev) => {
            if (arr.length > 0) return arr;
            if (isIosCapacitor() && Array.isArray(prev) && prev.length > 0) return prev;
            return arr;
          });
        }
      }catch(e){
        console.warn("load employees", e);
        if(mounted){
          setEmployees((prev) => (isIosCapacitor() && Array.isArray(prev) && prev.length > 0 ? prev : []));
          setStatusMessage({ type: "error", text: "No se pudieron cargar los empleados." });
          setDiagSafe({ where: "loadEmployees", businessId, error: String(e && e.message ? e.message : e) });
        }
      }
      finally{ if(mounted) setLoadingEmployees(false); }
    })();
    return ()=> { mounted = false; };
  }, [db, businessId]);

  // Load services
  useEffect(()=> {
    let mounted = true;
    (async ()=> {
      if(!db || !businessId) return;
      setLoadingServices(true);
      try{
        let list = [];
        if (isIosCapacitor()) {
          const docs = await withTimeout(fsClient.listCollectionByPath(`businesses/${businessId}/services`, {}, { db }), 7000, "ios-list-services-timeout");
          list = Array.isArray(docs) ? docs.map(d => ({ ...d, id: d.id || d.serviceId || d._id })) : [];
        } else {
          const snap = await withTimeout(getDocs(collection(db, `businesses/${businessId}/services`)), 15000, "getDocs-services-timeout");
          list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        }
        if(mounted) {
          const visibleFresh = (list || []).filter(isServiceVisibleForBooking);
          const visibleFromProps = Array.isArray(servicesProp) ? servicesProp.filter(isServiceVisibleForBooking) : [];
          setLocalServices((prev) => {
            if (visibleFresh.length > 0) return visibleFresh;
            if (Array.isArray(prev) && prev.length > 0) return prev;
            if (visibleFromProps.length > 0) return visibleFromProps;
            return [];
          });
        }
      }catch(e){
        console.warn("load services", e);
        if(mounted){
          const visibleFromProps = Array.isArray(servicesProp) ? servicesProp.filter(isServiceVisibleForBooking) : [];
          setLocalServices((prev) => {
            if (Array.isArray(prev) && prev.length > 0) return prev;
            if (visibleFromProps.length > 0) return visibleFromProps;
            return [];
          });
          if (!visibleFromProps.length && !(Array.isArray(localServices) && localServices.length > 0)) {
            setStatusMessage({ type: "error", text: "No se pudieron cargar los servicios." });
          }
          setDiagSafe({ where: "loadServices", businessId, error: String(e && e.message ? e.message : e) });
        }
      } finally{
        if(mounted) setLoadingServices(false);
      }
    })();
    return ()=> { mounted = false; };
  }, [db, businessId]);

  // Load only sanitized blocking availability for the visible calendar month.
  useEffect(()=> {
    let mounted = true;

    let inFlight = null;
    const load = async ()=> {
      if(!businessId || !currentMonth) return;

      const monthStart = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        1
      );

      const monthEnd = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        0
      );

      const startDate = isoDateFromDate(selectedDate || monthStart);
      const endDate = isoDateFromDate(selectedDate || monthEnd);

      try{
        if(mounted) setAvailabilityStatus('loading');

        const response = await callCallableNativeSafe({
          functionName: "getPublicBookingAvailability",
          data: {
            businessId,
            startDate,
            endDate,
          },
          timeoutMs: 20000,
          requireAuth: false,
          requireAppCheck: true,
        });

        const payload =
          response && Object.prototype.hasOwnProperty.call(response, "data")
            ? response.data
            : response;

        if (!Array.isArray(payload?.bookings)) throw new Error('Invalid availability response');
        const arr = payload.bookings;

        const map = {};

        arr.forEach((booking) => {
          const key = bookingDateKeyFromRecord(booking);
          if(!key) return;
          if(!map[key]) map[key] = [];
          map[key].push(booking);
        });

        if(mounted) {
          setBookingsMap(map); setAvailabilityStatus('ready');
          setStatusMessage(previous => previous?.text === 'No se pudieron cargar las reservas existentes.' ? null : previous);
        }
        return mounted ? map : null;
      }catch(e){
        console.warn("load public availability", e);

        if(mounted){
          setAvailabilityStatus('error');
          setStatusMessage({
            type: "error",
            text: "No se pudieron cargar las reservas existentes.",
          });
          setDiagSafe({
            where: "loadPublicBookingAvailability",
            businessId,
            startDate,
            endDate,
            error: String(e && e.message ? e.message : e),
          });
        }
      }
      return null;
    };
    const refresh = () => {
      if (!inFlight) inFlight = load().finally(() => { inFlight = null; });
      return inFlight;
    };
    availabilityRefreshRef.current = refresh;
    refresh();
    const resume = () => { if(document.visibilityState !== 'hidden') refresh(); };
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    const timer = selectedDate ? window.setInterval(resume, 60000) : null;

    return ()=> {
      mounted = false;
      availabilityRefreshRef.current = null;
      window.clearInterval(timer);
      window.removeEventListener('focus', resume);
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [businessId, currentMonth, selectedDate]);

  // Prefill customer fields
  useEffect(()=> {
    let mounted = true;

    // Never retain identity or contact state from the previous account.
    setCustomerName("");
    setCustomerEmail("");
    setCustomerPhone("");
    setCustomerPhoneVerified(false);
    setShowPhoneContactModal(false);

    (async ()=> {
      const effectiveUser = getEffectiveUser(user);
      if(!effectiveUser || !effectiveUser.uid || !db) return;
      try{
        let d = null;
        if (isIosCapacitor()) {
          d = await withTimeout(fsClient.getDocByPath(`users/${effectiveUser.uid}`, { db }), 5000, "ios-get-user-timeout");
        } else {
          const snap = await withTimeout(getDoc(doc(db, "users", effectiveUser.uid)), 12000, "getDoc-user-timeout");
          if (snap && snap.exists()) d = snap.data();
        }
        if(!mounted) return;
        if(d){
          const contactPhone =
            d.phoneNormalized ||
            d.phone ||
            d.telefonoNormalized ||
            d.telefono ||
            d.phoneNumber ||
            d.whatsapp ||
            d.customerPhone ||
            "";

          const profileVerifiedPhone = normalizePhoneToE164(
            d.phoneVerifiedE164 || "",
            "PA"
          );
          const authVerifiedPhone = normalizePhoneToE164(
            effectiveUser.phoneNumber || "",
            "PA"
          );
          const verificationProvider = String(
            d.phoneVerificationProvider || ""
          ).trim().toLowerCase();

          const verifiedPhone =
            authVerifiedPhone ||
            (
              d.phoneVerified === true &&
              verificationProvider === "firebase_auth"
                ? profileVerifiedPhone
                : ""
            );

          const hasVerifiedPhoneIdentity = Boolean(verifiedPhone);

          setCustomerName(d.nombre || d.name || d.displayName || effectiveUser.displayName || "");
          setCustomerEmail(d.email || effectiveUser.email || "");
          setCustomerPhone(
            hasVerifiedPhoneIdentity
              ? verifiedPhone
              : contactPhone
          );
          setCustomerPhoneVerified(hasVerifiedPhoneIdentity);
        } else {
          const authVerifiedPhone = normalizePhoneToE164(
            effectiveUser.phoneNumber || "",
            "PA"
          );

          setCustomerName(effectiveUser.displayName || "");
          setCustomerEmail(effectiveUser.email || "");
          setCustomerPhone(authVerifiedPhone || "");
          setCustomerPhoneVerified(Boolean(authVerifiedPhone));
        }
      }catch(e){
        console.warn("user prefill", e);
      }
    })();
    return ()=> { mounted = false; };
  }, [user, db]);

  const visibleServices = useMemo(() => {
    return (Array.isArray(localServices) ? localServices : []).filter(isServiceVisibleForBooking).map(service => ({...service, countryCode: businessDoc?.countryCode || "PA", currency: businessDoc?.currency || getMarket(businessDoc?.countryCode).currency, locale: businessDoc?.locale || getMarket(businessDoc?.countryCode).locale}));
  }, [localServices, businessDoc]);

  useEffect(()=> {
    if(selectedServiceId && visibleServices && visibleServices.length > 0){
      const s = visibleServices.find(x => String(getBookingServiceOptionId(x)) === String(selectedServiceId));
      setSelectedService(s || null);
    } else if (!selectedServiceId) {
      setSelectedService(null);
    }
  }, [selectedServiceId, visibleServices]);



  useEffect(() => {
    const nextInitialId = initialServiceId || getBookingServiceOptionId(initialService);
    if (!nextInitialId) return;
    setSelectedServiceId((prev) => (String(prev || "") === String(nextInitialId) ? prev : nextInitialId));
    if (initialService) setSelectedService(initialService);
  }, [initialServiceId, initialService]);

  const bookableEmployees = useMemo(() => {
    return (Array.isArray(employees) ? employees : []).filter(isPublicServiceWorker);
  }, [employees]);


  useEffect(() => {
    if (selectedServiceId && selectedService && !isServiceVisibleForBooking(selectedService)) {
      setSelectedServiceId("");
      setSelectedService(null);
    }
  }, [selectedServiceId, selectedService]);

  const companySlotsInfo = useMemo(() => {
    if(!selectedDate) return { slots: [], info: {} };
    const dateKey = isoDateFromDate(selectedDate);
    const companyRangesForDay = businessRangesForDate(businessDoc, selectedDate);

    const isNoWorkers = Boolean(isEventsBusinessDoc(businessDoc) && businessDoc && businessDoc.noWorkers);
    const hasBookableWorkers = !isNoWorkers && bookableEmployees && bookableEmployees.length > 0;
    const hasEmployeeSlots = hasBookableWorkers && bookableEmployees.some(emp => {
      const ranges = workerRangesForDate(emp, selectedDate, businessDoc);
      return normalizeAvailabilityRanges(ranges).length > 0 && !isEmployeeUnavailableOnDate(emp, selectedDate);
    });

    const allSlotsSet = new Set();
    if (isNoWorkers || !hasBookableWorkers) {
      const slots = generateSlotsFromRanges(companyRangesForDay, TIME_SLOT_INTERVAL);
      slots.forEach(s => allSlotsSet.add(s));
    } else {
      bookableEmployees.forEach(emp => {
        if (isEmployeeUnavailableOnDate(emp, selectedDate)) return;
        const slots = employeeBaseSlotsForDate(emp, selectedDate, businessDoc);
        slots.forEach(s => allSlotsSet.add(s));
      });
    }

    const allSlots = Array.from(allSlotsSet).filter(time => { try { localInstant(dateKey, time, businessZone(businessDoc || {})); return true; } catch { return false; } }).sort((a,b)=> timeToMinutes(a)-timeToMinutes(b));
    let serviceDuration = 30, serviceBufferBefore = 0, serviceBufferAfter = 0;
    if (selectedService) {
      serviceDuration = Number(selectedService.duration || 30);
      serviceBufferBefore = Number(selectedService.bufferBeforeMinutes || 0);
      serviceBufferAfter = Number(selectedService.bufferAfterMinutes || 0);
    } else {
      const sdoc = visibleServices.find(s => String(s.id) === String(selectedServiceId));
      if (sdoc) { serviceDuration = Number(sdoc.duration || 30); serviceBufferBefore = Number(sdoc.bufferBeforeMinutes || 0); serviceBufferAfter = Number(sdoc.bufferAfterMinutes || 0); }
      else if (businessDoc && businessDoc.singleServiceDuration) serviceDuration = Number(businessDoc.singleServiceDuration);
    }

    if (isEventsBusinessDoc(businessDoc) && selectedService) {
      serviceDuration = Math.max(30, Math.round(resolveEventBookingDraft(selectedService, eventOptions).visitHours * 60));
    }
    if (isEventsBusinessDoc(businessDoc)) {
      serviceBufferBefore = getBusinessEventBufferBefore(businessDoc);
      serviceBufferAfter = getBusinessEventBufferAfter(businessDoc);
    }

    const info = {};
    const now = wallDate(new Date(),businessDoc || {});
    const isToday = selectedDate.toDateString() === now.toDateString();
    const nowMinutes = now.getHours()*60 + now.getMinutes();
    const businessBlockedDate = isBusinessBlockedOnDate(businessDoc, selectedDate);
    const minimumNoticeMs = getMinimumBookingNoticeHours(businessDoc) * 60 * 60 * 1000;
    const bookedForDateBusiness = bookedSlotsForNoWorkerDate(bookingsMap, dateKey);
    const bookedCountsForDateBusiness = bookedSlotCountsForNoWorkerDate(bookingsMap, dateKey);
    const sameServiceBookedForDate = bookedSlotsForNoWorkerServiceDate(bookingsMap, dateKey, selectedServiceId);
    const eventGlobalCapacity = getGlobalEventCapacity(businessDoc);
    const bookedByEmployeeCache = {};

    for(const slot of allSlots){
      let anyOffererCanFit = false, anyHasSlot = false, selectedEmployeeCanFit = false;
      if (isNoWorkers || !hasEmployeeSlots) {
        anyHasSlot = true;
        const neededSlots = Math.ceil(serviceDuration / TIME_SLOT_INTERVAL);
        const preSlots = Math.ceil(serviceBufferBefore / TIME_SLOT_INTERVAL);
        const postSlots = Math.ceil(serviceBufferAfter / TIME_SLOT_INTERVAL);
        const index = allSlots.indexOf(slot);
        if (index === -1) anyOffererCanFit = false;
        else {
          let fits = true;
          for (let offset = -preSlots; offset < neededSlots + postSlots; offset++) {
            const pos = index + offset;
            const s = allSlots[pos];
            if (!s) { fits = false; break; }
            if (isEventsBusinessDoc(businessDoc)) {
              if (businessBlockedDate) { fits = false; break; }
              const slotDateTime = slotDateTimeFromDateAndTime(selectedDate, slot, businessDoc || {});
              if (minimumNoticeMs > 0 && slotDateTime.getTime() < Date.now() + minimumNoticeMs) { fits = false; break; }

              const overlapping = eventBookingsOverlappingSlot(bookingsMap, dateKey, slot, serviceDuration, businessDoc);

              if (overlapping.length >= eventGlobalCapacity) { fits = false; break; }

              if (!serviceAllowsConcurrentSameService(selectedService)) {
                const sameServiceOverlap = overlapping.some(b => String(b.serviceId || "") === String(selectedServiceId || ""));
                if (sameServiceOverlap) { fits = false; break; }
              }
            } else if (bookedForDateBusiness.has(s)) { fits = false; break; }
          }
          anyOffererCanFit = fits; selectedEmployeeCanFit = fits;
        }
      } else {
        for(const emp of bookableEmployees){
          if (isEmployeeUnavailableOnDate(emp, selectedDate)) continue;
          const empSlots = employeeBaseSlotsForDate(emp, selectedDate, businessDoc);
          if(empSlots.includes(slot)) anyHasSlot = true;
          let svc = selectedService || visibleServices.find(s => String(s.id) === String(selectedServiceId)) || null;
          const duration = Number((svc && svc.duration) || serviceDuration || 30);
          const bufferBefore = Number((svc && (svc.bufferBeforeMinutes || 0)) || serviceBufferBefore || 0);
          const bufferAfter = Number((svc && (svc.bufferAfterMinutes || 0)) || serviceBufferAfter || 0);
          const neededSlots = Math.ceil(duration / TIME_SLOT_INTERVAL);
          const preSlots = Math.ceil(bufferBefore / TIME_SLOT_INTERVAL);
          const postSlots = Math.ceil(bufferAfter / TIME_SLOT_INTERVAL);
          const index = empSlots.indexOf(slot);
          if(index === -1) continue;
          if(!bookedByEmployeeCache[emp.id]) bookedByEmployeeCache[emp.id] = bookedSlotsForEmployeeDate(bookingsMap, emp.id, dateKey);
          const booked = bookedByEmployeeCache[emp.id];
          let fits = true;
          for(let offset = -preSlots; offset < neededSlots + postSlots; offset++){
            const pos = index + offset;
            const s = empSlots[pos];
            if(!s){ fits = false; break; }
            if(booked.has(s)){ fits = false; break; }
          }
          if(fits) anyOffererCanFit = true;
          if(selectedEmployeeId && String(emp.id) === String(selectedEmployeeId)) selectedEmployeeCanFit = fits;
        }
      }

      const slotMinutes = timeToMinutes(slot);
      const isPast = isToday && (slotMinutes <= nowMinutes);
      info[slot] = { anyOffererCanFit, anyHasSlot, selectedEmployeeCanFit, past: isPast };
    }

    return { slots: allSlots, info };
  }, [selectedDate, bookableEmployees, bookingsMap, selectedServiceId, selectedEmployeeId, selectedService, visibleServices, businessDoc, eventOptions]);

  const employeesToShow = useMemo(() => {
    const canEmpFitSlot = (emp) => {
      if(!emp || !selectedDate || !selectedTime) return false;
      if(isEmployeeUnavailableOnDate(emp, selectedDate)) return false;
      const slots = employeeBaseSlotsForDate(emp, selectedDate, businessDoc);
      const idx = slots.indexOf(selectedTime);
      if(idx === -1) return false;
      let duration = 30;
      let bufferBefore = 0;
      let bufferAfter = 0;
      if(selectedService) {
        duration = Number(selectedService.duration || 30);
        bufferBefore = Number(selectedService.bufferBeforeMinutes || 0);
        bufferAfter = Number(selectedService.bufferAfterMinutes || 0);
      } else {
        const sdoc = visibleServices.find(s => String(s.id) === String(selectedServiceId));
        if(sdoc) duration = Number(sdoc.duration || 30);
      }
      const needed = Math.ceil(duration / TIME_SLOT_INTERVAL);
      const pre = Math.ceil(bufferBefore / TIME_SLOT_INTERVAL);
      const post = Math.ceil(bufferAfter / TIME_SLOT_INTERVAL);
      const booked = bookedSlotsForEmployeeDate(bookingsMap, emp.id, isoDateFromDate(selectedDate));
      for(let offset = -pre; offset < needed + post; offset++){
        const s = slots[idx + offset];
        if(!s) return false;
        if(booked.has(s)) return false;
      }
      return true;
    };

    if(selectedDate && selectedTime){
      const fit = [];
      const notFit = [];
      for(const emp of bookableEmployees){
        if(!emp) continue;
        const offers = selectedServiceId ? employeeOffersService(emp, selectedServiceId) : true;
        const fitsSlot = canEmpFitSlot(emp) && offers;
        if(fitsSlot) fit.push(emp); else notFit.push(emp);
      }
      return [...fit, ...notFit];
    }

    if(selectedServiceId){
      const offer = [];
      const notOffer = [];
      for(const emp of bookableEmployees){
        if(!emp) continue;
        if(employeeOffersService(emp, selectedServiceId)) offer.push(emp); else notOffer.push(emp);
      }
      return [...offer, ...notOffer];
    }

    return bookableEmployees;
  }, [bookableEmployees, selectedDate, selectedTime, selectedServiceId, selectedService, bookingsMap, localServices, businessDoc]);

  const isSingleServiceMode = !!(businessDoc && businessDoc.singleService);
  const isNoWorkers = Boolean(isEventsBusinessDoc(businessDoc) && businessDoc && businessDoc.noWorkers);
  const servicesWithImages = !!(businessDoc && businessDoc.servicesWithImages);
  const isEventsBusiness = isEventsBusinessDoc(businessDoc);
  const activeEventOptions = resolveEventBookingDraft(selectedService, eventOptions);

  const anyWorkerMeta = selectedTime ? (companySlotsInfo.info[selectedTime] || null) : null;
  const canChooseAnyWorker = !isNoWorkers && !!selectedDate && !!selectedTime && !!anyWorkerMeta?.anyOffererCanFit;
  const anyWorkerDisabledReason = !selectedDate
    ? "Elige fecha"
    : !selectedTime
      ? "Elige hora"
      : "Sin disponibilidad";

  const selectedEmployeeForPicker = useMemo(() => {
    if (workerChoiceMode !== "specific" || !selectedEmployeeId) return null;
    return bookableEmployees.find((e) => String(e.id) === String(selectedEmployeeId)) || null;
  }, [bookableEmployees, selectedEmployeeId, workerChoiceMode]);

  const availableWorkersForPicker = useMemo(() => {
    return (employeesToShow || []).filter((emp) => {
      const state = employeeVisualState(
        emp,
        selectedEmployeeId,
        selectedDate,
        selectedTime,
        selectedService,
        selectedServiceId,
        localServices,
        bookingsMap,
        businessDoc
      );
      return state.available && state.offers;
    });
  }, [employeesToShow, selectedEmployeeId, workerChoiceMode, selectedDate, selectedTime, selectedService, selectedServiceId, localServices, bookingsMap, businessDoc]);





  const workerPickerRows = useMemo(() => {
    const q = String(workerSearch || "").trim().toLowerCase();

    const rows = (employeesToShow || []).map((emp) => {
      const state = employeeVisualState(
        emp,
        selectedEmployeeId,
        selectedDate,
        selectedTime,
        selectedService,
        selectedServiceId,
        localServices,
        bookingsMap,
        businessDoc
      );

      const name = emp?.name || emp?.displayName || "Trabajador";
      const role = emp?.role || emp?.title || "";
      const haystack = `${name} ${role}`.toLowerCase();

      return {
        emp,
        state,
        name,
        role,
        photo: emp?.photoUrl || emp?.photo || emp?.image || emp?.avatarUrl || null,
        available: !!state.available && !!state.offers,
        disabled: !state.available || !state.offers,
        selected: workerChoiceMode === "specific" && !!selectedEmployeeId && String(emp.id) === String(selectedEmployeeId),
        matches: !q || haystack.includes(q),
      };
    }).filter((row) => row.matches);

    return {
      available: rows.filter((row) => row.available),
      unavailable: rows.filter((row) => !row.available),
    };
  }, [employeesToShow, selectedEmployeeId, workerChoiceMode, selectedDate, selectedTime, selectedService, selectedServiceId, localServices, bookingsMap, workerSearch, businessDoc]);

  const selectedWorkerPhoto =
    selectedEmployeeForPicker?.photoUrl ||
    selectedEmployeeForPicker?.photo ||
    selectedEmployeeForPicker?.image ||
    selectedEmployeeForPicker?.avatarUrl ||
    null;

  const selectedWorkerLabel = selectedEmployeeForPicker
    ? (selectedEmployeeForPicker.name || selectedEmployeeForPicker.displayName || "Trabajador")
    : canChooseAnyWorker
      ? "Cualquiera disponible"
      : "Elegir trabajador";

  const selectedWorkerSubLabel = selectedEmployeeForPicker
    ? (selectedEmployeeForPicker.role || selectedEmployeeForPicker.title || "Trabajador seleccionado")
    : canChooseAnyWorker
      ? "El negocio asignará el trabajador"
      : "Elige fecha, hora y servicio para ver opciones";

  const deliverySelectedForBooking = isDeliveryVisitMode(visitMode);
  const locationRequired = deliverySelectedForBooking;
  const matchedServiceArea = locationRequired && customerLocation ? customerLocation : null;
  const deliveryFee = locationRequired && matchedServiceArea ? bookingMoneyNumber(matchedServiceArea.extraFee, 0) : 0;
  const isOutsideCoverage = false;
  const locationDetailsRequired = Boolean(locationRequired && (!customerLocation || !String(customerExactAddress || "").trim()));
  const eventSummaryForTotal = isEventsBusiness && selectedService ? getEventPriceSummary(selectedService, activeEventOptions || {}) : null;
  const bookingPriceType = isEventsBusiness ? "fixed" : normalizeBookingServicePriceType(selectedService);
  const bookingPriceIsEstimate = !isEventsBusiness && bookingPriceType === "from";
  const baseBookingPrice = getBaseBookingPrice(selectedService, isEventsBusiness, eventSummaryForTotal);
  const finalBookingTotal = baseBookingPrice + deliveryFee;

  // LOOP D2.5 — Membership application from /my-memberships public booking flow.
  const urlUseMembership = qs.get("useMembership") === "1" || qs.get("membership") === "1";
  const urlMembershipId = String(qs.get("membershipId") || qs.get("customerMembershipId") || "").trim();
  const urlPlanId = String(qs.get("planId") || qs.get("membershipPlanId") || "").trim();
  const [membershipBenefits, setMembershipBenefits] = useState([]);
  const [membershipBenefitsLoading, setMembershipBenefitsLoading] = useState(false);
  const [membershipBenefitsError, setMembershipBenefitsError] = useState("");
  const [selectedMembershipBenefitId, setSelectedMembershipBenefitId] = useState("");

  const selectedMembershipBenefit = useMemo(() => {
    if (!selectedMembershipBenefitId) return null;
    return (Array.isArray(membershipBenefits) ? membershipBenefits : []).find((item) => String(item.id) === String(selectedMembershipBenefitId)) || null;
  }, [membershipBenefits, selectedMembershipBenefitId]);

  const selectedMembershipBenefitPayload = useMemo(() => {
    const item = selectedMembershipBenefit;
    if (!item || !item.membership) return null;
    const membership = item.membership || {};
    const plan = item.plan || {};
    const type = String(item.type || item.benefitType || "credit").toLowerCase();
    const discountPercent = Number(item.discountPercent || plan.discountPercent || membership.discountPercent || 0) || 0;
    return {
      membershipApplied: true,
      membershipBenefitApplied: true,
      customerMembershipId: membership.id || item.customerMembershipId || null,
      membershipId: membership.id || item.membershipId || null,
      membershipPlanId: plan.id || membership.planId || item.planId || null,
      membershipPlanName: plan.name || membership.planName || "Membresía",
      membershipBenefitType: type === "discount" ? "discount" : "credit",
      membershipCreditsUsed: type === "discount" ? 0 : 1,
      membershipDiscountPercent: type === "discount" ? discountPercent : 0,
      membershipDiscountAmount: 0,
      membershipBenefitLabel: item.label || (type === "discount" ? `Aplicar ${discountPercent}% de membresía` : `Usar 1 ${membership.creditUnit || plan.creditUnit || "crédito"}`),
      membershipSource: "public_booking_flow",
    };
  }, [selectedMembershipBenefit]);

  const eventCapabilityUid = getEffectiveUser(user)?.uid || null;
  useEffect(() => {
    let current = true;
    if (!businessId || !isEventsBusiness) return () => { current = false; };
    callCallableNativeSafe({functionName:'getEventBookingCapabilities',data:{businessId},requireAuth:false,requireAppCheck:true})
      .then(({data}) => { if(current)setEventPaymentCapability({businessId,uid:eventCapabilityUid,enabled:data?.approvalFirstEnabled===true,primaryCharacterSelectionEnabled:data?.primaryCharacterSelectionEnabled===true}); })
      .catch(() => { if(current)setEventPaymentCapability({businessId,uid:eventCapabilityUid,enabled:false}); });
    return () => { current = false; };
  }, [businessId,isEventsBusiness,eventCapabilityUid]);
  const eventApprovalFirstEnabled = eventPaymentCapability?.businessId===businessId && eventPaymentCapability?.uid===eventCapabilityUid && eventPaymentCapability?.enabled===true;
  const primaryCharacterSupported = !isEventsBusiness || selectedService?.charactersMode!=='choose_primary' || (eventPaymentCapability?.businessId===businessId && eventPaymentCapability?.uid===eventCapabilityUid && eventPaymentCapability?.primaryCharacterSelectionEnabled===true);
  const publicPaymentPreview = useMemo(
    () => getPublicBookingPaymentPreview({
      service: selectedService || {},
      isEventsBusiness,
      visitMode,
      membershipApplied: Boolean(selectedMembershipBenefitPayload),
      eventApprovalFirstEnabled, eventTotalAmount: finalBookingTotal, business: businessDoc,
      eventVisitType: activeEventOptions.visitType,
    }),
    [selectedService, isEventsBusiness, visitMode, selectedMembershipBenefitPayload, eventApprovalFirstEnabled, finalBookingTotal, businessDoc, activeEventOptions.visitType]
  );

  useEffect(() => {
    setRequiredPaymentStatus("");
  }, [selectedServiceId, publicPaymentPreview?.dueNowCents, publicPaymentPreview?.reason]);

  useEffect(() => {
    let cancelled = false;
    async function loadMembershipBenefits() {
      const effectiveUser = getEffectiveUser(user);
      if (!businessId || !effectiveUser?.uid || !selectedServiceId) {
        setMembershipBenefits([]);
        setSelectedMembershipBenefitId("");
        setMembershipBenefitsError("");
        return;
      }
      setMembershipBenefitsLoading(true);
      setMembershipBenefits([]);
      setSelectedMembershipBenefitId("");
      setMembershipBenefitsError("");
      try {
        const callable = bookingPaymentCallable("getMyApplicableMembershipBenefits");
        const res = await callable({
          businessId,
          preferredMembershipId: urlMembershipId || null,
          preferredPlanId: urlPlanId || null,
          booking: {
            serviceId: selectedServiceId || null,
            serviceName: selectedService?.name || selectedService?.title || null,
            customerEmail: effectiveUser.email || customerEmail || null,
            customerPhone: customerPhone || null,
          },
        });
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.items) ? res.data.items : [];
        setMembershipBenefits(rows);
        setSelectedMembershipBenefitId((prev) => {
          const stillValid = prev && rows.some((item) => String(item.id) === String(prev));
          if (stillValid) return prev;
          const preferred = rows.find((item) => {
            const membership = item?.membership || {};
            const plan = item?.plan || {};
            return (urlMembershipId && String(membership.id || item.membershipId || "") === urlMembershipId) ||
              (urlPlanId && String(plan.id || membership.planId || item.planId || "") === urlPlanId);
          });
          if (urlUseMembership && (preferred || rows[0])) return String((preferred || rows[0]).id);
          return "";
        });
      } catch (e) {
        if (cancelled) return;
        console.warn("[BookingInterface] membership benefits skipped", e);
        setMembershipBenefits([]);
        setSelectedMembershipBenefitId("");
        setMembershipBenefitsError("No pudimos cargar tus beneficios de membresía.");
      } finally {
        if (!cancelled) setMembershipBenefitsLoading(false);
      }
    }
    loadMembershipBenefits();
    return () => { cancelled = true; };
  }, [businessId, selectedServiceId, selectedService?.id, user?.uid, user?.email, customerEmail, customerPhone, urlMembershipId, urlPlanId, urlUseMembership]);

  const handleSelectWorkerFromSummary = (emp) => {
    if(!emp) { chooseAnyWorker(); return; }
    const empId = emp.id;
    const canFit = (() => {
      if(!selectedDate || !selectedTime) return true;
      if(isEmployeeUnavailableOnDate(emp, selectedDate)) return false;
      const slots = employeeBaseSlotsForDate(emp, selectedDate, businessDoc);
      const idx = slots.indexOf(selectedTime);
      if(idx === -1) return false;
      let duration = 30, bufferBefore = 0, bufferAfter = 0;
      if(selectedService) {
        duration = Number(selectedService.duration || 30);
        bufferBefore = Number(selectedService.bufferBeforeMinutes || 0);
        bufferAfter = Number(selectedService.bufferAfterMinutes || 0);
      } else {
        const sdoc = visibleServices.find(s => String(s.id) === String(selectedServiceId));
        if(sdoc) duration = Number(sdoc.duration || 30);
      }
      const needed = Math.ceil(duration / TIME_SLOT_INTERVAL);
      const pre = Math.ceil(bufferBefore / TIME_SLOT_INTERVAL);
      const post = Math.ceil(bufferAfter / TIME_SLOT_INTERVAL);
      const booked = bookedSlotsForEmployeeDate(bookingsMap, emp.id, isoDateFromDate(selectedDate));
      for(let offset = -pre; offset < needed + post; offset++){
        const s = slots[idx + offset];
        if(!s) return false;
        if(booked.has(s)) return false;
      }
      return true;
    })();

    chooseSpecificWorker(empId);
    if(!canFit){
      setSelectedTime(null);
    }
  };

  const handleOpenPhoneProfile = () => {
    try {
      saveDraftToSession({
        businessId: businessId || null,
        selectedEmployeeId:
          workerChoiceMode === "specific"
            ? (selectedEmployeeId || null)
            : null,
        workerChoiceMode: workerChoiceMode || "any",
        selectedServiceId: selectedServiceId || null,
        selectedDate: selectedDate
          ? selectedDate.toISOString()
          : null,
        eventOptions,
        selectedTime: selectedTime || null,
        customerName: customerName || "",
        customerEmail: customerEmail || "",
        customerPhone: customerPhone || "",
        visitMode: visitMode || null,
        customerLocation: customerLocation || null,
        customerExactAddress: customerExactAddress || "",
        savedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.warn("save phone verification draft failed", error);
    }

    const effectiveUser = getEffectiveUser(user);
    const handoffSaved = savePhoneProfileHandoff({
      uid: effectiveUser?.uid,
      phone: customerPhone,
    });

    if (!handoffSaved && String(customerPhone || "").trim()) {
      console.warn("phone profile handoff was not saved");
    }

    setShowPhoneContactModal(false);
    navigate("/profile");
  };

  const assertRequiredPaymentBackendReady = async () => {
    const runtimeCallable = bookingPaymentCallable("getPublicBookingPaymentRuntimeInfo");
    const runtimeResult = await withTimeout(
      runtimeCallable({ businessId }),
      12000,
      "public-payment-runtime-timeout"
    );
    const runtime = runtimeResult?.data || {};

    if (
      runtime.ok !== true ||
      String(runtime.flowVersion || "") !== PUBLIC_BOOKING_PAYMENT_FLOW_VERSION
    ) {
      throw new Error("public-payment-backend-version-mismatch");
    }

    return runtime;
  };

  const completeRequiredPaymentUi = async ({
    bookingData,
    createResult,
    employeeIdToUse,
    paidNow,
    finalBooking,
  }) => {
    const completedBooking = {
      ...bookingData,
      ...(finalBooking || {}),
      id: bookingData.id || createResult?.bookingId || finalBooking?.id || null,
    };

    if (
      bookingRequestAttemptRef.current &&
      bookingRequestAttemptRef.current.requestId === bookingData.clientRequestId
    ) {
      bookingRequestAttemptRef.current = null;
    }

    const completedAnalyticsPayload = {
      ...completedBooking,
      bookingId: completedBooking.id,
      createdFrom: "BookingInterface",
    };

    if (typeof onBookingCompleted === "function") {
      try {
        onBookingCompleted(completedAnalyticsPayload);
      } catch (analyticsErr) {
        try { console.warn("[BookingInterface] onBookingCompleted failed", analyticsErr); } catch (_) {}
      }
    } else {
      trackBookingCompletedEventSafe({
        db,
        businessId,
        payload: {
          sourceArea: "booking_interface",
          bookingSource: completedBooking.bookingSource || "client_online",
          createdFrom: completedBooking.createdFrom || "booking_interface",
          createdByRole: completedBooking.createdByRole || "client",
          onlineBooking: true,
          manual: false,
          bookingId: completedBooking.id,
          serviceId: completedBooking.serviceId || null,
          serviceName: completedBooking.serviceName || null,
          totalPrice: completedBooking.totalPrice ?? null,
          status: completedBooking.status || null,
          approvalModeAtCreation: completedBooking.approvalModeAtCreation || null,
          confirmedAutomatically: completedBooking.confirmedAutomatically === true,
          paymentRequired: true,
          paymentPaidNow: paidNow,
        },
      });
    }

    const selectedEmpForSummary = employeeIdToUse
      ? (bookableEmployees.find((e) => String(e.id) === String(employeeIdToUse)) || null)
      : null;

    const total = Number(
      completedBooking.totalPrice ||
      finalBooking?.totalPrice ||
      finalBooking?.expectedServiceAmount ||
      0
    );
    const balance = Math.max(
      0,
      Number(
        finalBooking?.balanceAmount ??
        completedBooking.balanceAmount ??
        (total - Number(paidNow || 0))
      ) || 0
    );

    setBookingSuccessSummary({
      serviceName: selectedService?.name || selectedService?.title || completedBooking.serviceName || "Servicio reservado",
      dateText: selectedDate
        ? selectedDate.toLocaleDateString(interfaceLocale(), { weekday: "short", day: "numeric", month: "short" })
        : completedBooking.date,
      timeText: selectedTime || completedBooking.time,
      workerText: selectedEmpForSummary
        ? (selectedEmpForSummary.name || selectedEmpForSummary.displayName || "Trabajador")
        : "El negocio asignará el trabajador",
      locationText: customerLocation?.label || null,
      deliveryFee,
      totalPrice: total || null,
      status: finalBooking?.status || completedBooking.status,
      approvalModeAtCreation: completedBooking.approvalModeAtCreation,
      requiresApproval: finalBooking?.requiresApproval ?? completedBooking.requiresApproval,
      confirmedAutomatically: finalBooking?.confirmedAutomatically ?? completedBooking.confirmedAutomatically,
      membershipBenefitLabel: null,
      isGuestBooking: false,
      paymentRequired: true,
      paymentPaidNow: Number(paidNow || 0),
      paymentBalance: balance,
    });

    setRequiredPaymentInline(null);
    setRequiredPaymentStatus("");
    setStatusMessage(null);
    setBookingsMap((prev) => {
      const copy = { ...(prev || {}) };
      copy[completedBooking.date] = (copy[completedBooking.date] || []).concat([completedBooking]);
      return copy;
    });
    setSelectedServiceId(null);
    setSelectedService(null);
    setSelectedMembershipBenefitId("");
    chooseAnyWorker();
    setSelectedDate(null);
    setSelectedTime(null);
    setCustomerLocation(null);
    setEventOptions({});
    clearDraftFromSession();
  };

  const prepareRequiredInlinePayment = async ({
    bookingData,
    createResult,
    employeeIdToUse,
    session,
  }) => {
    const reference = String(session?.reference || "").trim();
    const expectedCents = Number(publicPaymentPreview?.dueNowCents || 0);
    const sessionCents = Number(session?.amountInCents || 0);

    if (!reference || !(expectedCents > 0) || sessionCents !== expectedCents) {
      throw new Error("No pudimos preparar el pago seguro dentro de Loop.");
    }

    setRequiredPaymentInline({
      bookingData: { ...bookingData },
      createResult: createResult || null,
      employeeIdToUse: employeeIdToUse || null,
      session: { ...session },
      balanceCents: Math.max(
        0,
        Math.round(Number(session?.projectedBalanceAmount || 0) * 100)
      ),
    });
    setRequiredPaymentStatus(
      `Tu horario está reservado por ${publicPaymentPreview?.holdMinutes || 35} minutos. Completa el pago abajo.`
    );

    window.setTimeout(() => {
      try {
        document
          .querySelector('[data-loop-inline-booking-payment="true"]')
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } catch (_) {}
    }, 60);
  };

  const handleRequiredInlinePaymentApproved = async (paymentData = {}) => {
    const current = requiredPaymentInline;
    if (!current?.bookingData?.id) {
      setRequiredPaymentStatus(
        "El pago fue aprobado, pero no pudimos localizar la reserva. Revisa Mis Reservas."
      );
      return;
    }

    let finalBooking = null;
    try {
      const bookingRef = doc(
        db,
        "businesses",
        businessId,
        "bookings",
        current.bookingData.id
      );
      const finalSnap = await getDoc(bookingRef);
      if (finalSnap?.exists()) {
        finalBooking = { id: finalSnap.id, ...(finalSnap.data() || {}) };
      }
    } catch (readErr) {
      console.warn(
        "[BookingInterface] final inline-payment booking read failed",
        readErr
      );
    }

    const finalStatus = String(finalBooking?.status || "").toLowerCase();
    const finalRequirement = String(
      finalBooking?.paymentRequirementStatus || ""
    ).toLowerCase();

    if (
      finalBooking &&
      (finalStatus === "pending_payment" ||
        finalRequirement !== "satisfied")
    ) {
      setRequiredPaymentStatus(
        "El pago fue aprobado. Estamos terminando de confirmar la reserva; revisa Mis Reservas en unos segundos."
      );
      return;
    }

    await completeRequiredPaymentUi({
      bookingData: current.bookingData,
      createResult: current.createResult,
      employeeIdToUse: current.employeeIdToUse,
      paidNow: Number(
        paymentData?.amount ??
        current.session?.amount ??
        (Number(current.session?.amountInCents || 0) / 100)
      ),
      finalBooking: finalBooking || {
        status:
          current.bookingData.bookingStatusAfterPayment ||
          current.bookingData.confirmationStatusAfterPayment ||
          "pending",
        paymentRequirementStatus: "satisfied",
        balanceAmount: Number(current.session?.projectedBalanceAmount || 0),
      },
    });
  };

  const handleConfirmBooking = async (options = {}) => {
    if (isSaving || availabilityCheckingRef.current) return;
    if (availabilityStatus !== 'ready' && !requiredPaymentInline) {
      setStatusMessage({type:'error',text:'Espera a que comprobemos los horarios disponibles.'});
      return;
    }
    if (publicPaymentPreview?.requiresPayment && refundPolicyKey && acceptedRefundPolicyKey !== refundPolicyKey) {
      alert("Lee y acepta la política de cancelación antes de continuar.");
      return;
    }
    const allowUnverifiedContact =
      options?.allowUnverifiedContact === true;
    const guestMode = options?.guestMode === true;
    const guestContact =
      options?.guestContact &&
      typeof options.guestContact === "object"
        ? options.guestContact
        : {};

    const effectiveUser = getEffectiveUser(user);
    const isGuestBooking =
      guestMode && (!effectiveUser || !effectiveUser.uid);
    if(isGuestBooking && publicPaymentPreview.requiresAccount){setShowGuestBookingModal(false);setShowLoginModalLocal(true);return;}

    if ((!effectiveUser || !effectiveUser.uid) && !isGuestBooking) {
      try {
        const draft = {
          businessId: businessId || null,
          selectedEmployeeId: workerChoiceMode === "specific" ? (selectedEmployeeId || null) : null,
          workerChoiceMode: workerChoiceMode || "any",
          selectedServiceId: selectedServiceId || null,
          eventOptions,
          selectedDate: selectedDate ? selectedDate.toISOString() : null,
          selectedTime: selectedTime || null,
          customerName: customerName || "",
          customerEmail: customerEmail || "",
          customerPhone: customerPhone || "",
          visitMode: visitMode || null,
          customerExactAddress: customerExactAddress || "",
          customerLocation: customerLocation || null,
          savedAt: new Date().toISOString(),
        };
        saveDraftToSession(draft);
      } catch (e) { console.warn("save draft failed", e); }
      setShowLoginModalLocal(true);
      return;
    }

    if (!primaryCharacterSupported) {
      setStatusMessage({type:'error',text:'Este paquete aún está en preparación. El negocio debe completar su activación antes de recibir reservas.'});
      return;
    }
    if (publicPaymentPreview?.requiresPayment === true) {
      if (publicPaymentPreview.supported !== true) {
        const msg = publicBookingPaymentUnsupportedMessage(publicPaymentPreview.reason);
        setStatusMessage({ type: "error", text: msg });
        setRequiredPaymentStatus(msg);
        window.alert(msg);
        return;
      }

      if (isGuestBooking) {
        const msg = "Para reservar este servicio necesitas iniciar sesión y completar el pago.";
        setShowGuestBookingModal(false);
        setStatusMessage({ type: "error", text: msg });
        setRequiredPaymentStatus(msg);
        setShowLoginModalLocal(true);
        return;
      }
    }

    const bookingCustomerName = String(
      isGuestBooking
        ? (guestContact.name || customerName || "")
        : (customerName || effectiveUser?.displayName || ""),
    ).trim();

    const bookingCustomerEmail = String(
      isGuestBooking
        ? (guestContact.email || customerEmail || "")
        : (customerEmail || effectiveUser?.email || ""),
    ).trim().toLowerCase();

    const guestPhoneSnapshot = isGuestBooking
      ? parsePhoneValue(
          guestContact.phone || customerPhone || "",
          { defaultCountry: "PA" },
        )
      : null;

    const bookingCustomerPhone = isGuestBooking
      ? (guestPhoneSnapshot?.isValid
        ? guestPhoneSnapshot.e164
        : "")
      : String(customerPhone || "").trim();

    if (isGuestBooking) {
      if (bookingCustomerName.length < 2) {
        const msg = "Escribe tu nombre completo.";
        setStatusMessage({ type: "error", text: msg });
        window.alert(msg);
        return;
      }

      if (
        bookingCustomerEmail &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingCustomerEmail)
      ) {
        const msg = "Revisa el correo electrónico o déjalo vacío.";
        setStatusMessage({ type: "error", text: msg });
        window.alert(msg);
        return;
      }

      if (!bookingCustomerPhone) {
        const msg = "Escribe un teléfono válido con código de país.";
        setStatusMessage({ type: "error", text: msg });
        window.alert(msg);
        return;
      }

      if (guestContact.whatsappConsent !== true) {
        const msg = "Debes autorizar las confirmaciones por WhatsApp para reservar sin cuenta.";
        setStatusMessage({ type: "error", text: msg });
        window.alert(msg);
        return;
      }
    }

    const isSingleServiceModeLocal = !!(businessDoc && businessDoc.singleService);
    const isNoWorkersLocal = Boolean(isEventsBusinessDoc(businessDoc) && businessDoc && businessDoc.noWorkers);
    if(!selectedDate || !selectedTime || (!selectedServiceId && !isSingleServiceModeLocal)){
      const msg = "Selecciona servicio (o si la empresa tiene un solo servicio, no es necesario), fecha y hora.";
      setStatusMessage({ type: "error", text: msg });
      window.alert(msg);
      return;
    }

    if(locationRequired && !customerLocation){
      const msg = "Selecciona la zona donde necesitas el servicio.";
      setStatusMessage({ type: "error", text: msg });
      window.alert(msg);
      return;
    }

    if(locationRequired && !String(customerExactAddress || "").trim()){
      const msg = "Agrega la dirección exacta y referencias para la atención a domicilio.";
      setStatusMessage({ type: "error", text: msg });
      window.alert(msg);
      return;
    }

    if (!isGuestBooking && !customerPhoneVerified && !allowUnverifiedContact) {
      setShowPhoneContactModal(true);
      return;
    }

    if (publicPaymentPreview?.requiresPayment === true) {
      setIsSaving(true);
      setRequiredPaymentStatus("Validando el pago seguro...");
      try {
        await assertRequiredPaymentBackendReady();
      } catch (runtimeErr) {
        console.error("[BookingInterface] public payment runtime not ready", runtimeErr);
        const msg = "El pago para reservar todavía no está habilitado en el servidor. No se creó ninguna reserva.";
        setStatusMessage({ type: "error", text: msg });
        setRequiredPaymentStatus(msg);
        setIsSaving(false);
        window.alert(msg);
        return;
      }
      setIsSaving(false);
    }

    let duration = 30;
    let bufferBefore = 0;
    let bufferAfter = 0;
    if(selectedService) {
      duration = Number(selectedService.duration || 30);
      bufferBefore = Number(selectedService.bufferBeforeMinutes || 0);
      bufferAfter = Number(selectedService.bufferAfterMinutes || 0);
    } else {
      const sdoc = visibleServices.find(s => String(s.id) === String(selectedServiceId));
      if(sdoc && sdoc.duration) duration = Number(sdoc.duration);
      else if(businessDoc && businessDoc.singleServiceDuration) duration = Number(businessDoc.singleServiceDuration);
    }

    const isEventsBooking = isEventsBusinessDoc(businessDoc);
    const normalizedEventOptions = resolveEventBookingDraft(selectedService, eventOptions);
    if (isEventsBooking) {
      const selectionError = getEventPriceSummary(selectedService, normalizedEventOptions).pricingError;
      if (selectionError) { alert(selectionError); return; }
    }
    if (isEventsBooking && normalizedEventOptions.visitHours) {
      duration = Math.max(30, Math.round(Number(normalizedEventOptions.visitHours || 1) * 60));
    }

    const eventBaseMinutes = Math.max(1, Number(selectedService?.duration || duration || 60));
    const eventVisitTypeOptions = (Array.isArray(selectedService?.visitTypeOptions) && selectedService.visitTypeOptions.length
      ? selectedService.visitTypeOptions
      : [
          { id: "show", label: "Show completo", enabled: true, price: Number(selectedService?.price || 0) },
          { id: "visit", label: "Solo visita", enabled: true, price: Number(selectedService?.price || 0) ? Math.round(Number(selectedService?.price || 0) * 0.65) : 0 },
          { id: "photos", label: "Fotos y saludo", enabled: false, price: Number(selectedService?.price || 0) ? Math.round(Number(selectedService?.price || 0) * 0.5) : 0 },
        ]
    ).filter((x) => x && x.enabled !== false);
    const selectedVisitTypeOption = eventVisitTypeOptions.find((x) => x.id === normalizedEventOptions.visitType) || eventVisitTypeOptions[0] || { id: "show", label: "Show completo", price: Number(selectedService?.price || 0) };
    const eventBasePrice = Number(selectedVisitTypeOption.price || 0);
    let eventDurationPrice = null;
    if (isEventsBooking) {
      try { eventDurationPrice = require('./utils/eventPackageSelectionsV1').eventDurationPrice(selectedService, selectedVisitTypeOption, normalizedEventOptions.visitHours); }
      catch(error) { alert(error.message); return; }
    }
    let packageSelections = {};
    try { if(isEventsBooking) packageSelections = require("./utils/eventPackageSelectionsV1").eventPackageSelections(selectedService, normalizedEventOptions); }
    catch(error) { alert(error.message); return; }
    const eventCharacters = Array.isArray(selectedService?.characters) ? selectedService.characters : [];
    const eventSelectedCharacters = isEventsBooking ? selectedService.charactersMode === 'choose_primary' ? packageSelections.selectedCharacters : eventCharacters.filter(c=>normalizedEventOptions.selectedCharacterIds.includes(c.id||c.name)) : [];
    const eventCharactersPrice = eventSelectedCharacters.reduce((sum,c)=>sum+Number(c.price||0),0);
    const eventTotalPrice = isEventsBooking
      ? Number(eventDurationPrice || 0) + Number(eventCharactersPrice || 0) + packageSelections.extrasPrice
      : null;

    // IMPORTANT:
    // "Cualquiera" is explicit and independent from selectedEmployeeId.
    // This prevents stale UI/draft state from saving Zelda/Alberto automatically.
    const clientChoseAnyWorker = !isNoWorkersLocal && workerChoiceMode !== "specific";
    let employeeIdToUse = workerChoiceMode === "specific" ? (selectedEmployeeId || null) : null;

    if (isNoWorkersLocal || clientChoseAnyWorker) {
      employeeIdToUse = null;
    }

    availabilityCheckingRef.current = true;
    let freshBookings;
    try { freshBookings = await availabilityRefreshRef.current?.(); }
    finally { availabilityCheckingRef.current = false; }
    if (!freshBookings) return;
    // If the client selected a specific worker, validate that this worker can still take the slot.
    if (!isNoWorkersLocal && employeeIdToUse) {
      const selectedEmp = employees.find((emp) => String(emp.id) === String(employeeIdToUse));
      const dateKey = isoDateFromDate(selectedDate);

      let canFitSelectedWorker = true;

      if (!selectedEmp) {
        canFitSelectedWorker = false;
      } else if (selectedServiceId && !employeeOffersService(selectedEmp, selectedServiceId)) {
        canFitSelectedWorker = false;
      } else if (isEmployeeUnavailableOnDate(selectedEmp, selectedDate)) {
        canFitSelectedWorker = false;
      } else {
        const empSlots = employeeBaseSlotsForDate(selectedEmp, selectedDate, businessDoc);
        const idx = empSlots.indexOf(selectedTime);
        const needed = Math.ceil(duration / TIME_SLOT_INTERVAL);
        const pre = Math.ceil(bufferBefore / TIME_SLOT_INTERVAL);
        const post = Math.ceil(bufferAfter / TIME_SLOT_INTERVAL);
        const booked = bookedSlotsForEmployeeDate(freshBookings, selectedEmp.id, dateKey);

        if (idx === -1) canFitSelectedWorker = false;

        for (let i = -pre; canFitSelectedWorker && i < needed + post; i++) {
          const slot = empSlots[idx + i];
          if (!slot || booked.has(slot)) canFitSelectedWorker = false;
        }
      }

      if (!canFitSelectedWorker) {
        const msg = "Ese trabajador ya no está disponible para esa franja. Elige otro horario o trabajador.";
        setStatusMessage({ type: "error", text: msg });
        setSelectedTime(null);
        setSelectionNotice(msg);
        return;
      }
    }

    if (isEventsBooking && (isNoWorkersLocal || clientChoseAnyWorker || !employeeIdToUse)) {
      const dateKey = isoDateFromDate(selectedDate);
      const counts = bookedSlotCountsForNoWorkerDate(freshBookings, dateKey);
      const sameServiceBooked = bookedSlotsForNoWorkerServiceDate(freshBookings, dateKey, selectedServiceId);
      const globalCapacity = getGlobalEventCapacity(businessDoc);
      const neededSlots = Math.ceil(duration / TIME_SLOT_INTERVAL);
      const startMinutes = timeToMinutes(selectedTime);
      for (let i = 0; i < neededSlots; i++) {
        const slot = minutesToTime(startMinutes + i * TIME_SLOT_INTERVAL);
        if (sameServiceBooked.has(slot)) {
          const msg = "Ese paquete ya está reservado en ese horario. Puedes elegir otro paquete u otra hora.";
          setStatusMessage({ type: "error", text: msg });
          setSelectedTime(null);
        setSelectionNotice(msg);
          return;
        }
        if ((counts[slot] || 0) >= globalCapacity) {
          const msg = "La empresa ya alcanzó el máximo de eventos simultáneos en ese horario.";
          setStatusMessage({ type: "error", text: msg });
          setSelectedTime(null);
        setSelectionNotice(msg);
          return;
        }
      }
    }

    const scheduledDate = localInstant(isoDateFromDate(selectedDate),selectedTime,businessZone(businessDoc || {}));
    const scheduledAt = Timestamp.fromDate(scheduledDate);
    const approvalMode = getBookingApprovalModeForBusiness(businessDoc,selectedService || {});
    const approvalFields = getBookingApprovalFields(approvalMode);

    let serviceNameResolved = null;
    if(selectedService) serviceNameResolved = selectedService.name || selectedService.title || selectedService.id;
    else {
      const sdoc = visibleServices.find(s => String(s.id) === String(selectedServiceId));
      if(sdoc) serviceNameResolved = sdoc.name || sdoc.title || sdoc.id;
      else if(businessDoc && businessDoc.singleServiceName) serviceNameResolved = businessDoc.singleServiceName;
      else serviceNameResolved = "Reserva";
    }

    const cleanCustomerPhone = bookingCustomerPhone;
    if (!cleanCustomerPhone) {
      const msg = "Agrega tu teléfono para que el negocio pueda contactarte y enviarte confirmaciones por WhatsApp.";
      setStatusMessage({ type: "error", text: msg });
      window.alert(msg);
      return;
    }

    const bookingAttemptFingerprint = buildLoopBookingAttemptFingerprint({
      uid: isGuestBooking ? "guest" : effectiveUser.uid,
      businessId,
      date: isoDateFromDate(selectedDate),
      time: selectedTime,
      serviceId: selectedServiceId || "service",
      employeeId: clientChoseAnyWorker ? "" : (employeeIdToUse || ""),
      requestedWorkerMode: clientChoseAnyWorker ? "any" : (employeeIdToUse ? "specific" : "none"),
      duration,
      totalPrice: isEventsBooking
        ? Number(eventTotalPrice || 0) + Number(deliveryFee || 0)
        : (baseBookingPrice
          ? Number(baseBookingPrice || 0) + Number(deliveryFee || 0)
          : Number(deliveryFee || 0)),
      priceType: bookingPriceType,
      priceIsEstimate: bookingPriceIsEstimate,
      customerName: bookingCustomerName,
      customerEmail: bookingCustomerEmail,
      customerPhone: cleanCustomerPhone,
      visitMode: visitMode || null,
      customerLocation: customerLocation || null,
      customerExactAddress: String(customerExactAddress || "").trim() || null,
      deliveryFee: Number(deliveryFee || 0),
      eventOptions: isEventsBooking ? normalizedEventOptions : null,
      selectedCharacterIds: isEventsBooking ? normalizedEventOptions.selectedCharacterIds : [],
      membershipId:
        selectedMembershipBenefitPayload?.customerMembershipId ||
        selectedMembershipBenefitPayload?.membershipId ||
        null,
      membershipPlanId:
        selectedMembershipBenefitPayload?.membershipPlanId ||
        selectedMembershipBenefitPayload?.planId ||
        null,
    });

    let bookingRequestId = "";
    const previousBookingAttempt = bookingRequestAttemptRef.current;

    if (
      previousBookingAttempt &&
      previousBookingAttempt.fingerprint === bookingAttemptFingerprint &&
      previousBookingAttempt.requestId
    ) {
      bookingRequestId = previousBookingAttempt.requestId;
    } else {
      bookingRequestId = buildLoopBookingClientRequestId({
        uid: isGuestBooking ? "guest" : effectiveUser.uid,
        businessId,
      });
      bookingRequestAttemptRef.current = {
        fingerprint: bookingAttemptFingerprint,
        requestId: bookingRequestId,
        createdAt: Date.now(),
      };
    }

    const bookingData = {
      refundPolicyAcceptance: publicPaymentPreview?.requiresPayment && acceptedRefundPolicyKey === refundPolicyKey && refundPolicyKey ? JSON.parse(refundPolicyKey) : null,
      userId: isGuestBooking ? null : effectiveUser.uid,
      userUid: isGuestBooking ? null : effectiveUser.uid,
      customerUserId: isGuestBooking ? null : effectiveUser.uid,
      customerUserUid: isGuestBooking ? null : effectiveUser.uid,
      linkedUserId: isGuestBooking ? null : effectiveUser.uid,
      linkedUserUid: isGuestBooking ? null : effectiveUser.uid,
      hasUserAccount: !isGuestBooking,
      isGuestBooking,
      guestSessionId: isGuestBooking
        ? getOrCreateGuestBookingSessionId()
        : null,
      whatsappConsent: isGuestBooking
        ? guestContact.whatsappConsent === true
        : null,
      businessId,
      businessName: businessDisplayName,
      date: isoDateFromDate(selectedDate),
      time: selectedTime,
      scheduledAt,
      serviceId: selectedServiceId || null,
      serviceName: serviceNameResolved,
      duration,
      customerName: bookingCustomerName,
      customerEmail: bookingCustomerEmail,
      customerPhone: cleanCustomerPhone,
      clientPhone: cleanCustomerPhone,
      phone: cleanCustomerPhone,
      employeeId: clientChoseAnyWorker ? null : (employeeIdToUse || null),
      resourceId: clientChoseAnyWorker ? null : (employeeIdToUse || null),
      employeeName: clientChoseAnyWorker
        ? "Cualquiera"
        : ((bookableEmployees.find(e => String(e.id) === String(employeeIdToUse)) || {}).name || ""),
      assignmentStatus: clientChoseAnyWorker ? "unassigned" : (employeeIdToUse ? "assigned" : "none"),
      requestedWorkerMode: clientChoseAnyWorker ? "any" : (employeeIdToUse ? "specific" : "none"),
      eventOptions: isEventsBooking ? {
        ...normalizedEventOptions,
        ...packageSelections,
        visitTypeLabel: selectedVisitTypeOption?.label || normalizedEventOptions.visitType,
        selectedCharacters: eventSelectedCharacters.map((c) => ({
          id: c.id || c.name,
          name: c.name || c.id || "Extra",
          price: Number(c.price || 0),
        })),
        durationPrice: eventDurationPrice,
        charactersPrice: eventCharactersPrice,
        totalPrice: eventTotalPrice,
      } : null,
      eventDetails: isEventsBooking ? {
        ...packageSelections,
        visitHours: normalizedEventOptions.visitHours,
        visitType: normalizedEventOptions.visitType,
        visitTypeLabel: selectedVisitTypeOption?.label || normalizedEventOptions.visitType,
        visitTypeBasePrice: eventBasePrice,
        selectedCharacterIds: normalizedEventOptions.selectedCharacterIds,
        selectedCharacters: eventSelectedCharacters.map((c) => ({
          id: c.id || c.name,
          name: c.name || c.id || "Extra",
          price: Number(c.price || 0),
        })),
        durationPrice: eventDurationPrice,
        charactersPrice: eventCharactersPrice,
        totalPrice: eventTotalPrice,
      } : null,
      totalPrice: isEventsBooking ? Number(eventTotalPrice || 0) + Number(deliveryFee || 0) : (baseBookingPrice ? Number(baseBookingPrice || 0) + Number(deliveryFee || 0) : Number(deliveryFee || 0)),
      basePrice: baseBookingPrice || null,
      servicePrice: baseBookingPrice || null,
      priceType: bookingPriceType,
      pricingType: bookingPriceType,
      priceIsEstimate: bookingPriceIsEstimate,
      finalPricePending: bookingPriceIsEstimate,
      priceDisplayLabel: selectedService ? formatBookingServicePrice(selectedService) : null,
      visitMode: visitMode || null,
      visitTypeLocation: visitMode || null,
      customerLocation: customerLocation || null,
      customerExactAddress: String(customerExactAddress || "").trim() || null,
      customerAddressDetails: String(customerExactAddress || "").trim() || null,
      serviceAreaMatched: matchedServiceArea || null,
      deliveryFee,
      locationFee: deliveryFee,
      visitType: isEventsBooking ? normalizedEventOptions.visitType : null,
      visitTypeLabel: isEventsBooking ? (selectedVisitTypeOption?.label || normalizedEventOptions.visitType) : null,
      visitHours: isEventsBooking ? normalizedEventOptions.visitHours : null,
      selectedCharacterIds: isEventsBooking ? normalizedEventOptions.selectedCharacterIds : null,
      selectedCharacters: isEventsBooking ? eventSelectedCharacters.map((c) => ({
        id: c.id || c.name,
        name: c.name || c.id || "Extra",
        price: Number(c.price || 0),
      })) : null,
      durationPrice: isEventsBooking ? eventDurationPrice : null,
      charactersPrice: isEventsBooking ? eventCharactersPrice : null,
      status: approvalFields.status,
      confirmationStatus: approvalFields.confirmationStatus,
      approvalModeAtCreation: approvalFields.approvalModeAtCreation,
      requiresApproval: approvalFields.requiresApproval,
      confirmedAutomatically: approvalFields.confirmedAutomatically,
      pendingReason: approvalFields.pendingReason,

      // Loop analytics source classification.
      bookingSource: isGuestBooking
        ? "guest_online"
        : "client_online",
      createdFrom: isGuestBooking
        ? "booking_interface_guest"
        : "booking_interface",
      createdByRole: isGuestBooking
        ? "guest"
        : "client",
      channel: isGuestBooking
        ? "public_guest_booking_flow"
        : "public_booking_flow",
      origin: isGuestBooking
        ? "business_page_guest_booking"
        : "business_page_booking",
      onlineBooking: true,
      isOnlineBooking: true,
      requestedByClient: true,
      createdByClient: true,
      manual: false,
      isManual: false,

      ...(isGuestBooking
        ? {}
        : (selectedMembershipBenefitPayload || {})),

      clientRequestId: bookingRequestId,
      clientRequestKey: bookingRequestId,
      bookingRequestClientVersion: isGuestBooking
        ? "r33_2_guest_ui"
        : "r33_p2",

      createdAt: serverTimestamp(),
    };

    if (clientChoseAnyWorker) {
      employeeIdToUse = null;
      bookingData.employeeId = null;
      bookingData.resourceId = null;
      bookingData.employeeName = "Cualquiera";
      bookingData.employeeEmail = null;
      bookingData.employeeUserUid = null;
      bookingData.assignmentStatus = "unassigned";
      bookingData.requestedWorkerMode = "any";
    }

    try{
      if(employeeIdToUse && businessId){
        try{
          let emp = null;
          if (isIosCapacitor()) {
            emp = await withTimeout(fsClient.getDocByPath(`businesses/${businessId}/employees/${employeeIdToUse}`, { db }), 5000, "ios-get-employee-timeout");
          } else {
            const empRef = doc(db, `businesses/${businessId}/employees`, employeeIdToUse);
            const empSnap = await withTimeout(getDoc(empRef), 12000, "getDoc-employee-timeout");
            if(empSnap && empSnap.exists()) emp = empSnap.data() || {};
          }
          if(emp){
            if(emp.email) bookingData.employeeEmail = String(emp.email).toLowerCase();
            if(emp.userUid) bookingData.employeeUserUid = String(emp.userUid);
            else if(emp.userId) bookingData.employeeUserUid = String(emp.userId);
            else if(emp.uid) bookingData.employeeUserUid = String(emp.uid);
          }
        }catch(e){ console.warn("Could not read employee doc to attach email/uid", e); }
      }
    }catch(e){ console.warn("attach employeeEmail/employeeUserUid error", e); }

    try {
      console.log("[Loop BookingInterface] worker choice before save", {
        workerChoiceMode,
        selectedEmployeeId,
        clientChoseAnyWorker,
        employeeIdToUse,
        employeeId: bookingData.employeeId,
        resourceId: bookingData.resourceId,
        assignmentStatus: bookingData.assignmentStatus,
        requestedWorkerMode: bookingData.requestedWorkerMode,
        approvalModeAtCreation: bookingData.approvalModeAtCreation,
        status: bookingData.status,
      });
    } catch (_) {}

    setIsSaving(true);
    try{
      const createResult = await withTimeout(
        isGuestBooking
          ? createGuestBookingSecure(bookingData)
          : createBookingSecure(bookingData),
        isGuestBooking ? 45000 : 20000,
        isGuestBooking
          ? "createGuestBooking-function-timeout"
          : "createBooking-function-timeout"
      );

      if (createResult && createResult.booking) {
        bookingData.id = createResult.bookingId || createResult.booking.id || null;
      }

      if (isGuestBooking && createResult) {
        bookingData.status =
          createResult.status ||
          createResult.booking?.status ||
          bookingData.status;
        bookingData.confirmationStatus =
          createResult.status ||
          createResult.booking?.confirmationStatus ||
          bookingData.confirmationStatus;
        bookingData.requiresApproval =
          createResult.requiresApproval === true;
        bookingData.confirmedAutomatically =
          createResult.confirmedAutomatically === true;
      }

      const approvalBeforePayment = createResult?.booking?.eventBookingFlowVersion === 'event_booking_approval_first_v1'
        && createResult.booking.eventApprovalStatus === 'pending';
      if (approvalBeforePayment) {
        Object.assign(bookingData, {
          eventBookingFlowVersion: createResult.booking.eventBookingFlowVersion,
          eventApprovalStatus: 'pending', status: 'pending', confirmationStatus: 'pending',
          requiresApproval: true, confirmedAutomatically: false,
        });
      }
      if (publicPaymentPreview?.requiresPayment === true && !approvalBeforePayment) {
        const serverBooking = createResult?.booking || {};

        if (!bookingData.id || !isServerRequiredPaymentBooking(serverBooking)) {
          throw new Error("public-payment-backend-booking-contract-mismatch");
        }

        bookingData.status = serverBooking.status || "pending_payment";
        bookingData.confirmationStatus = serverBooking.confirmationStatus || "pending_payment";
        bookingData.requiresApproval = serverBooking.requiresApproval === true;
        bookingData.confirmedAutomatically = false;
        bookingData.requiresPaymentBeforeConfirmation = true;
        bookingData.bookingStatusAfterPayment = serverBooking.bookingStatusAfterPayment || null;
        bookingData.confirmationStatusAfterPayment = serverBooking.confirmationStatusAfterPayment || null;
        bookingData.paymentRequirementStatus = serverBooking.paymentRequirementStatus || "pending";
        bookingData.requiredBookingPaymentAmount = serverBooking.requiredBookingPaymentAmount || (Number(publicPaymentPreview.dueNowCents || 0) / 100);
        bookingData.requiredBookingPaymentAmountInCents = serverBooking.requiredBookingPaymentAmountInCents || Number(publicPaymentPreview.dueNowCents || 0);
        bookingData.paymentHoldExpiresAtIso = serverBooking.paymentHoldExpiresAtIso || null;

        setRequiredPaymentStatus(
          `Horario reservado por ${publicPaymentPreview.holdMinutes || 35} minutos. Preparando Wompi...`
        );

        const createPayment = bookingPaymentCallable("createWompiPaymentSession");
        const sessionRequest = buildRequiredPaymentSessionRequest({
          businessId,
          bookingId: bookingData.id,
          preview: publicPaymentPreview,
        });
        const paymentResult = await withTimeout(
          createPayment(sessionRequest),
          20000,
          "create-wompi-required-payment-timeout"
        );
        const session = paymentResult?.data || {};

        await prepareRequiredInlinePayment({
          bookingData,
          createResult,
          employeeIdToUse,
          session,
        });
        return;
      }

      if (
        bookingRequestAttemptRef.current &&
        bookingRequestAttemptRef.current.requestId === bookingData.clientRequestId
      ) {
        bookingRequestAttemptRef.current = null;
      }

      const completedAnalyticsPayload = {
        ...bookingData,
        bookingId: bookingData.id || createResult?.bookingId || createResult?.booking?.id || null,
        createdFrom: "BookingInterface",
      };

      if (typeof onBookingCompleted === "function") {
        try {
          onBookingCompleted(completedAnalyticsPayload);
        } catch (analyticsErr) {
          try { console.warn("[BookingInterface] onBookingCompleted failed", analyticsErr); } catch (_) {}
        }
      } else {
        trackBookingCompletedEventSafe({
          db,
          businessId,
          payload: {
            sourceArea: "booking_interface",
            bookingSource: bookingData.bookingSource || "client_online",
            createdFrom: bookingData.createdFrom || "booking_interface",
            createdByRole: bookingData.createdByRole || "client",
            onlineBooking: true,
            manual: false,
            bookingId: completedAnalyticsPayload.bookingId,
            serviceId: bookingData.serviceId || null,
            serviceName: bookingData.serviceName || null,
            totalPrice: bookingData.totalPrice ?? null,
            status: bookingData.status || null,
            approvalModeAtCreation: bookingData.approvalModeAtCreation || null,
            confirmedAutomatically: bookingData.confirmedAutomatically === true,
          },
        });
      }

      const selectedEmpForSummary = employeeIdToUse
        ? (bookableEmployees.find((e) => String(e.id) === String(employeeIdToUse)) || null)
        : null;

      setBookingSuccessSummary({
        serviceName: selectedService?.name || selectedService?.title || bookingData.serviceName || "Servicio reservado",
        dateText: selectedDate
          ? selectedDate.toLocaleDateString(interfaceLocale(), { weekday: "short", day: "numeric", month: "short" })
          : bookingData.date,
        timeText: selectedTime || bookingData.time,
        workerText: selectedEmpForSummary
          ? (selectedEmpForSummary.name || selectedEmpForSummary.displayName || "Trabajador")
          : "El negocio asignará el trabajador",
        locationText: customerLocation?.label || null,
        deliveryFee,
        totalPrice: bookingData.totalPrice || null,
        status: bookingData.status,
        approvalModeAtCreation: bookingData.approvalModeAtCreation,
        requiresApproval: bookingData.requiresApproval,
        confirmedAutomatically: bookingData.confirmedAutomatically,
        membershipBenefitLabel: isGuestBooking
          ? null
          : (bookingData.membershipBenefitLabel || null),
        isGuestBooking,
        guestReferenceCode: isGuestBooking
          ? (createResult?.guestReferenceCode || null)
          : null,
        guestManagementPath: isGuestBooking
          ? (createResult?.managementPath || null)
          : null,
      });

      setStatusMessage(null);
      setBookingsMap(prev => { const copy = { ...(prev || {}) }; copy[bookingData.date] = (copy[bookingData.date] || []).concat([bookingData]); return copy; });
      setSelectedServiceId(null); setSelectedService(null); setSelectedMembershipBenefitId(""); chooseAnyWorker(); setSelectedDate(null); setSelectedTime(null); setCustomerLocation(null);
      setEventOptions({});
      if (isGuestBooking) {
        setGuestWhatsappConsent(false);
        setShowGuestBookingModal(false);
      }
      clearDraftFromSession();
    }catch(e){
      console.error("create booking", e);
      const diagnostic = buildCreateBookingErrorDiagnostic(e, {
        businessId,
        serviceId: bookingData.serviceId || null,
        serviceName: bookingData.serviceName || null,
        employeeId: bookingData.employeeId || null,
        requestedWorkerMode: bookingData.requestedWorkerMode || null,
        date: bookingData.date || null,
        time: bookingData.time || null,
        clientRequestId: bookingData.clientRequestId || null,
      });
      const rawPaymentError = String(
        e?.message ||
        e?.details?.message ||
        e?.details?.reason ||
        ""
      ).toLowerCase();
      const isRequiredPaymentError = publicPaymentPreview?.requiresPayment === true;
      const errMsg = isRequiredPaymentError
        ? (
            rawPaymentError.includes("payment-hold-expired")
              ? "El tiempo para completar el pago venció. Vuelve a elegir el horario."
              : rawPaymentError.includes("backend-booking-contract-mismatch") ||
                rawPaymentError.includes("public-payment-backend")
                ? "El motor de pago todavía no está listo en el servidor. No continúes con esta reserva."
                : e?.message || e?.details?.message || "No pudimos preparar el pago. Intenta nuevamente."
          )
        : (
            isGuestBooking
              ? getFriendlyCreateGuestBookingError(e)
              : getFriendlyCreateBookingError(e)
          );

      if (rawPaymentError.includes("payment-hold-expired")) {
        bookingRequestAttemptRef.current = null;
      }

      if (isRequiredPaymentError) {
        setRequiredPaymentStatus(String(errMsg || "").slice(0, 220));
      }
      setStatusMessage({ type: "error", text: errMsg });
      setDiagSafe(diagnostic);
      trackBookingFunnelEventSafe({
        db,
        businessId,
        type: "booking_create_error",
        payload: {
          sourceArea: "booking_interface",
          funnelStep: "booking_create_error",
          errorCode: diagnostic.code || null,
          errorMessage: diagnostic.message || null,
          clientRequestId: diagnostic.clientRequestId || null,
          serviceId: diagnostic.serviceId || null,
          employeeId: diagnostic.employeeId || null,
          requestedWorkerMode: diagnostic.requestedWorkerMode || null,
          date: diagnostic.date || null,
          time: diagnostic.time || null,
        },
      });
      if (/slot|unavailable|conflict|already-exists|no.*disponible|ocupad/i.test(String(e?.code || '')+' '+rawPaymentError+' '+errMsg)) {
        setSelectedTime(null);
        bookingRequestAttemptRef.current = null;
        setSelectionNotice('Ese horario acaba de ocuparse. Conservamos tus datos; elige otra hora.');
        availabilityRefreshRef.current?.();
        document.getElementById('booking-availability-feedback')?.scrollIntoView({block:'center',behavior:'smooth'});
      }
    } finally{
      setIsSaving(false);
    }
  };


  const handleWorkerPointerDown = (e) => {
    const el = workerScrollRef.current;
    if (!el) return;
    workerDragRef.current = {
      active: true,
      startX: e.clientX || 0,
      scrollLeft: el.scrollLeft || 0,
      moved: false,
    };
    try { el.setPointerCapture?.(e.pointerId); } catch (_) {}
  };

  const handleWorkerPointerMove = (e) => {
    const el = workerScrollRef.current;
    const drag = workerDragRef.current;
    if (!el || !drag.active) return;
    const dx = (e.clientX || 0) - drag.startX;
    if (Math.abs(dx) > 3) {
      drag.moved = true;
      el.scrollLeft = drag.scrollLeft - dx;
      e.preventDefault?.();
    }
  };

  const handleWorkerPointerUp = (e) => {
    const el = workerScrollRef.current;
    if (el) {
      try { el.releasePointerCapture?.(e.pointerId); } catch (_) {}
    }
    setTimeout(() => {
      workerDragRef.current = {
        active: false,
        startX: 0,
        scrollLeft: 0,
        moved: false,
      };
    }, 0);
  };

  const scrollWorkersBy = (direction) => {
    const el = workerScrollRef.current;
    if (!el) return;
    const amount = Math.max(130, Math.round(el.clientWidth * 0.72));
    el.scrollBy({ left: direction * amount, behavior: "smooth" });
  };



  const handleWorkerTouchStart = (e) => {
    const el = workerScrollRef.current;
    const touch = e.touches && e.touches[0];
    if (!el || !touch) return;

    workerTouchRef.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
      scrollLeft: el.scrollLeft || 0,
      lock: null,
      moved: false,
    };
  };

  const handleWorkerTouchMove = (e) => {
    const el = workerScrollRef.current;
    const touch = e.touches && e.touches[0];
    const drag = workerTouchRef.current;

    if (!el || !touch || !drag.active) return;

    const dx = touch.clientX - drag.startX;
    const dy = touch.clientY - drag.startY;

    if (!drag.lock) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        drag.lock = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
    }

    if (drag.lock === "x") {
      drag.moved = true;
      el.scrollLeft = drag.scrollLeft - dx;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleWorkerTouchEnd = () => {
    setTimeout(() => {
      workerTouchRef.current = {
        active: false,
        startX: 0,
        startY: 0,
        scrollLeft: 0,
        lock: null,
        moved: false,
      };
    }, 40);
  };


  if (loadingBusinessDoc) {
    // Keep loading silent to avoid the ugly white "Cargando..." screen on mobile/native.
    return (
      <>
        <style>{bookingAirbnbStyles}</style>
        <div
          className="booking-silent-loading"
          aria-label={t("Preparando reserva")}
        />
      </>
    );
  }

  const companyInitials = (() => {
    const name = (businessDisplayName || (businessDoc && (businessDoc.name || businessDoc.title)) || "Reserva") || "";
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if(parts.length === 0) return "R";
    if(parts.length === 1) return parts[0].slice(0,2).toUpperCase();
    return (String(parts[0][0] || "") + String(parts[1][0] || "")).toUpperCase();
  })();



  const hasServiceForSticky = Boolean(selectedService || selectedServiceId || isSingleServiceMode);
  const hasDateForSticky = Boolean(selectedDate);
  const hasTimeForSticky = Boolean(selectedTime);
  const stickyPaymentSupported =
    publicPaymentPreview?.requiresPayment !== true ||
    publicPaymentPreview?.supported === true;
  const stickyReadyToConfirm =
    hasServiceForSticky &&
    hasDateForSticky &&
    hasTimeForSticky &&
    !locationDetailsRequired &&
    !eventSummaryForTotal?.pricingError &&
    primaryCharacterSupported &&
    !isSaving &&
    !requiredPaymentInline &&
    stickyPaymentSupported;
  const stickyActionLabel = requiredPaymentInline
    ? "Completa el pago arriba"
    : isSaving
      ? (publicPaymentPreview?.requiresPayment ? "Preparando pago..." : "Confirmando...")
      : !hasServiceForSticky
      ? "Elige servicio"
      : eventSummaryForTotal?.pricingError
        ? "Revisa las opciones del paquete"
      : !hasDateForSticky
        ? "Elige fecha"
        : !hasTimeForSticky
          ? "Elige hora"
          : locationDetailsRequired
            ? "Completa ubicación"
            : requiredPaymentButtonLabel(publicPaymentPreview || {}, false);
  const stickyKickerLabel = publicPaymentPreview?.requiresPayment
    ? (publicPaymentPreview?.supported ? "Pago requerido para reservar" : "Pago no disponible con esta opción")
    : (stickyReadyToConfirm ? "Listo para reservar" : "Completa tu reserva");
  const stickySummaryLabel = [
    selectedService?.name || selectedService?.title || (isSingleServiceMode ? "Reserva" : "Selecciona servicio"),
    selectedDate ? selectedDate.toLocaleDateString(interfaceLocale(), { day: "numeric", month: "short" }) : null,
    selectedTime || null,
    publicPaymentPreview?.requiresPayment && publicPaymentPreview?.supported
      ? `Paga hoy ${formatBookingMoney(Number(publicPaymentPreview.dueNowCents || 0) / 100)}`
      : publicPaymentPreview?.requiresQuote
        ? 'Precio por cotizar · sin pago ahora'
      : Number(finalBookingTotal || 0) > 0
        ? (bookingPriceIsEstimate ? `Desde ${formatBookingMoney(finalBookingTotal)}` : formatBookingMoney(finalBookingTotal))
        : null,
  ].filter(Boolean).join(" · ");

  const workerSelection = (!isNoWorkers && employeesToShow && employeesToShow.length > 0 ? (
              <div
                className="booking-pro-card booking-worker-panel booking-worker-area"
                id="booking-workers-section"
                style={{
                  padding: 14,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  boxSizing: "border-box",
                  contain: "paint",
                }}
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-base font-semibold text-slate-800">{t("Trabajador")}</div>
                    <div className="text-sm text-slate-500">{t("Elige quién hará tu servicio.")}</div>
                  </div>

                  {workerChoiceMode === "specific" && selectedEmployeeId ? (
                    <button
                      type="button"
                      onClick={() => chooseAnyWorker()}
                      className="text-sm px-3 py-1.5 rounded-full border bg-white hover:bg-slate-50"
                    >{t("Quitar")}</button>
                  ) : null}
                </div>

                <div className="worker-mobile-picker">
                  <button
                    type="button"
                    className="worker-mobile-selected-card"
                    onClick={() => {
                      setWorkerSearch("");
                      setWorkerPickerOpen(true);
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                      <div className="worker-mobile-avatar">
                        {selectedWorkerPhoto ? (
                          <img src={selectedWorkerPhoto} alt={selectedWorkerLabel} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : selectedEmployeeForPicker ? (
                          selectedWorkerLabel.slice(0, 1).toUpperCase()
                        ) : (
                          "👥"
                        )}
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>{t("Selección")}</div>
                        <div style={{ fontSize: 16, color: "#0f172a", fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {selectedWorkerLabel}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {selectedWorkerSubLabel}
                        </div>
                      </div>
                    </div>

                    <div style={{ color: "#0f766e", fontSize: 14, fontWeight: 950, flex: "0 0 auto" }}>{t("Cambiar")}</div>
                  </button>

                  {!canChooseAnyWorker && availableWorkersForPicker.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 750, marginTop: 8 }}>{t("Elige servicio, fecha y hora para ver trabajadores disponibles.")}</div>
                  ) : null}
                </div>

                <div className="booking-worker-navigation">
                  <button type="button" onClick={()=>{setWorkerSearch("");setWorkerPickerOpen(true);}}>{t("Ver todos (")}{employeesToShow.length})</button>
                  <div><button type="button" aria-label={t("Ver trabajadores anteriores")} aria-controls="booking-worker-options" onClick={()=>scrollWorkersBy(-1)}>←</button><button type="button" aria-label={t("Ver más trabajadores")} aria-controls="booking-worker-options" onClick={()=>scrollWorkersBy(1)}>→</button></div>
                </div>
                <div
                  className="worker-desktop-scroller" ref={workerScrollRef} id="booking-worker-options" aria-label={t("Trabajadores disponibles")} tabIndex={0}
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    touchAction: "pan-x",
                    boxSizing: "border-box",
                  }}
                >
                  <AnyWorkerPill
                    selected={workerChoiceMode !== "specific" && canChooseAnyWorker}
                    available={canChooseAnyWorker}
                    disabledReason={anyWorkerDisabledReason}
                    onClick={() => {
                      chooseAnyWorker();
                    }}
                  />

                  {employeesToShow.map((emp) => {
                    const state = employeeVisualState(
                      emp,
                      selectedEmployeeId,
                      selectedDate,
                      selectedTime,
                      selectedService,
                      selectedServiceId,
                      localServices,
                      bookingsMap,
                      businessDoc
                    );
                    return (
                      <WorkerPill
                        key={emp.id}
                        emp={emp}
                        state={state}
                        onClick={() => handleSelectWorkerFromSummary(emp || null)}
                      />
                    );
                  })}
                </div>

                {workerPickerOpen ? createPortal(
                  <div className="worker-mobile-modal booking-worker-dialog" role="dialog" aria-modal="true" aria-label={t("Elige tu trabajador")} onKeyDown={e=>{
                    if(e.key==="Escape"){e.stopPropagation();setWorkerPickerOpen(false);}
                    if(e.key==="Tab"){
                      const items=Array.from(e.currentTarget.querySelectorAll('button:not(:disabled),input:not(:disabled),[tabindex="0"]')).filter(el=>el.getClientRects().length);
                      const first=items[0],last=items[items.length-1];
                      if(e.shiftKey&&document.activeElement===first){e.preventDefault();last?.focus();}
                      else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first?.focus();}
                    }
                  }} onClick={() => setWorkerPickerOpen(false)}>
                    <div className="worker-mobile-modal-panel" onClick={(e) => e.stopPropagation()}>
                      <div className="worker-mobile-modal-header">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 19, fontWeight: 950, color: "#0f172a", lineHeight: 1.1 }}>{t("Elegir trabajador")}</div>
                        <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700, marginTop: 3 }}>
                          {selectedDate && selectedTime ? "Selecciona una opción disponible" : "Elige profesional; la disponibilidad se verifica con fecha y hora"}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setWorkerPickerOpen(false)}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 999,
                          border: "1px solid #e2e8f0",
                          background: "#fff",
                          color: "#0f172a",
                          fontSize: 22,
                          fontWeight: 900,
                          flex: "0 0 auto",
                        }}
                      >
                        ×
                      </button>
                    </div>

                    <div className="worker-mobile-modal-body">
                      <input
                        className="worker-search-input" autoFocus aria-label={t("Buscar trabajador")}
                        placeholder={t("Buscar trabajador...")}
                        value={workerSearch}
                        onChange={(e) => setWorkerSearch(e.target.value)}
                      />

                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 13, color: "#64748b", fontWeight: 950, marginBottom: 8 }}>{t("Recomendado")}</div>

                        <button
                          type="button"
                          className={`worker-mobile-option-card ${workerChoiceMode !== "specific" && canChooseAnyWorker ? "is-selected" : ""} ${!canChooseAnyWorker ? "is-disabled" : ""}`}
                          disabled={!canChooseAnyWorker}
                          onClick={() => {
                            if (!canChooseAnyWorker) return;
                            chooseAnyWorker();
                            setWorkerPickerOpen(false);
                          }}
                          style={{ width: "100%", minHeight: 96, display: "flex", flexDirection: "row", alignItems: "center" }}
                        >
                          <div className="worker-mobile-option-avatar" style={{ background: "#f0fdfa", color: "#0f766e" }}>👥</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 950, color: "#0f172a" }}>{t("Cualquiera disponible")}</div>
                            <div style={{ fontSize: 12, color: canChooseAnyWorker ? "#0f766e" : "#94a3b8", fontWeight: 750 }}>
                              {canChooseAnyWorker ? "El negocio asignará el trabajador" : anyWorkerDisabledReason}
                            </div>
                          </div>
                          {workerChoiceMode !== "specific" && canChooseAnyWorker ? <div className="worker-mobile-check">✓</div> : null}
                        </button>
                      </div>

                      {workerPickerRows.available.length > 0 ? (
                        <div style={{ marginTop: 18 }}>
                          <div style={{ fontSize: 13, color: "#64748b", fontWeight: 950, marginBottom: 8 }}>
                            {selectedDate && selectedTime ? "Disponibles" : "Ofrecen este servicio"}
                          </div>

                          <div className="worker-mobile-grid">
                            {workerPickerRows.available.map(({ emp, name, role, photo, selected }) => (
                              <button
                                key={emp.id}
                                type="button"
                                className={`worker-mobile-option-card ${selected ? "is-selected" : ""}`}
                                onClick={() => {
                                  handleSelectWorkerFromSummary(emp);
                                  setWorkerPickerOpen(false);
                                }}
                              >
                                {selected ? <div className="worker-mobile-check">✓</div> : null}
                                <div className="worker-mobile-option-avatar">
                                  {photo ? (
                                    <img src={photo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  ) : (
                                    name.slice(0, 1).toUpperCase()
                                  )}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {name}
                                  </div>
                                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {role || "Disponible"}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {workerPickerRows.unavailable.length > 0 ? (
                        <div style={{ marginTop: 18 }}>
                          <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 950, marginBottom: 8 }}>{t("No disponibles")}</div>

                          <div className="worker-mobile-grid">
                            {workerPickerRows.unavailable.map(({ emp, name, photo, state }) => (
                              <button
                                key={emp.id}
                                type="button"
                                className="worker-mobile-option-card is-disabled"
                                disabled
                              >
                                <div className="worker-mobile-option-avatar">
                                  {photo ? (
                                    <img src={photo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  ) : (
                                    name.slice(0, 1).toUpperCase()
                                  )}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {name}
                                  </div>
                                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {!state.offers ? t("No ofrece servicio") : t("No disponible")}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {workerPickerRows.available.length === 0 && workerPickerRows.unavailable.length === 0 ? (
                        <div style={{ marginTop: 18, color: "#64748b", fontWeight: 750, textAlign: "center" }}>{t("No encontramos trabajadores con esa búsqueda.")}</div>
                      ) : null}
                    </div>
                    </div>
                  </div>
                , document.body) : null}
              </div>
            ) : null);

  if (bookingSuccessSummary) {
    return (
      <BookingSuccessScreen businessDoc={businessDoc || {}}
        summary={bookingSuccessSummary}
        onViewBookings={async () => {
          if (bookingSuccessSummary?.isGuestBooking) {
            const reference = String(
              bookingSuccessSummary?.guestReferenceCode || "",
            ).trim();

            if (!reference) return;

            try {
              await navigator.clipboard.writeText(reference);
              window.alert("Referencia copiada.");
            } catch (_) {
              window.prompt(
                "Copia y guarda tu referencia de reserva:",
                reference,
              );
            }
            return;
          }

          navigate("/my-bookings");
        }}
        onClose={() => {
          if (typeof props.onClose === "function") {
            props.onClose();
          } else {
            navigate(-1);
          }
        }}
      />
    );
  }

  return (
    <>
      <style>{bookingAirbnbStyles}</style>
      <div className={`booking-pro-root booking-vertical-${bookingVertical || "general"} ${isEventsBusiness ? "booking-event-journey" : ""}`} data-event-step={eventBookingStep}>
        <style>{bookingSpacingStyles}</style>
        <header className="booking-pro-header">
          <div className="booking-pro-logo">
            { (businessDoc && (businessDoc.logoUrl || businessDoc.logo)) ? (
              <img src={businessDoc.logoUrl || businessDoc.logo} alt={businessDisplayName || "Logo"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div className="flex items-center justify-center w-full h-full bg-indigo-50 rounded-lg">
                <span className="text-2xl font-semibold text-indigo-700">{companyInitials}</span>
              </div>
            )}
          </div>
          <div>
            <h1 className="booking-pro-title">{businessDisplayName || (businessDoc && (businessDoc.name || businessDoc.title)) || t("Reserva")}</h1>
            {businessDoc && businessDoc.tagline ? <div className="booking-pro-subtitle">{businessDoc.tagline}</div> : null}
          </div>
        </header>

        {selectionNotice && <div className="booking-selection-notice" role="status"><span>{selectionNotice}</span><button type="button" aria-label={t("Cerrar aviso")} onClick={()=>setSelectionNotice("")}>×</button></div>}
        {isEventsBusiness && !requiredPaymentInline && <nav className="event-booking-steps" aria-label={t("Pasos de tu evento")} ref={eventJourneyRef} tabIndex={-1}>
          {['Tu evento','Lugar y fecha','Revisar y confirmar'].map((label,index)=><button type="button" key={label} aria-current={eventBookingStep===index?'step':undefined} disabled={index>0&&(!selectedService||!!eventSummaryForTotal?.pricingError)||index>1&&(!selectedDate||!selectedTime||locationDetailsRequired||isOutsideCoverage)} onClick={()=>changeEventStep(index)}><span>{index+1}</span>{label}</button>)}
        </nav>}
        {requiredPaymentInline ? (
          <section aria-label={t("Pago de la reserva")} style={{ width: "100%", maxWidth: 620, margin: "0 auto", padding: "16px 12px" }}>
            <h2 style={{ fontSize: 20, fontWeight: 800 }}>{t("Completa tu reserva")}</h2>
            <p style={{ color: "#64748b", marginBottom: 16 }}>
              {selectedService?.name || selectedService?.title} · {selectedDate?.toLocaleDateString(interfaceLocale())} · {selectedTime}
            </p>
            <PaymentHoldNotice booking={requiredPaymentInline.bookingData} onManage={()=>navigate("/my-bookings")} />
            <div data-loop-inline-booking-payment="true">
              <PublicBookingWompiInlineCard
                businessId={businessId}
                bookingId={requiredPaymentInline.bookingData?.id}
                session={requiredPaymentInline.session}
                balanceCents={requiredPaymentInline.balanceCents}
                onStatus={setRequiredPaymentStatus}
                onApproved={handleRequiredInlinePaymentApproved}
              />
            </div>
            <p style={{ color: "#64748b", fontSize: 12, marginTop: 16 }}>{t("Si cierras esta ventana, puedes consultar el pago desde Mis reservas.")}</p>
          </section>
        ) : <div className="booking-pro-layout">
          <div className="booking-pro-left">
            <div className="booking-event-mode"><BookingVisitLocationSection
              businessDoc={businessDoc}
              visitMode={visitMode}
              onVisitModeChange={setVisitMode}
              customerLocation={customerLocation}
              onCustomerLocationChange={setCustomerLocation}
              customerExactAddress={customerExactAddress}
              onCustomerExactAddressChange={setCustomerExactAddress}
              deliveryFee={deliveryFee}
              variant="mode"
            /></div>


            {!isSingleServiceMode && (
              <div className="booking-pro-card booking-event-service" id="booking-service-section">
                <ServiceDropdownPro compact={isEventsBusiness}
                  services={localServices}
                  loading={loadingServices}
                  selectedServiceId={selectedServiceId}
                  selectedService={selectedService}
                  onSelectService={(s) => {
                    if(s && String(getBookingServiceOptionId(s))===String(selectedServiceId)) return;
                    if(selectedTime || selectedEmployeeId)setSelectionNotice("Cambiaste el servicio. Conservamos la fecha; elige nuevamente profesional y hora para comprobar su disponibilidad.");
                    if (s) {
                      setSelectedServiceId(getBookingServiceOptionId(s));
                      setSelectedService(s);
                      if (String(getBookingServiceOptionId(s)) !== String(selectedServiceId)) setEventOptions({});
                      setSelectedTime(null);
                      setSelectedEmployeeId("");
                    } else {
                      setSelectedServiceId(null);
                      setSelectedService(null);
                      setEventOptions({});
                    }
                  }}
                />
              </div>
            )}

            {isEventsBusiness && (selectedService || isSingleServiceMode) ? (
              <EventBookingOptions
                selectedService={selectedService}
                value={activeEventOptions}
                onChange={(next)=>{ setEventOptions(next); if(selectedTime)setSelectionNotice("Cambiaste las opciones del evento. Conservamos la fecha y dirección; vuelve a elegir la hora para comprobar la disponibilidad."); setSelectedTime(null); }}
              />
            ) : null}

            {!isEventsBusiness && workerSelection}
            <div className="booking-pro-card booking-event-schedule" id="booking-date-section">
            <div style={{ marginBottom: 16 }}>
              <h2 className="booking-pro-section-title">{t("Selecciona fecha")}</h2>
              <div className="booking-pro-section-subtitle">{t("Elige el día de tu reserva.")}</div>
            </div>

            <div className="booking-calendar-head">
              <button
                type="button"
                onClick={() => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                className="booking-month-button"
                aria-label={t("Mes anterior")}
              >
                ‹
              </button>

              <div className="booking-month-label">
                {currentMonth.toLocaleDateString(interfaceLocale(), { month: "long", year: "numeric" })}
              </div>

              <button
                type="button"
                onClick={() => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                className="booking-month-button"
                aria-label={t("Mes siguiente")}
              >
                ›
              </button>
            </div>

            <div className="booking-weekdays">
              {["L","M","X","J","V","S","D"].map(d => <div key={d}>{d}</div>)}
            </div>

            <div className="booking-days-grid">
              {(() => {
                const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
                const adjusted = firstDay === 0 ? 6 : firstDay - 1;
                const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth()+1, 0).getDate();
                const blanks = Array.from({ length: adjusted }, (_, i) => <div key={`b-${i}`} className="booking-day-placeholder" />);
                const dayButtons = [];
                const today0 = wallDate(new Date(),businessDoc || {}); today0.setHours(0,0,0,0);
                for(let d=1; d<=daysInMonth; d++){
                  const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d);
                  date.setHours(0,0,0,0);
                  const isToday = date.toDateString() === wallDate(new Date(),businessDoc || {}).toDateString();
                  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                  const isPast = date < today0;
                  const disabled = isPast;
                  const classes = [
                    "booking-day-button",
                    disabled ? "is-disabled" : "",
                    isToday && !isSelected && !disabled ? "is-today" : "",
                    isSelected ? "is-selected" : "",
                  ].filter(Boolean).join(" ");
                  dayButtons.push(
                    <button
                      key={`d-${d}`}
                      onClick={() => {
                        if(!disabled){
                          if(selectedDate?.getTime()!==date.getTime()){setSelectedDate(date);setSelectedTime(null);setEventTimePeriod("all");}
                          setTimeout(() => document.getElementById("booking-hours-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
                        }
                      }}
                      disabled={disabled}
                      className={classes}
                      type="button"
                    >
                      {d}
                    </button>
                  );
                }
                return [...blanks, ...dayButtons];
              })()}
            </div>

            <div className="booking-time-section" id="booking-hours-section">
              <div style={{ marginBottom: 12 }}>
                <h2 className="booking-pro-section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Clock className="w-5 h-5" />{t("Horas disponibles")}</h2>
                <div className="booking-pro-section-subtitle">{t("Selecciona el horario que mejor te funcione.")}</div>
              </div>

              <div id="booking-availability-feedback" role="status" aria-live="polite">
                {availabilityStatus === 'loading' && <p>{t("Comprobando horarios…")}</p>}
                {availabilityStatus === 'error' && <p>{t("No pudimos verificar los horarios.") + " "}<button type="button" onClick={()=>availabilityRefreshRef.current?.()}>{t("Reintentar")}</button></p>}
                {statusMessage?.type === 'error' && <p>{statusMessage.text}</p>}
              </div>
              {!selectedDate ? <div className="text-slate-500">{t("Selecciona una fecha para ver horas.")}</div> : null}
              {selectedDate && !selectedService && !isSingleServiceMode && !isNoWorkers && <div className="text-slate-500 mb-2">{t("Selecciona un servicio para ver franjas disponibles.")}</div>}

              {selectedDate && <div className="event-time-periods" aria-label={t("Momento del día")}>{[['all','Todos'],['morning','Mañana'],['afternoon','Tarde'],['evening','Noche'],['overnight','Madrugada']].filter(([id])=>id==='all'||companySlotsInfo.slots.some(t=>require('./utils/eventTimePeriod').matchesEventTimePeriod(t,id))).map(([id,label])=><button type="button" key={id} aria-pressed={eventTimePeriod===id} onClick={()=>setEventTimePeriod(id)}>{label}</button>)}</div>}
              {selectedDate && companySlotsInfo.slots.length>0 && !companySlotsInfo.slots.some(t=>require('./utils/eventTimePeriod').matchesEventTimePeriod(t,eventTimePeriod)) && <p>{t("No hay horarios en este período.") + " "}<button type="button" onClick={()=>setEventTimePeriod("all")}>{t("Ver todos los horarios")}</button></p>}
              {selectedDate && (selectedService || isSingleServiceMode || isNoWorkers) && (
                <div>
                  {companySlotsInfo.slots.length === 0 ? <div className="booking-empty-slots"><p>{t("Este día no tiene horarios para reservar.")}</p><button type="button" onClick={()=>{const next=new Date(selectedDate);next.setDate(next.getDate()+1);setSelectedDate(next);setCurrentMonth(new Date(next.getFullYear(),next.getMonth(),1));setSelectedTime(null);setEventTimePeriod("all");}}>{t("Consultar el día siguiente")}</button></div> : (
                    <div className="booking-time-grid">
                      {companySlotsInfo.slots.filter(t=>require('./utils/eventTimePeriod').matchesEventTimePeriod(t,eventTimePeriod)).map(t => {
                        const meta = companySlotsInfo.info[t] || { anyOffererCanFit:false, anyHasSlot:false, selectedEmployeeCanFit:false, past:false };
                        const past = !!meta.past;
                        const disabled = availabilityStatus !== 'ready' || past || (workerChoiceMode === "specific" && selectedEmployeeId ? !meta.selectedEmployeeCanFit : !meta.anyOffererCanFit);
                        const visuallyDisabled = !meta.anyHasSlot || disabled;
                        return (
                          <button
                            key={t}
                            disabled={disabled}
                            onClick={() => {
                              if (!disabled) {
                                setSelectedTime(t);
                                if (!isEventsBusiness) {
                                  setTimeout(() => document.getElementById("booking-review-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
                                }
                              }
                            }}
                            className={[
                              "booking-time-button",
                              selectedTime === t ? "is-selected" : "",
                              visuallyDisabled ? "is-disabled" : "",
                            ].filter(Boolean).join(" ")}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>
          </div>

          <div className="booking-pro-sidebar">
            {isEventsBusiness && workerSelection}

            <div className="booking-event-location" id="booking-location-section"><BookingVisitLocationSection
              businessDoc={businessDoc}
              visitMode={visitMode}
              onVisitModeChange={setVisitMode}
              customerLocation={customerLocation}
              onCustomerLocationChange={setCustomerLocation}
              customerExactAddress={customerExactAddress}
              onCustomerExactAddressChange={setCustomerExactAddress}
              deliveryFee={deliveryFee}
              variant="location"
            /></div>

            {(!membershipBenefitsLoading && !membershipBenefitsError && membershipBenefits.length > 0) ? (
              <div className="booking-pro-card booking-membership-benefits" style={{ borderColor: selectedMembershipBenefit ? "rgba(20,184,166,.45)" : undefined }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 950, letterSpacing: ".12em", textTransform: "uppercase", color: "#00796f" }}>{t("Membresía")}</div>
                  </div>
                  {selectedMembershipBenefit ? <span style={{ borderRadius: 999, background: "#ccfbf1", color: "#0f766e", padding: "6px 10px", fontSize: 11, fontWeight: 950 }}>{t("Seleccionada")}</span> : null}
                </div>

                {membershipBenefitsLoading ? (
                  <div style={{ marginTop: 12, color: "#64748b", fontWeight: 800, fontSize: 13 }}>{t("Buscando tus membresías...")}</div>
                ) : membershipBenefitsError ? (
                  <div style={{ marginTop: 12, color: "#92400e", fontWeight: 850, fontSize: 13 }}>{membershipBenefitsError}</div>
                ) : (
                  <div style={{ display: "grid", gap: 8, marginTop: 13 }}>
                    {membershipBenefits.map((item) => {
                      const membership = item?.membership || {};
                      const plan = item?.plan || {};
                      const active = String(item.id) === String(selectedMembershipBenefitId);
                      const remaining = Number(item.remainingCredits ?? membership.creditsRemaining ?? membership.remainingCredits ?? 0) || 0;
                      const isDiscount = String(item.type || "credit") === "discount";
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setSelectedMembershipBenefitId(active ? "" : String(item.id))}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            border: active ? "1px solid rgba(15,118,110,.65)" : "1px solid #dbe4ef",
                            background: active ? "#f0fdfa" : "#fff",
                            borderRadius: 16,
                            padding: "11px 12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                          }}
                        >
                          <span style={{ minWidth: 0 }}>
                            <strong style={{ display: "block", color: "#0f172a", fontSize: 13, fontWeight: 750 }}>{plan.name || membership.planName || item.label || t("Membresía")}</strong>
                            <small style={{ display: "block", marginTop: 3, color: "#64748b", fontSize: 12, fontWeight: 800 }}>{item.type === "unlimited" ? "Sin límite de usos" : isDiscount ? `${Number(item.discountPercent || 0)}% de descuento` : `${remaining} disponibles`}</small>
                          </span>
                          <span style={{ borderRadius: 999, padding: "6px 9px", fontSize: 12, fontWeight: 950, color: active ? "#fff" : "#0f766e", background: active ? "#0f766e" : "#ecfdf5" }}>{active ? "Usando" : t("Usar")}</span>
                        </button>
                      );
                    })}
                    {selectedMembershipBenefit ? (
                      <div style={{ color: "#047857", fontSize: 12, fontWeight: 850, lineHeight: 1.35 }}>{t("Listo: esta reserva quedará marcada para usar membresía. El descuento/crédito se procesará al completar el servicio.")}</div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}

            <div className="booking-pro-card booking-inline-confirm" id="booking-review-section" style={{ padding: 18 }}>
              {statusMessage?.type === 'error' && <p role="alert" style={{color:'#974428',fontSize:14,lineHeight:1.5}}>{statusMessage.text}</p>}
              {!primaryCharacterSupported && <p role="status">{t("Este paquete aún está en preparación. El negocio debe completar su activación antes de recibir reservas.")}</p>}
              <CompactReservationConfirm businessDoc={businessDoc || {}}
                selectedService={selectedService}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                selectedEmployeeId={workerChoiceMode === "specific" ? selectedEmployeeId : ""}
                employees={employees}
                isSaving={isSaving}
                isEventsBusiness={isEventsBusiness}
                eventOptions={activeEventOptions}
                noWorkerMode={isNoWorkers || isEventsBusiness}
                locationRequired={locationRequired}
                customerLocation={customerLocation}
                matchedServiceArea={matchedServiceArea}
                deliveryFee={deliveryFee}
                isOutsideCoverage={isOutsideCoverage}
                finalBookingTotal={finalBookingTotal}
                visitMode={visitMode}
                customerExactAddress={customerExactAddress}
                configurationReady={primaryCharacterSupported}
                paymentPreview={publicPaymentPreview}
                paymentStatus={requiredPaymentStatus}
                paymentInlineActive={Boolean(requiredPaymentInline)}
                canChangeService={!isSingleServiceMode}
                onEditSelection={target=>{
                  if(isEventsBusiness) changeEventStep(['service','experience'].includes(target)?0:1);
                  const id={service:'booking-service-section',experience:'booking-experience-section',worker:'booking-workers-section',date:'booking-date-section',location:'booking-location-section'}[target];
                  requestAnimationFrame(()=>{const section=document.getElementById(id);section?.scrollIntoView({behavior:'smooth',block:'start'});section?.querySelector('button,input,select,textarea')?.focus({preventScroll:true});});
                }}
                onChooseLocation={() => {}}
                onConfirm={handleConfirmBooking}
                onClear={() => { setSelectedServiceId(null); setSelectedService(null); setSelectedDate(null); setSelectedTime(null); setSelectedEmployeeId(""); setCustomerLocation(null); setCustomerExactAddress(""); clearDraftFromSession(); if(isEventsBusiness)changeEventStep(0); }}
              />

              {publicPaymentPreview?.requiresPayment && refundPolicyKey && !requiredPaymentInline && (
                <label style={{display: "block", padding: 16}}>
                  <input type="checkbox" checked={acceptedRefundPolicyKey === refundPolicyKey}
                    onChange={(event) => setAcceptedRefundPolicyKey(event.target.checked ? refundPolicyKey : "")} />{" "}{t("Acepto la política de cancelación:")}{refundPolicy.enabled
                    ? `devolución del anticipo si cancelo al menos ${refundPolicy.noticeHours} horas antes de la cita.`
                    : "sin devolución del anticipo por cancelación del cliente."}
                </label>
              )}
            </div>
          </div>
        </div>}

{isEventsBusiness && !requiredPaymentInline && eventBookingStep<2 && <div className="event-booking-next"><div className="event-payment-preview" aria-live="polite">{eventSummaryForTotal?.requiresQuote ? <span>{t("Recibirás una propuesta antes de pagar.")}</span> : !eventSummaryForTotal?.pricingError && selectedService ? <><strong>Total {formatBookingMoney(finalBookingTotal)}</strong><span>{publicPaymentPreview?.approvalBeforePayment ? 'Abono tras aprobar' : 'Abono para reservar'}: {formatBookingMoney(Number(publicPaymentPreview?.dueNowCents || 0)/100)}{" " + t("· Restante") + " "}{formatBookingMoney(Math.max(0,finalBookingTotal-Number(publicPaymentPreview?.dueNowCents || 0)/100))}</span></> : <span>{t("Elige tu experiencia y personaje incluido.")}</span>}</div><button type="button" onClick={()=>changeEventStep(eventBookingStep+1)} disabled={eventBookingStep===0 ? !selectedService || !!eventSummaryForTotal?.pricingError : !selectedDate || !selectedTime || locationDetailsRequired || isOutsideCoverage}>{eventBookingStep===0?'Continuar con lugar y fecha':'Revisar mi reserva'} →</button></div>}
{!requiredPaymentInline && (!isEventsBusiness || eventBookingStep===2) && <div className="booking-sticky-confirm booking-sticky-confirm-v54a">
          <div className="booking-sticky-inner">
            <div className="booking-sticky-copy" style={{ minWidth: 0 }}>
              <div className="booking-sticky-kicker">
                {stickyKickerLabel}
              </div>
              <div className="booking-sticky-summary">
                {stickySummaryLabel}
              </div>
            </div>

            <button
              type="button"
              className="booking-sticky-primary"
              onClick={handleConfirmBooking}
              disabled={!stickyReadyToConfirm}
              aria-disabled={!stickyReadyToConfirm}
            >
              {stickyActionLabel}
            </button>
          </div>
        </div>}
      </div>

      {showPhoneContactModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="phone-contact-modal-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(15, 23, 42, 0.52)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onMouseDown={() => {
            if (!isSaving) {
              setShowPhoneContactModal(false);
            }
          }}
        >
          <div
            onMouseDown={(event) => event.stopPropagation()}
            style={{
              width: "min(460px, calc(100vw - 32px))",
              background: "#ffffff",
              borderRadius: 18,
              padding: 20,
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
            }}
          >
            <h3
              id="phone-contact-modal-title"
              style={{
                margin: 0,
                color: "#0f172a",
                fontSize: 20,
                fontWeight: 900,
              }}
            >{t("Teléfono para esta reserva")}</h3>

            <p
              style={{
                margin: "8px 0 16px",
                color: "#64748b",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              Úsalo solamente para que el negocio te contacte o
              verifícalo para guardarlo en tu cuenta Loop.
            </p>

            <PhoneInput
              label={t("Número de teléfono")}
              value={customerPhone || ""}
              onChange={(nextValue) => {
                setCustomerPhone(nextValue);
                setCustomerPhoneVerified(false);
              }}
              defaultCountry="PA"
              hint="Usarlo solo como contacto no vincula cuentas ni transfiere fidelidad."
            />

            <div
              style={{
                display: "grid",
                gap: 10,
                marginTop: 18,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setShowPhoneContactModal(false);
                  handleConfirmBooking({
                    allowUnverifiedContact: true,
                  });
                }}
                disabled={
                  isSaving ||
                  !String(customerPhone || "").trim()
                }
                className="w-full px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-60"
              >
                {isSaving
                  ? "Confirmando..."
                  : "Reservar con este número"}
              </button>

              <button
                type="button"
                onClick={handleOpenPhoneProfile}
                disabled={isSaving}
                className="w-full px-4 py-3 rounded-xl bg-emerald-50 text-emerald-800 font-semibold disabled:opacity-60"
              >{t("Verificar y guardar en mi cuenta")}</button>

              <button
                type="button"
                onClick={() => setShowPhoneContactModal(false)}
                disabled={isSaving}
                style={{
                  border: 0,
                  background: "transparent",
                  color: "#64748b",
                  fontSize: 13,
                  fontWeight: 750,
                  padding: 6,
                  cursor: "pointer",
                }}
              >{t("Cancelar")}</button>
            </div>
          </div>
        </div>
      )}

      {showGuestBookingModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="guest-booking-modal-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10020,
            background: "rgba(15, 23, 42, 0.52)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onMouseDown={() => {
            if (!isSaving) setShowGuestBookingModal(false);
          }}
        >
          <div
            onMouseDown={(event) => event.stopPropagation()}
            style={{
              width: "min(500px, calc(100vw - 32px))",
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
              background: "#ffffff",
              borderRadius: 20,
              padding: 20,
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
            }}
          >
            <h3
              id="guest-booking-modal-title"
              style={{ margin: 0, color: "#0f172a", fontSize: 21, fontWeight: 900 }}
            >{t("Reserva sin crear una cuenta")}</h3>
            <p style={{ margin: "8px 0 18px", color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>{t("Tus datos se usarán solo para esta reserva. El teléfono no se verificará, no unirá cuentas y no activará lealtad.")}</p>

            <label style={{ display: "grid", gap: 6, marginBottom: 14 }}>
              <span style={{ color: "#334155", fontSize: 13, fontWeight: 850 }}>{t("Nombre completo")}</span>
              <input
                type="text"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                autoComplete="name"
                disabled={isSaving}
                style={{ width: "100%", minHeight: 48, border: "1px solid #dbe5ee", borderRadius: 14, padding: "0 13px", fontSize: 15, outline: "none" }}
              />
            </label>

            <PhoneInput
              label="WhatsApp"
              value={customerPhone || ""}
              onChange={(nextValue) => {
                setCustomerPhone(nextValue);
                setCustomerPhoneVerified(false);
              }}
              defaultCountry="PA"
              required
              disabled={isSaving}
              hint="Incluye el código de país para que el negocio pueda contactarte."
            />

            <label style={{ display: "grid", gap: 6, marginTop: 14 }}>
              <span style={{ color: "#334155", fontSize: 13, fontWeight: 850 }}>{t("Correo electrónico (opcional)")}</span>
              <input
                type="email"
                value={customerEmail}
                onChange={(event) => setCustomerEmail(event.target.value)}
                autoComplete="email"
                disabled={isSaving}
                style={{ width: "100%", minHeight: 48, border: "1px solid #dbe5ee", borderRadius: 14, padding: "0 13px", fontSize: 15, outline: "none" }}
              />
            </label>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 18, color: "#334155", fontSize: 13, fontWeight: 750, lineHeight: 1.45, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={guestWhatsappConsent}
                onChange={(event) => setGuestWhatsappConsent(event.target.checked)}
                disabled={isSaving}
                style={{ marginTop: 3, width: 17, height: 17 }}
              />
              <span>{t("Autorizo que el negocio y Loop me envíen por WhatsApp confirmaciones y cambios relacionados con esta reserva.")}</span>
            </label>

            <div style={{ display: "grid", gap: 10, marginTop: 20 }}>
              <button
                type="button"
                onClick={() => handleConfirmBooking({
                  guestMode: true,
                  guestContact: {
                    name: customerName,
                    email: customerEmail,
                    phone: customerPhone,
                    whatsappConsent: guestWhatsappConsent,
                  },
                })}
                disabled={
                  isSaving ||
                  String(customerName || "").trim().length < 2 ||
                  !parsePhoneValue(
                    customerPhone || "",
                    { defaultCountry: "PA" },
                  ).isValid ||
                  !guestWhatsappConsent
                }
                className="w-full px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-60"
              >
                {isSaving ? "Creando reserva..." : "Confirmar reserva sin cuenta"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowGuestBookingModal(false);
                  handleGoToLogin();
                }}
                disabled={isSaving}
                className="w-full px-4 py-3 rounded-xl bg-emerald-50 text-emerald-800 font-semibold disabled:opacity-60"
              >{t("Prefiero iniciar sesión")}</button>

              <button
                type="button"
                onClick={() => setShowGuestBookingModal(false)}
                disabled={isSaving}
                style={{ border: 0, background: "transparent", color: "#64748b", fontSize: 13, fontWeight: 750, padding: 6, cursor: "pointer" }}
              >{t("Cancelar")}</button>
            </div>
          </div>
        </div>
      )}

      {showLoginModalLocal && (
        <div role="dialog" aria-modal="true" aria-labelledby="login-modal-title" style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onMouseDown={() => setShowLoginModalLocal(false)}>
          <div onMouseDown={(e) => e.stopPropagation()} style={{ width: "min(440px, calc(100vw - 32px))", background: "#fff", borderRadius: 18, padding: 20, boxShadow: "0 8px 32px rgba(2,6,23,0.12)" }}>
            <h3 id="login-modal-title" style={{ marginTop: 0, marginBottom: 8, fontWeight: 900, color: "#0f172a" }}>
              {publicPaymentPreview?.requiresAccount ? "Guarda y sigue tu solicitud" : publicPaymentPreview?.requiresPayment ? "Inicia sesión para pagar y reservar" : "¿Cómo quieres reservar?"}
            </h3>
            <p style={{ color: "#475569", lineHeight: 1.5 }}>
              {publicPaymentPreview?.requiresAccount ? 'Inicia sesión o crea tu cuenta para recibir la cotización, aprobarla y consultar tus pagos. Conservaremos el paquete, los personajes y la ubicación que elegiste.' : publicPaymentPreview?.requiresPayment
                ? `Este servicio requiere ${formatBookingMoney(Number(publicPaymentPreview.dueNowCents || 0) / 100)} antes de confirmar la reserva.`
                : "Puedes continuar sin cuenta o iniciar sesión para guardar la reserva en tu perfil."}
            </p>
            <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
              {!publicPaymentPreview?.requiresPayment && !publicPaymentPreview?.requiresAccount ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowLoginModalLocal(false);
                    setShowGuestBookingModal(true);
                  }}
                  className="w-full px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold"
                >{t("Reservar sin cuenta")}</button>
              ) : null}
              <button type="button" onClick={handleGoToLogin} className="w-full px-4 py-3 rounded-xl bg-emerald-50 text-emerald-800 font-semibold">{t("Iniciar sesión")}</button>
              <button type="button" onClick={() => setShowLoginModalLocal(false)} className="w-full px-3 py-2 rounded-xl bg-gray-100">{t("Cancelar")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
